import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import {
  Building2,
  Package,
  TruckIcon as Truck,
  MapPin,
  Upload,
  Download,
  Plus,
  Edit,
  Trash2,
  Database,
  X,
  Map as MapIcon,
  List,
  Phone,
  QrCode,
  Sparkles,
  Navigation,
  CheckCircle,
  AlertCircle,
  Camera,
  Zap,
  Route as RouteIcon
} from 'lucide-react';
import { getDocuments, createDocument, updateDocument, deleteDocument, deleteField, db, getDocumentsByQuery } from '../../services/firebase';
import { doc as fsDoc, setDoc as fsSetDoc, serverTimestamp as fsServerTimestamp, collection as fsCollection, query as fsQuery, where as fsWhere, getDocs as fsGetDocs, writeBatch as fsWriteBatch } from 'firebase/firestore';
import { City, Area, Locality, OutletClassification, Outlet, Warehouse, Route, PolygonBoundary, AreaBoundary, LocalityBoundary, Pincode } from '../../types';
import { MarkerClusterer } from '@googlemaps/markerclusterer';
import { getFilteredOutletsForUser } from '../../utils/outletFiltering';
import AddOutlet from '../AddOutlet';
import PolygonExtractorModal from '../PolygonExtractorModal';
import AreaRoutingSection from './AreaRoutingSection';
import { geocodingService } from '../../services/geocoding';
import { logAddressComponentsSample } from '../../services/addressSampling';

// Utility function to calculate distance between two points using Haversine formula
const calculateDistance = (lat1: number, lon1: number, lat2: number, lon2: number): number => {
  const R = 6371; // Earth's radius in kilometers
  const dLat = (lat2 - lat1) * Math.PI / 180;
  const dLon = (lon2 - lon1) * Math.PI / 180;
  const a = 
    Math.sin(dLat/2) * Math.sin(dLat/2) +
    Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) * 
    Math.sin(dLon/2) * Math.sin(dLon/2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
  return R * c;
};

// TSP solver using nearest neighbor heuristic with improvements
const solveTSP = (outlets: Outlet[], warehouseLat: number, warehouseLon: number): Outlet[] => {
  if (outlets.length === 0) return [];
  if (outlets.length === 1) return outlets;

  // Create distance matrix
  const points = [{ lat: warehouseLat, lon: warehouseLon, outlet: null }, ...outlets.map(outlet => ({ 
    lat: outlet.latitude!, 
    lon: outlet.longitude!, 
    outlet 
  }))];
  
  const distanceMatrix: number[][] = [];
  for (let i = 0; i < points.length; i++) {
    distanceMatrix[i] = [];
    for (let j = 0; j < points.length; j++) {
      distanceMatrix[i][j] = calculateDistance(points[i].lat, points[i].lon, points[j].lat, points[j].lon);
    }
  }

  // Nearest neighbor algorithm starting from warehouse (index 0)
  const visited = new Set<number>();
  const route: number[] = [];
  let currentIndex = 0; // Start at warehouse
  visited.add(currentIndex);

  while (visited.size < points.length) {
    let nearestIndex = -1;
    let nearestDistance = Infinity;

    for (let i = 1; i < points.length; i++) { // Skip warehouse (index 0) for destinations
      if (!visited.has(i)) {
        const distance = distanceMatrix[currentIndex][i];
        if (distance < nearestDistance) {
          nearestDistance = distance;
          nearestIndex = i;
        }
      }
    }

    if (nearestIndex !== -1) {
      visited.add(nearestIndex);
      route.push(nearestIndex);
      currentIndex = nearestIndex;
    }
  }

  // Return outlets in optimal order (excluding warehouse)
  return route.map(index => points[index].outlet!).filter(Boolean);
};

// Stable Google Maps Container Component
const MapContainer = React.memo<{
  outlets: Outlet[];
  localities: Locality[];
  areas: Area[];
  warehouses: Warehouse[];
  cities: City[];
  routes: Route[];
  classifications: OutletClassification[];
  pincodes: Pincode[];
  selectedPincodeFilter: string[];
  onEditOutlet?: (outlet: Outlet) => void;
  showAreaBoundaries?: boolean;
  showLocalityBoundaries?: boolean;
  polygonBoundaries?: PolygonBoundary[];
  showOptimizedRoute?: boolean;
  selectedRouteFilter?: string;
}>(({ outlets, localities, areas, warehouses, cities, routes, classifications, pincodes, selectedPincodeFilter, onEditOutlet, showAreaBoundaries = false, showLocalityBoundaries = false, polygonBoundaries = [], showOptimizedRoute = false, selectedRouteFilter }) => {
  const mapRef = useRef<HTMLDivElement>(null);
  const mapInstanceRef = useRef<any>(null);
  const markersRef = useRef<any[]>([]);
  const clusterRef = useRef<any>(null);
  const areaLayerRef = useRef<any>(null);
  const localityLayerRef = useRef<any>(null);
  const polygonOverlaysRef = useRef<any[]>([]);
  // Support multiple route segments (Google limits to 23 waypoints per request)
  const directionsRenderersRef = useRef<any[]>([]);

  useEffect(() => {
    const initializeMap = async () => {
      if (!mapRef.current || outlets.length === 0) return;

      try {
        // Load Google Maps if not already loaded
        await loadGoogleMaps();

        const google = (window as any).google;
        if (!google?.maps?.Map) return;

        // Only create map instance once
        if (!mapInstanceRef.current) {
          // Build center point from valid outlets
          const validOutlets = outlets.filter(o => o.latitude && o.longitude);
          if (validOutlets.length === 0) return;

          const center = validOutlets.reduce(
            (acc, outlet) => {
              acc.lat += parseFloat((outlet.latitude || 0).toString());
              acc.lng += parseFloat((outlet.longitude || 0).toString());
              return acc;
            },
            { lat: 0, lng: 0 }
          );

          center.lat /= validOutlets.length;
          center.lng /= validOutlets.length;

          const mapId = process.env.REACT_APP_GOOGLE_MAPS_MAP_ID;
          console.log('🔍 Environment check:', {
            hasMapId: !!mapId,
            mapId: mapId,
            allEnvVars: Object.keys(process.env).filter(key => key.startsWith('REACT_APP_GOOGLE')),
            googleMapsAvailable: !!(window as any).google?.maps,
            featureLayerAvailable: !!(window as any).google?.maps?.FeatureLayer
          });
          
          console.log('🗺️ Map initialization details:', {
            mapId: mapId,
            expectedMapId: '509a5857abfe5ac82647ef1c', // From Google Cloud Console
            mapIdMatches: mapId === '509a5857abfe5ac82647ef1c',
            mapIdLength: mapId?.length,
            expectedLength: '509a5857abfe5ac82647ef1c'.length,
            mapIdType: typeof mapId
          });
          
          mapInstanceRef.current = new google.maps.Map(mapRef.current, {
            center: { lat: center.lat, lng: center.lng },
            zoom: 12,
            ...(mapId ? { mapId } : {
              // Only use inline styles when no mapId is present
              styles: [
                {
                  featureType: 'poi',
                  elementType: 'labels.text.fill',
                  stylers: [{ color: '#757575' }]
                }
              ]
            })
          });

          // Initialize feature layers for boundaries using Google's documented approach
          try {
            console.log('🔍 Google Maps API check:', {
              hasGoogle: !!(window as any).google,
              hasMaps: !!(window as any).google?.maps,
              hasFeatureLayer: !!(window as any).google?.maps?.FeatureLayer,
              mapInstance: !!mapInstanceRef.current,
              mapIdPresent: !!mapId
            });
            
            if (mapId && mapInstanceRef.current && (window as any).google?.maps) {
              console.log('🗺️ Initializing feature layers with Map ID:', mapId);
              
              // Debug: Check what's available in google.maps
              const google = (window as any).google;
              console.log('🔍 Google Maps API inspection:', {
                hasFeatureLayer: !!google.maps.FeatureLayer,
                hasFeatureType: !!google.maps.FeatureType,
                hasGetFeatureLayer: !!mapInstanceRef.current.getFeatureLayer,
                apiVersion: google.maps.version || 'unknown',
                isBeta: google.maps.version?.includes('beta') || 'unknown',
                mapMethods: Object.getOwnPropertyNames(mapInstanceRef.current.constructor.prototype).filter(m => m.includes('feature') || m.includes('Feature')),
                googleMapsVersion: google.maps.version || 'unknown'
              });
              
              // Try to get feature layer information
              if (google.maps.FeatureType) {
                console.log('Available FeatureType constants:', Object.keys(google.maps.FeatureType));
              }
              
              // Dynamic postal code polygon display based on selected pincode filter
              try {
                console.log('🔍 Checking for selected pincode filter...');
                
                // Configure POSTAL_CODE feature layer
                const postalCodeLayer = mapInstanceRef.current.getFeatureLayer(google.maps.FeatureType.POSTAL_CODE);
                let postalPlaceIds: Set<string> = new Set();
                
                postalCodeLayer.style = (options: any) => {
                  const placeId = options.feature.placeId;
                  const postalCode = options.feature.displayName; // This should contain the pincode
                  
                  // Only log unique Place IDs to avoid spam
                  if (!postalPlaceIds.has(placeId)) {
                    postalPlaceIds.add(placeId);
                    console.log('📍 POSTAL_CODE Place ID found:', placeId, 'Display Name:', postalCode);
                  }
                  
                  // Check if there's a selected pincode filter and find matching pincode data
                  if (selectedPincodeFilter.length > 0) {
                    // Find the selected pincodes from pincodes collection
                    const selectedPincodeData = pincodes.filter(p => 
                      selectedPincodeFilter.includes(p.pincode) && p.isActive
                    );
                    
                    // Check if current placeId matches any selected pincode's placeId
                    const matchingPincode = selectedPincodeData.find(p => placeId === p.placeId);
                    if (matchingPincode) {
                      const style = {
                        fillColor: '#0066FF', // Bright Blue for selected postal code area
                        fillOpacity: 0.7,     // Semi-transparent
                        strokeColor: '#0044CC',
                        strokeWeight: 4.0,    // Medium border
                        strokeOpacity: 1.0
                      };
                      console.log(`🎯 FOUND selected postal code ${matchingPincode.pincode} by Place ID - APPLYING BLUE STYLE!`, style);
                      return style;
                    }
                  }
                  
                  return null; // No styling if no pincode is selected or doesn't match
                };
                
                // Store reference for future postal code boundary styling
                localityLayerRef.current = postalCodeLayer;
                
                // Check if feature layer has additional properties we need to set
                console.log('🔍 POSTAL_CODE Feature layer properties:', {
                  isAvailable: !!postalCodeLayer,
                  hasStyle: !!postalCodeLayer.style,
                  hasIsAvailable: 'isAvailable' in postalCodeLayer,
                  featureLayerProps: Object.getOwnPropertyNames(postalCodeLayer),
                  featureLayerMethods: Object.getOwnPropertyNames(Object.getPrototypeOf(postalCodeLayer))
                });
                
                // Try to ensure the layer is available/visible
                if ('isAvailable' in postalCodeLayer && typeof postalCodeLayer.isAvailable === 'boolean') {
                  console.log('🔍 POSTAL_CODE Feature layer isAvailable:', postalCodeLayer.isAvailable);
                }
                
                console.log('✅ POSTAL_CODE configured for Khar pincode 400052');
                console.log('🔍 POSTAL_CODE Feature layer object:', postalCodeLayer);
                console.log('🔍 POSTAL_CODE Feature layer style property:', postalCodeLayer.style);
                
                // Test: Try to trigger a style refresh
                setTimeout(() => {
                  console.log('🔄 Attempting to refresh POSTAL_CODE feature layer styling...');
                  if (postalCodeLayer && postalCodeLayer.style) {
                    // Force style refresh by re-assigning
                    const currentStyle = postalCodeLayer.style;
                    postalCodeLayer.style = null;
                    setTimeout(() => {
                      postalCodeLayer.style = currentStyle;
                      console.log('✅ POSTAL_CODE Feature layer style refreshed');
                    }, 100);
                  }
                }, 2000);
                
              } catch (layerError) {
                console.log('❌ POSTAL_CODE configuration failed:', layerError);
              }
            } else {
              console.log('ℹ️ Map ID or Google Maps not available for feature layers');
            }
          } catch (error) {
            console.log('ℹ️ Feature layers not supported:', (error as Error).message || error);
          }
        }

        // Clear existing markers
        if (markersRef.current.length > 0) {
          markersRef.current.forEach(marker => {
            if (marker && typeof marker.setMap === 'function') {
              marker.setMap(null);
            }
          });
          markersRef.current = [];
        }

        // Clear existing cluster
        if (clusterRef.current) {
          try {
            if (clusterRef.current.clearMarkers) {
              clusterRef.current.clearMarkers();
            }
          } catch (e) {
            // Ignore cleanup errors
          }
          clusterRef.current = null;
        }

        // Create new markers
        const validOutlets = outlets.filter(o => o.latitude && o.longitude);
        const newMarkers: any[] = [];

        validOutlets.forEach(outlet => {
          // Create marker with sequence number if route is optimized
          let markerIcon;
          
          if (selectedRouteFilter && outlet.outletSequence && outlet.outletSequence > 0) {
            // Create numbered marker for route sequence
            const canvas = document.createElement('canvas');
            const context = canvas.getContext('2d');
            canvas.width = 32;
            canvas.height = 32;
            
            // Draw circle background
            context!.beginPath();
            context!.arc(16, 16, 14, 0, 2 * Math.PI);
            // Use a distinct color (orange) instead of blue when showing route
            context!.fillStyle = '#F97316'; // Orange background
            context!.fill();
            context!.strokeStyle = '#C2410C'; // Darker orange border
            context!.lineWidth = 2;
            context!.stroke();
            
            // Draw sequence number
            context!.fillStyle = 'white';
            context!.font = 'bold 12px Arial';
            context!.textAlign = 'center';
            context!.textBaseline = 'middle';
            context!.fillText(outlet.outletSequence.toString(), 16, 16);
            
            markerIcon = {
              url: canvas.toDataURL(),
              scaledSize: new google.maps.Size(32, 32),
              anchor: new google.maps.Point(16, 16)
            };
          } else {
            // Default circular marker
            markerIcon = {
              path: google.maps.SymbolPath.CIRCLE,
              fillColor: outlet.status === 'active' ? '#10B981' : 
                        outlet.status === 'pending_approval' ? '#F59E0B' : '#EF4444',
              fillOpacity: 1,
              strokeColor: outlet.status === 'active' ? '#059669' : 
                          outlet.status === 'pending_approval' ? '#D97706' : '#DC2626',
              strokeWeight: 2,
              scale: 8
            };
          }

          const marker = new google.maps.Marker({
            position: {
              lat: parseFloat((outlet.latitude || 0).toString()),
              lng: parseFloat((outlet.longitude || 0).toString()),
            },
            title: `${outlet.name}${outlet.id ? ` #${outlet.id}` : ''}`,
            icon: markerIcon
          });

          const hierarchy = buildOutletHierarchy(outlet, localities, areas, warehouses, cities, routes);
          const classification = classifications.find(cl => cl.id === outlet.classificationId);

          const infoWindow = new google.maps.InfoWindow({
            content: `
              <div style="padding: 12px; max-width: 300px;">
                <h3 style="font-weight: 600; color: #1f2937; margin-bottom: 8px;">${outlet.name}</h3>
                <p style="font-size: 14px; color: #4b5563; margin-bottom: 8px;">${outlet.address}</p>
                ${outlet.mobile ? `<p style="font-size: 14px; color: #4b5563; margin-bottom: 4px;">📞 ${outlet.mobile}</p>` : ''}
                ${hierarchy.city ? `<p style="font-size: 14px; color: #4b5563; margin-bottom: 4px;">🏙️ ${hierarchy.city.name}</p>` : ''}
                ${hierarchy.area ? `<p style="font-size: 14px; color: #4b5563; margin-bottom: 4px;">📍 ${hierarchy.area.name}</p>` : ''}
                ${hierarchy.locality ? `<p style="font-size: 14px; color: #4b5563; margin-bottom: 4px;">🏘️ ${hierarchy.locality.name}</p>` : ''}
                ${classification ? `<p style="font-size: 14px; color: #4b5563; margin-bottom: 8px;">🏪 ${classification.name}</p>` : ''}
                <div style="display: flex; align-items: center; margin-top: 8px; margin-bottom: 8px;">
                  <div style="width: 8px; height: 8px; background-color: ${
                    outlet.status === 'active' ? '#10b981' : 
                    outlet.status === 'pending_approval' ? '#f59e0b' : 
                    '#ef4444'
                  }; border-radius: 50%; margin-right: 8px;"></div>
                  <span style="font-size: 14px; color: ${
                    outlet.status === 'active' ? '#059669' : 
                    outlet.status === 'pending_approval' ? '#d97706' : 
                    '#dc2626'
                  };">${
                    outlet.status === 'pending_approval' ? 'Pending Approval' :
                    outlet.status === 'active' ? 'Active' : 
                    outlet.status === 'inactive' ? 'Inactive' : 
                    outlet.status === 'draft' ? 'Draft' : outlet.status
                  }</span>
                </div>
                <button 
                  id="edit-outlet-${outlet.id}" 
                  style="
                    background-color: #3B82F6; 
                    color: white; 
                    border: none; 
                    padding: 8px 16px; 
                    border-radius: 6px; 
                    font-size: 12px; 
                    cursor: pointer; 
                    display: flex; 
                    align-items: center; 
                    gap: 4px;
                    font-weight: 500;
                    transition: background-color 0.2s;
                    margin-top: 8px;
                    width: 100%;
                    justify-content: center;
                  "
                  onmouseover="this.style.backgroundColor='#2563EB'"
                  onmouseout="this.style.backgroundColor='#3B82F6'"
                >
                  ✏️ Edit Outlet
                </button>
              </div>
            `
          });

          // Set up event listener for the InfoWindow's domready event to attach button click handler
          google.maps.event.addListener(infoWindow, 'domready', () => {
            const editButton = document.getElementById(`edit-outlet-${outlet.id}`);
            if (editButton) {
              editButton.addEventListener('click', () => {
                // Close the InfoWindow
                infoWindow.close();
                // Trigger edit mode via callback
                if (onEditOutlet) {
                  onEditOutlet(outlet);
                }
              });
            }
          });

          marker.addListener('click', () => {
            infoWindow.open(mapInstanceRef.current, marker);
          });

          newMarkers.push(marker);
        });

        // Add warehouse marker if route is selected and optimized
        if (selectedRouteFilter) {
          // Find the warehouse for the selected route
          const selectedRoute = routes.find(route => route.name === selectedRouteFilter);
          if (selectedRoute) {
            const warehouse = warehouses.find(w => w.warehouseId === selectedRoute.warehouseId);
            if (warehouse && warehouse.latitude && warehouse.longitude) {
              // Create warehouse marker icon
              const warehouseCanvas = document.createElement('canvas');
              const warehouseContext = warehouseCanvas.getContext('2d');
              warehouseCanvas.width = 40;
              warehouseCanvas.height = 40;
              
              // Draw warehouse icon background
              warehouseContext!.beginPath();
              warehouseContext!.arc(20, 20, 18, 0, 2 * Math.PI);
              warehouseContext!.fillStyle = '#DC2626'; // Red background for warehouse
              warehouseContext!.fill();
              warehouseContext!.strokeStyle = '#991B1B'; // Darker red border
              warehouseContext!.lineWidth = 3;
              warehouseContext!.stroke();
              
              // Draw warehouse icon (simple house/building shape)
              warehouseContext!.fillStyle = 'white';
              warehouseContext!.font = 'bold 16px Arial';
              warehouseContext!.textAlign = 'center';
              warehouseContext!.textBaseline = 'middle';
              warehouseContext!.fillText('🏭', 20, 20);

              const warehouseMarker = new google.maps.Marker({
                position: {
                  lat: parseFloat(warehouse.latitude.toString()),
                  lng: parseFloat(warehouse.longitude.toString()),
                },
                title: `${warehouse.name} (Warehouse - Start/End Point)`,
                icon: {
                  url: warehouseCanvas.toDataURL(),
                  scaledSize: new google.maps.Size(40, 40),
                  anchor: new google.maps.Point(20, 20)
                },
                zIndex: 1000 // Ensure warehouse marker appears on top
              });

              // Create info window for warehouse
              const warehouseInfoWindow = new google.maps.InfoWindow({
                content: `
                  <div style="padding: 12px; max-width: 300px;">
                    <h3 style="font-weight: 600; color: #1f2937; margin-bottom: 8px;">🏭 ${warehouse.name}</h3>
                    <p style="font-size: 14px; color: #4b5563; margin-bottom: 8px;">${warehouse.address || 'Warehouse Location'}</p>
                    <div style="display: flex; align-items: center; margin-top: 8px; margin-bottom: 8px;">
                      <div style="width: 8px; height: 8px; background-color: #DC2626; border-radius: 50%; margin-right: 8px;"></div>
                      <span style="font-size: 14px; color: #DC2626; font-weight: 600;">Route Start/End Point</span>
                    </div>
                    <p style="font-size: 12px; color: #6b7280; margin-top: 8px;">All routes begin and end at this warehouse location</p>
                  </div>
                `
              });

              warehouseMarker.addListener('click', () => {
                warehouseInfoWindow.open(mapInstanceRef.current, warehouseMarker);
              });

              newMarkers.push(warehouseMarker);
            }
          }
        }

        // Create new cluster unless we're showing a route; when showing route, place markers directly for clarity
        if (newMarkers.length > 0) {
          if (showOptimizedRoute && selectedRouteFilter) {
            // Place markers directly on the map (no clustering)
            newMarkers.forEach(m => m.setMap(mapInstanceRef.current));
            markersRef.current = newMarkers;
            clusterRef.current = null;
          } else {
            const markerCluster = new MarkerClusterer({ 
              map: mapInstanceRef.current, 
              markers: newMarkers,
              renderer: {
                render: ({ count, position }: { count: number; position: any }) => {
                  const color = count > 25 ? '#FF6B6B' : count > 10 ? '#FFB347' : '#4ECDC4';
                  const size = count > 25 ? 18 : count > 10 ? 15 : 12;
                  
                  return new google.maps.Marker({
                    position,
                    icon: {
                      path: google.maps.SymbolPath.CIRCLE,
                      fillColor: color,
                      fillOpacity: 0.8,
                      strokeColor: '#ffffff',
                      strokeWeight: 2,
                      scale: size,
                    },
                    label: {
                      text: String(count),
                      color: '#ffffff',
                      fontSize: '11px',
                      fontWeight: 'bold'
                    },
                    title: `${count} outlets in this area`,
                  });
                }
              }
            });

            markersRef.current = newMarkers;
            clusterRef.current = markerCluster;
          }
        }

        // Clear existing polygon overlays
        if (polygonOverlaysRef.current.length > 0) {
          polygonOverlaysRef.current.forEach(overlay => {
            if (overlay && typeof overlay.setMap === 'function') {
              overlay.setMap(null);
            }
          });
          polygonOverlaysRef.current = [];
        }

        // Create polygon overlays for area and locality boundaries
        if (polygonBoundaries && polygonBoundaries.length > 0) {
          const visibleBoundaries = polygonBoundaries.filter(boundary => {
            // Show area boundaries if enabled
            if (boundary.type === 'area' && showAreaBoundaries) return true;
            // Show locality boundaries if enabled
            if (boundary.type === 'locality' && showLocalityBoundaries) return true;
            return false;
          });

          visibleBoundaries.forEach(boundary => {
            // Convert normalized coordinates back to map coordinates
            // Note: This assumes the boundary coordinates are in lat/lng format
            // If they're normalized pixel coordinates, you'll need to convert them back
            let polygonCoords;
            
            if (boundary.coordinates[0]?.lat !== undefined) {
              // Already in lat/lng format
              polygonCoords = boundary.coordinates.map(coord => ({
                lat: coord.lat,
                lng: coord.lng
              }));
            } else {
              // Assume they're normalized coordinates [0-1] that need conversion
              // This would require knowledge of the original map bounds
              polygonCoords = boundary.coordinates.map((coord, index) => ({
                lat: coord.lat || 0,
                lng: coord.lng || 0
              }));
            }

            // Create polygon overlay
            const polygon = new google.maps.Polygon({
              paths: polygonCoords,
              strokeColor: boundary.type === 'area' ? '#FF6B6B' : '#4ECDC4',
              strokeOpacity: 0.8,
              strokeWeight: 2,
              fillColor: boundary.type === 'area' ? '#FF6B6B' : '#4ECDC4',
              fillOpacity: 0.2,
              clickable: true
            });

            // Create info window for polygon
            const infoWindow = new google.maps.InfoWindow({
              content: `
                <div style="padding: 8px;">
                  <h4 style="font-weight: 600; margin-bottom: 4px;">${boundary.entityName}</h4>
                  <p style="font-size: 12px; color: #666; margin-bottom: 4px;">
                    Type: ${boundary.type === 'area' ? 'Area' : 'Locality'}
                  </p>
                  ${boundary.extractionMetadata ? `
                    <p style="font-size: 12px; color: #666; margin-bottom: 2px;">
                      Points: ${boundary.extractionMetadata.num_points}
                    </p>
                    <p style="font-size: 12px; color: #666;">
                      Extracted: ${new Date(boundary.extractionMetadata.extracted_at?.toDate?.() || boundary.extractionMetadata.extracted_at).toLocaleDateString()}
                    </p>
                  ` : ''}
                </div>
              `
            });

            // Add click listener to show info window
            polygon.addListener('click', (event: any) => {
              infoWindow.setPosition(event.latLng);
              infoWindow.open(mapInstanceRef.current);
            });

            polygon.setMap(mapInstanceRef.current);
            polygonOverlaysRef.current.push(polygon);
          });
        }

        // Handle route optimization visualization
        console.log('🔄 Route visualization check:', { showOptimizedRoute, selectedRouteFilter });
        
        if (showOptimizedRoute && selectedRouteFilter) {
          console.log('🗺️ Attempting to show route for:', selectedRouteFilter);
          console.log('📍 Available outlets:', outlets.length);
          
          // Clear existing directions (all segments)
          if (directionsRenderersRef.current.length > 0) {
            directionsRenderersRef.current.forEach(r => {
              try { r.setMap(null); } catch {}
            });
            directionsRenderersRef.current = [];
          }

          // Get optimized outlets for the selected route using the same logic as route optimization
          const routeOutlets = outlets.filter(outlet => {
            if (!outlet.latitude || !outlet.longitude || !outlet.outletSequence) {
              console.log('❌ Outlet missing coords or sequence:', outlet.id, {
                lat: outlet.latitude,
                lng: outlet.longitude,
                seq: outlet.outletSequence
              });
              return false;
            }
            
            // Use the same hierarchy logic as route optimization
            const hierarchy = buildOutletHierarchy(outlet, localities, areas, warehouses, cities, routes);
            const isMatch = hierarchy.route?.name === selectedRouteFilter;
            
            console.log('🔍 Outlet hierarchy check:', outlet.id, {
              outletName: outlet.name,
              locality: hierarchy.locality?.name,
              area: hierarchy.area?.name,
              warehouse: hierarchy.warehouse?.name,
              route: hierarchy.route?.name,
              selectedRoute: selectedRouteFilter,
              matches: isMatch,
              sequence: outlet.outletSequence
            });
            
            if (isMatch) {
              console.log('✅ Outlet matches route:', outlet.id, 'sequence:', outlet.outletSequence);
            }
            
            return isMatch;
          }).sort((a, b) => (a.outletSequence || 0) - (b.outletSequence || 0));

          console.log('🎯 Filtered route outlets:', routeOutlets.length, routeOutlets.map(o => ({id: o.id, seq: o.outletSequence})));

          if (routeOutlets.length > 0) {
            // Find the warehouse for this route
            const selectedRoute = routes.find(route => route.name === selectedRouteFilter);
            const routeWarehouse = selectedRoute ? warehouses.find(wh => wh.warehouseId === selectedRoute.warehouseId) : null;

            console.log('🏭 Route warehouse:', routeWarehouse?.name, {
              lat: routeWarehouse?.latitude,
              lng: routeWarehouse?.longitude
            });

            if (routeWarehouse && routeWarehouse.latitude && routeWarehouse.longitude) {
              // Create waypoints: warehouse -> outlets -> warehouse
              const waypoints = [
                { location: { lat: routeWarehouse.latitude, lng: routeWarehouse.longitude }, stopover: false }, // Start
                ...routeOutlets.map(outlet => ({
                  location: { lat: outlet.latitude!, lng: outlet.longitude! },
                  stopover: true
                })),
                { location: { lat: routeWarehouse.latitude, lng: routeWarehouse.longitude }, stopover: false } // End
              ];

              console.log('🛣️ Waypoints created:', waypoints.length);

              // Check if Google Maps Directions API is available
              if (!google.maps.DirectionsService || !google.maps.DirectionsRenderer) {
                console.error('❌ Google Maps Directions API not available');
                return;
              }

              // Add a small delay to ensure map is fully loaded
              setTimeout(() => {
                // Build full ordered list of points: warehouse -> outlets... -> warehouse
                const allPoints = [
                  { lat: routeWarehouse.latitude, lng: routeWarehouse.longitude },
                  ...routeOutlets.map(o => ({ lat: o.latitude!, lng: o.longitude! })),
                  { lat: routeWarehouse.latitude, lng: routeWarehouse.longitude }
                ];

                // Helper: chunk into sequences with max (origin + waypoints + destination) <= 25
                const maxPointsPerRequest = 25; // i.e., <=23 waypoints
                const chunks: Array<{ origin: any; destination: any; waypoints: any[] }> = [];
                let startIndex = 0;
                while (startIndex < allPoints.length - 1) {
                  const endIndex = Math.min(startIndex + (maxPointsPerRequest - 1), allPoints.length - 1);
                  const origin = allPoints[startIndex];
                  const destination = allPoints[endIndex];
                  const waypointsList = allPoints.slice(startIndex + 1, endIndex).map(p => ({ location: p, stopover: true }));
                  chunks.push({ origin, destination, waypoints: waypointsList });
                  startIndex = endIndex; // next chunk starts where this one ended
                }

                console.log(`🧩 Route split into ${chunks.length} chunk(s) to respect waypoint limits`);

                const directionsService = new google.maps.DirectionsService();

                // Draw each chunk sequentially to avoid rate/sequence issues
                const drawChunksSequentially = async () => {
                  // Pre-compute bounds to include all points
                  const bounds = new google.maps.LatLngBounds();
                  allPoints.forEach(p => bounds.extend(new google.maps.LatLng(p.lat, p.lng)));

                  for (let idx = 0; idx < chunks.length; idx++) {
                    await new Promise<void>((resolveChunk) => {
                      const renderer = new google.maps.DirectionsRenderer({
                        polylineOptions: {
                          strokeColor: '#2563EB',
                          strokeWeight: 4,
                          strokeOpacity: 0.8
                        },
                        suppressMarkers: true,
                        draggable: false,
                        preserveViewport: true
                      });

                      const { origin, destination, waypoints } = chunks[idx];
                      const request = {
                        origin,
                        destination,
                        waypoints,
                        optimizeWaypoints: false,
                        travelMode: google.maps.TravelMode.DRIVING,
                        unitSystem: google.maps.UnitSystem.METRIC,
                        avoidHighways: false,
                        avoidTolls: false
                      } as any;

                      directionsService.route(request, (result: any, status: any) => {
                        if (status === 'OK' || (google.maps.DirectionsStatus && status === (google.maps as any).DirectionsStatus.OK)) {
                          renderer.setDirections(result);
                          renderer.setMap(mapInstanceRef.current);
                          directionsRenderersRef.current.push(renderer);
                        }
                        resolveChunk();
                      });
                    });
                  }

                  // Fit bounds once at the end
                  try { mapInstanceRef.current.fitBounds(bounds); } catch {}
                };

                // Kick off drawing
                drawChunksSequentially();
              }, 500); // 500ms delay
            } else {
              // No route selected; nothing to draw
            }
          } else {
            // Not showing optimized route
          }
        } else {
          // Clear directions if not showing optimized route
          console.log('🚫 Clearing route directions - showOptimizedRoute:', showOptimizedRoute);
          if (directionsRenderersRef.current.length > 0) {
            console.log('✅ Removing directions renderers from map');
            directionsRenderersRef.current.forEach(r => {
              try { r.setMap(null); } catch {}
            });
            directionsRenderersRef.current = [];
          } else {
            console.log('ℹ️ No directions renderer to clear');
          }
        }

      } catch (error) {
        console.error('Error initializing map:', error);
      }
    };

    initializeMap();
  }, [outlets, localities, areas, warehouses, cities, routes, classifications, showAreaBoundaries, showLocalityBoundaries, polygonBoundaries, showOptimizedRoute, selectedRouteFilter]);

  return (
    <div 
      ref={mapRef} 
      style={{ height: '100%', width: '100%' }}
      className="rounded-lg"
    />
  );
});

MapContainer.displayName = 'MapContainer';

// Load Google Maps API function
const loadGoogleMaps = () => {
  return new Promise((resolve, reject) => {
    // Check if Google Maps is already loaded
    if ((window as any).google && (window as any).google.maps) {
      resolve(true);
      return;
    }
    
    // Check if script is already loading
    if ((window as any).googleMapsLoading) {
      const checkInterval = setInterval(() => {
        if ((window as any).google && (window as any).google.maps) {
          clearInterval(checkInterval);
          resolve(true);
        }
      }, 100);
      return;
    }
    
    // Mark as loading
    (window as any).googleMapsLoading = true;
    
    // Create script element
    const script = document.createElement('script');
    const apiKey = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;
    
    if (!apiKey) {
      reject(new Error('Google Maps API key not found'));
      return;
    }
    
  script.src = `https://maps.googleapis.com/maps/api/js?key=${apiKey}&libraries=places&loading=async&v=beta`;
    script.async = true;
    script.defer = true;
    
    script.onload = () => {
      (window as any).googleMapsLoading = false;
      resolve(true);
    };
    
    script.onerror = () => {
      (window as any).googleMapsLoading = false;
      reject(new Error('Failed to load Google Maps API'));
    };
    
    document.head.appendChild(script);
  });
};

// Utility function to build hierarchical data from outlet's localityId
const buildOutletHierarchy = (outlet: Outlet, localities: Locality[], areas: Area[], warehouses: Warehouse[], cities: City[], routes: Route[]) => {
  // Follow the proper hierarchy chain: outlet -> locality -> area -> warehouse -> city
  const locality = localities.find(l => l.id === outlet.localityId);
  const area = locality ? areas.find(a => a.id === locality.areaId) : null;
  
  // Use the same working pattern as Areas tab - check both document ID and warehouseId field
  const warehouse = area ? warehouses.find(w => w.warehouseId === area.warehouseId) : null;
  
  // Use the same working mapping as warehouses tab: warehouse.cityCode maps to city.cityCode
  const city = warehouse ? cities.find(c => c.cityCode === warehouse.cityCode) : null;
  
  const route = locality ? routes.find(r => r.routeId === locality.routeId) : null;
  
  
  return { 
    locality, 
    area, 
    warehouse, 
    city, 
    route,
    // Helper function to get display names - provide fallbacks for robust display
    cityName: city?.name || 'Unknown City',
    areaName: area?.name || 'Unknown Area',
    warehouseName: warehouse?.name || 'Unknown Warehouse',
    routeName: route?.name || 'No Route'
  };
};

const DistributionNetworkSection: React.FC = () => {
  const { currentUser } = useAuth();
  
  // Distribution Network states
  const [activeTab, setActiveTab] = useState<'cities' | 'areas' | 'localities' | 'classifications' | 'outlets' | 'warehouses' | 'routes'>('cities');
  const [showAreaRouting, setShowAreaRouting] = useState(false);
  const [showLocalityRouting, setShowLocalityRouting] = useState(false);
  const [cities, setCities] = useState<City[]>([]);
  const [areas, setAreas] = useState<Area[]>([]);
  const [localities, setLocalities] = useState<Locality[]>([]);
  const [classifications, setClassifications] = useState<OutletClassification[]>([]);
  const [outlets, setOutlets] = useState<Outlet[]>([]);
  const [warehouses, setWarehouses] = useState<Warehouse[]>([]);
  const [routes, setRoutes] = useState<Route[]>([]);
  const [pincodes, setPincodes] = useState<Pincode[]>([]);
  const [networkLoading, setNetworkLoading] = useState(false);
  
  // Filter and Sort states
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<'all' | 'active' | 'inactive' | 'pending_approval'>('all');
  const [sortField, setSortField] = useState<string>('name');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');
  const [locationFilter, setLocationFilter] = useState('');
  
  // Column-specific filters for outlets
  const [cityFilter, setCityFilter] = useState('');
  const [warehouseFilter, setWarehouseFilter] = useState('');
  const [areaFilter, setAreaFilter] = useState('');
  const [localityFilter, setLocalityFilter] = useState('');
  const [routeFilter, setRouteFilter] = useState('');
  const [pincodeFilter, setPincodeFilter] = useState<string[]>([]);
  const [exoticFruitsFilter, setExoticFruitsFilter] = useState<'all' | 'yes' | 'no'>('all');
  const [statusOutletFilter, setStatusOutletFilter] = useState<'all' | 'active' | 'inactive' | 'pending_approval' | 'draft'>('all');
  const [createdDateFromFilter, setCreatedDateFromFilter] = useState('');
  const [createdDateToFilter, setCreatedDateToFilter] = useState('');
  const [showDateRangePicker, setShowDateRangePicker] = useState(false);
  const [showPincodeDropdown, setShowPincodeDropdown] = useState(false);
  
  // Pagination states
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 25;
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingItem, setEditingItem] = useState<any>(null);
  const [formData, setFormData] = useState<any>({});
  // Helper: compute next Route ID (RT###)
  const computeNextRouteId = (): string => {
    const prefix = 'RT';
    const nums = (routes || [])
      .map((r: any) => String(r?.routeId || ''))
      .map((id: string) => {
        const m = id.match(/^RT(\d{3,})$/i);
        return m ? parseInt(m[1], 10) : 0;
      });
    const max = nums.length ? Math.max(...nums) : 0;
    const next = (max || 0) + 1;
    return `${prefix}${String(next).padStart(3, '0')}`;
  };
  const [showMigrationModal, setShowMigrationModal] = useState(false);
  const [migrationData, setMigrationData] = useState<any>(null);
  
  // Route optimization states
  const [isOptimizingRoute, setIsOptimizingRoute] = useState(false);
  const [optimizationMessage, setOptimizationMessage] = useState('');
  const [showOptimizedRoute, setShowOptimizedRoute] = useState(false);
  // Routes tab: optimize via popup modal
  const [showRouteOptimizeModal, setShowRouteOptimizeModal] = useState(false);
  // Ensure map initializes after modal paints
  const [modalMapReady, setModalMapReady] = useState(false);
  // Route-specific outlets for modal preview (only outlets belonging to selected route)
  const modalRouteOutlets = useMemo(() => {
    if (!routeFilter) return [] as Outlet[];
    return outlets.filter(outlet => {
      const hierarchy = buildOutletHierarchy(outlet, localities, areas, warehouses, cities, routes);
      return hierarchy.route?.name === routeFilter;
    });
  }, [outlets, localities, areas, warehouses, cities, routes, routeFilter]);
  const [migrationLoading, setMigrationLoading] = useState(false);
  
  // Boundary visibility states for map
  const [showAreaBoundaries, setShowAreaBoundaries] = useState(false);
  const [showLocalityBoundaries, setShowLocalityBoundaries] = useState(false);
  const [cleanupLoading, setCleanupLoading] = useState(false);
  const [showCleanupModal, setShowCleanupModal] = useState(false);
  // Rebuild Localities states
  const [rebuildRunning, setRebuildRunning] = useState(false);
  const [showRebuildModal, setShowRebuildModal] = useState(false);
  const [rebuildDryRun, setRebuildDryRun] = useState(false);
  const [rebuildProgress, setRebuildProgress] = useState<{ total: number; completed: number; failed: number; pending: number; renamed: number; retained: number; lastMessage?: string }>({ total: 0, completed: 0, failed: 0, pending: 0, renamed: 0, retained: 0 });
  
  // Map-related states for outlets
  const [outletViewTab, setOutletViewTab] = useState<'list' | 'map'>('map');
  
  // AddOutlet component integration
  const [showAddOutletModal, setShowAddOutletModal] = useState(false);
  const [editingOutlet, setEditingOutlet] = useState<any>(null);

  // Polygon extraction states
  const [showPolygonExtractor, setShowPolygonExtractor] = useState(false);
  const [polygonExtractionTarget, setPolygonExtractionTarget] = useState<{ type: 'area' | 'locality'; id: string; name: string } | null>(null);
  const [polygonBoundaries, setPolygonBoundaries] = useState<PolygonBoundary[]>([]);

  useEffect(() => {
    loadNetworkData();
  }, []);

  // Click outside handler for date range picker
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      if (showDateRangePicker && !target.closest('.date-range-picker')) {
        setShowDateRangePicker(false);
      }
    };

    if (showDateRangePicker) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showDateRangePicker]);

  const loadNetworkData = async () => {
    setNetworkLoading(true);
    try {
      const [citiesData, warehousesData, areasData, routesData, localitiesData, classificationsData, outletsData, polygonBoundariesData, pincodesData] = await Promise.all([
        getDocuments('cities'),
        getDocuments('warehouses'),
        getDocuments('areas'),
        getDocuments('routes'),
        getDocuments('localities'),
        getDocuments('outlet_classifications'),
        getDocuments('outlets'),
        getDocuments('polygon_boundaries'),
        getDocuments('pincodes')
      ]);
      
      setCities(citiesData as City[]);
      setWarehouses(warehousesData as Warehouse[]);
      setAreas(areasData as Area[]);
      setRoutes(routesData as Route[]);
      setLocalities(localitiesData as Locality[]);
      setClassifications(classificationsData as OutletClassification[]);
      setOutlets(outletsData as Outlet[]);
      setPolygonBoundaries(polygonBoundariesData as PolygonBoundary[]);
      setPincodes(pincodesData as Pincode[]);
    } catch (error) {
      console.error('Error loading network data:', error);
    } finally {
      setNetworkLoading(false);
    }
  };


  // --- CSV helpers: robust parse/serialize and dynamic headers ---
  const parseCSVLine = (line: string): string[] => {
    const result: string[] = [];
    let current = '';
    let inQuotes = false;
    for (let i = 0; i < line.length; i++) {
      const char = line[i];
      if (inQuotes) {
        if (char === '"') {
          // Escaped quote if next char is also a quote
          if (i + 1 < line.length && line[i + 1] === '"') {
            current += '"';
            i++; // skip next
          } else {
            inQuotes = false;
          }
        } else {
          current += char;
        }
      } else {
        if (char === ',') {
          result.push(current);
          current = '';
        } else if (char === '"') {
          inQuotes = true;
        } else {
          current += char;
        }
      }
    }
    result.push(current);
    return result.map(v => v.replace(/\r$/, '')); // trim trailing CR
  };

  const safeParseValue = (value: string): any => {
    if (value === undefined || value === null) return undefined;
    const trimmed = value.trim();
    if (trimmed === '') return undefined;
    // Booleans
    if (/^(true|false)$/i.test(trimmed)) return trimmed.toLowerCase() === 'true';
    // JSON arrays/objects
    if ((trimmed.startsWith('[') && trimmed.endsWith(']')) || (trimmed.startsWith('{') && trimmed.endsWith('}'))) {
      try { return JSON.parse(trimmed); } catch { /* fallthrough */ }
    }
    return value; // keep as string to avoid unintended coercion
  };

  const sanitizeForUpsert = (data: Record<string, any>, type: string): { payload: Record<string, any>, documentId?: string } => {
    const payload: Record<string, any> = {};
    const documentId = data.documentId as string | undefined;
    const excluded = new Set<string>([
      'documentId', // never write back
      'id', // internal/business id not auto-managed here
      'createdAt', 'updatedAt', // server managed
      // derived fields per collection type
      ...(type === 'areas' ? ['warehouseName'] : []),
      ...(type === 'localities' ? ['areaName'] : []),
      ...(type === 'outlets' ? [
        // derived display fields
        'city', 'classification', 'subArea',
        // media/photo and bulky fields to ignore on CSV import
        'businessCardPhoto',
        'mainOutletDisplayPhoto',
        'outletInteriorExteriorPhotos',
        'photo',
        'photoURL',
        'qrCodeData',
        'visitingCardImage',
        'wtfBrandingStickerPhoto'
      ] : []),
    ]);
    Object.entries(data).forEach(([k, v]) => {
      if (!excluded.has(k)) payload[k] = v;
    });
    return { payload, documentId };
  };

  const buildDynamicHeaders = (rows: any[]): string[] => {
    const keys = new Set<string>();
    keys.add('documentId');
    for (const row of rows) {
      Object.keys(row || {}).forEach(k => keys.add(k));
    }
    // Ensure documentId first, id second if present, then the rest sorted for stability
    const all = Array.from(keys);
    const rest = all.filter(k => k !== 'documentId' && k !== 'id').sort();
    return ['documentId', ...(keys.has('id') ? ['id'] : []), ...rest];
  };

  const formatDateOnly = (d: Date): string => {
    // Use ISO (UTC) date-only to keep CSV stable across locales
    return d.toISOString().slice(0, 10);
  };

  const isFirestoreTimestamp = (v: any): v is { toDate?: () => Date, seconds?: number, nanoseconds?: number, _seconds?: number, _nanoseconds?: number } => {
    if (!v || typeof v !== 'object') return false;
    // Firestore Timestamp typically has toDate(), seconds/nanoseconds; also handle serialized _seconds/_nanoseconds
    return typeof (v as any).toDate === 'function' || typeof (v as any).seconds === 'number' || typeof (v as any)._seconds === 'number';
  };

  const serializeCSVValue = (value: any): string => {
    if (value === null || value === undefined) return '';
    // Date objects
    if (value instanceof Date) return formatDateOnly(value);
    // Firestore Timestamp objects or serialized timestamp-like objects
    if (isFirestoreTimestamp(value)) {
      try {
        if (typeof value.toDate === 'function') return formatDateOnly(value.toDate());
        const secs = (value as any).seconds ?? (value as any)._seconds;
        if (typeof secs === 'number') return formatDateOnly(new Date(secs * 1000));
      } catch {/* fallthrough */}
    }
    // ISO-like date strings -> format as date-only if valid
    if (typeof value === 'string') {
      const looksISO = /^(\d{4}-\d{2}-\d{2})(?:[T\s].*)?$/.test(value) || /Z$/.test(value) || /T\d{2}:\d{2}:\d{2}/.test(value);
      if (looksISO) {
        const d = new Date(value);
        if (!isNaN(d.getTime())) return formatDateOnly(d);
      }
    }
    if (typeof value === 'object') {
      try { return JSON.stringify(value); } catch { return String(value); }
    }
    const str = String(value);
    const needsQuoting = /[",\n]/.test(str);
    const escaped = str.replace(/"/g, '""');
    return needsQuoting ? `"${escaped}"` : escaped;
  };

  const handleCSVUpload = async (file: File, type: string) => {
    try {
      const text = await file.text();
      const lines = text.split(/\r?\n/).filter(line => line.trim() !== '');
      if (lines.length < 2) throw new Error('CSV has no data rows');
      const headers = parseCSVLine(lines[0]).map(h => h.trim());
      
      let uploadCount = 0;
      for (let i = 1; i < lines.length; i++) {
        const values = parseCSVLine(lines[i]);
        if (values.length === 0) continue;
        const row: any = {};
        headers.forEach((header, index) => {
          row[header] = safeParseValue(values[index]);
        });

        // Default isActive to true if not present
        if (row.isActive === undefined) row.isActive = true;

        const { payload, documentId } = sanitizeForUpsert(row, type);
        try {
          if (documentId) {
            // Try update first
            await updateDocument(type, documentId, { ...payload, updatedBy: currentUser?.id || '' });
          } else {
            await createDocument(type, { ...payload, createdBy: currentUser?.id || '', updatedBy: currentUser?.id || '' });
          }
          uploadCount++;
        } catch (e) {
          // If update failed because doc may not exist, create with custom id if provided
          if (documentId) {
            await createDocument(type, { ...payload, createdBy: currentUser?.id || '', updatedBy: currentUser?.id || '' }, documentId);
            uploadCount++;
          } else {
            console.error('Row upload failed:', e);
          }
        }
      }
      
      loadNetworkData();
      setShowUploadModal(false);
      alert(`Successfully processed ${uploadCount} ${type} record(s).`);
    } catch (error) {
      console.error('Error uploading CSV:', error);
      alert('Error uploading CSV. Please check the file format.');
    }
  };

  const downloadCSV = (data: any[], filename: string, headers?: string[]) => {
    try {
      const hdrs = headers && headers.length ? headers : buildDynamicHeaders(data);
      const csvRows: string[] = [];
      csvRows.push(hdrs.join(','));
      for (const item of data) {
        const row = hdrs.map(h => serializeCSVValue(item[h]));
        csvRows.push(row.join(','));
      }
      const csvContent = csvRows.join('\n');

      // Create blob and download
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      link.setAttribute('download', filename);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (error) {
      console.error('Error downloading CSV:', error);
      alert('Error downloading CSV file.');
    }
  };

  const handleDownload = () => {
    const timestamp = new Date().toISOString().split('T')[0];
    
    switch (activeTab) {
      case 'cities':
        downloadCSV(cities, `cities_${timestamp}.csv`, ['name', 'state', 'isActive']);
        break;
      case 'areas':
        const areasWithCityNames = areas.map(area => ({
          ...area,
          warehouseName: warehouses.find(w => w.id === area.warehouseId)?.name || ''
        }));
        // Dynamic headers: include all fields present
        downloadCSV(areasWithCityNames, `areas_${timestamp}.csv`);
        break;
      case 'localities':
        const localitiesWithAreaNames = localities.map(locality => ({
          ...locality,
          areaName: areas.find(a => a.id === locality.areaId)?.name || ''
        }));
        // Dynamic headers: include all fields present
        downloadCSV(localitiesWithAreaNames, `localities_${timestamp}.csv`);
        break;
      case 'classifications':
        downloadCSV(classifications, `classifications_${timestamp}.csv`, ['name', 'description', 'isActive']);
        break;
      case 'outlets':
        const outletsWithLocationNames = outlets.map(outlet => {
          const hierarchy = buildOutletHierarchy(outlet, localities, areas, warehouses, cities, routes);
          return {
            ...outlet,
            city: hierarchy.cityName,
            classification: classifications.find(cl => cl.id === outlet.classificationId)?.name || '',
            subArea: (outlet as any).subArea || hierarchy.locality?.name || ''
          };
        });
        // Dynamic headers: include all fields present except excluded media/binary fields
        const outletExcluded = new Set<string>([
          'businessCardPhoto',
          'mainOutletDisplayPhoto',
          'outletInteriorExteriorPhotos',
          'photo',
          'photoURL',
          'qrCodeData',
          'visitingCardImage',
          'wtfBrandingStickerPhoto'
        ]);
        const outletHeaders = buildDynamicHeaders(outletsWithLocationNames).filter(h => !outletExcluded.has(h));
        downloadCSV(outletsWithLocationNames, `outlets_${timestamp}.csv`, outletHeaders);
        break;
    }
  };

  const handleEdit = async (item: any) => {
    // All entities including outlets use the generic edit modal
    setEditingItem(item);
    
    // Debug: Log the item data to see what fields are available
    if (activeTab === 'outlets') {
      const mappedWarehouseId = (() => {
        const wh = warehouses.find(w => w.warehouseId === item?.warehouseId || w.id === item?.warehouseId);
        return wh?.id || '';
      })();
      setFormData({
        ...item,
        name: item?.name || '',
        routeId: item?.routeId || '',
        warehouseId: mappedWarehouseId,
        wtfBrandingStickerPhoto: item.wtfBrandingStickerPhoto,
        outletInteriorExteriorPhotos: item.outletInteriorExteriorPhotos,
        mainOutletDisplayPhoto: item.mainOutletDisplayPhoto
      });
    }
    
    // For routes, ensure warehouse data is properly loaded
    if (activeTab === 'routes') {
      const warehouse = warehouses.find(w => w.warehouseId === item.warehouseId);
      setFormData({ 
        ...item, 
        warehouseId: warehouse?.id || item.warehouseId, // Use Firebase document ID for dropdown
        warehouseName: warehouse?.name || item.warehouseName || ''
      });
    } 
    // For areas, ensure warehouse data is properly loaded
    else if (activeTab === 'areas') {
      const warehouse = warehouses.find(w => w.warehouseId === item.warehouseId);
      setFormData({ 
        ...item, 
        warehouseId: warehouse?.id || item.warehouseId, // Use Firebase document ID for dropdown
        warehouseName: warehouse?.name || item.warehouseName || ''
      });
    }
    // For localities, just load the form data without auto-selection
    else if (activeTab === 'localities') {
      setFormData({ ...item });
    }
    else {
      setFormData({ ...item });
    }
    
    // Debug: Log formData after setting it
    if (activeTab === 'outlets') {
      setTimeout(() => {
        console.log('FormData after setting:', { ...item });
        console.log('Business card image in formData:', item.businessCardPhoto);
      }, 100);
    }
    
    setShowEditModal(true);
  };

  const handleDelete = async (id: string, documentId?: string) => {
    if (!window.confirm('Are you sure you want to delete this item?')) return;
    
    try {
      let collectionName = '';
      switch (activeTab) {
        case 'cities': collectionName = 'cities'; break;
        case 'areas': collectionName = 'areas'; break;
        case 'localities': collectionName = 'localities'; break;
        case 'classifications': collectionName = 'outlet_classifications'; break;
        case 'outlets': collectionName = 'outlets'; break;
        case 'warehouses': collectionName = 'warehouses'; break;
        case 'routes': collectionName = 'routes'; break;
      }
      
      // For outlets after migration, use documentId if available, otherwise use id
      const deleteId = documentId || id;

      // If deleting a locality, first null-out references from any outlets pointing to it
      if (collectionName === 'localities') {
        try {
          const batch = fsWriteBatch(db);
          const q = fsQuery(fsCollection(db, 'outlets'), fsWhere('localityId', '==', deleteId));
          const snap = await fsGetDocs(q);
          if (!snap.empty) {
            snap.forEach((docSnap) => {
              batch.update(docSnap.ref, {
                localityId: null,
                localityName: null,
                updatedAt: fsServerTimestamp(),
                updatedBy: currentUser?.id || ''
              } as any);
            });
            await batch.commit();
            console.log(`✅ Cleared locality references from ${snap.size} outlet(s) before locality deletion.`);
          }
        } catch (cleanupErr) {
          console.error('Error clearing locality references from outlets:', cleanupErr);
          // Proceeding with deletion even if cleanup encounters issues, but user is informed
        }
      }
      
      await deleteDocument(collectionName, deleteId);
      loadNetworkData();
      alert('Item deleted successfully!');
    } catch (error) {
      console.error('Error deleting item:', error);
      alert('Error deleting item. Please try again.');
    }
  };

  const handleApproveOutlet = async () => {
    if (!editingItem || activeTab !== 'outlets') return;
    
    try {
      const documentIdToUse = editingItem.documentId || editingItem.id;
      
      await updateDocument('outlets', documentIdToUse, {
        ...editingItem,
        status: 'active',
        approvedAt: new Date(),
        approvedBy: currentUser?.id || '',
        updatedAt: new Date(),
        updatedBy: currentUser?.id || ''
      });
      
      // Update form data to reflect the change
      setFormData({ ...formData, status: 'active' });
      
      // Refresh data
      loadNetworkData();
      
      alert('Outlet approved successfully!');
    } catch (error) {
      console.error('Error approving outlet:', error);
      alert('Error approving outlet. Please try again.');
    }
  };

  const handleSave = async () => {
    try {
      let collectionName = '';
      switch (activeTab) {
        case 'cities': collectionName = 'cities'; break;
        case 'areas': collectionName = 'areas'; break;
        case 'localities': collectionName = 'localities'; break;
        case 'classifications': collectionName = 'outlet_classifications'; break;
        case 'outlets': collectionName = 'outlets'; break;
        case 'warehouses': collectionName = 'warehouses'; break;
        case 'routes': collectionName = 'routes'; break;
      }

      const data: any = {
        ...formData,
        createdAt: editingItem ? editingItem.createdAt : new Date(),
        updatedAt: new Date(),
        createdBy: editingItem ? (editingItem.createdBy || currentUser?.id || '') : currentUser?.id || '',
        updatedBy: currentUser?.id || ''
      };

      // Auto-assign next Route ID when creating a new route
      if (!editingItem && activeTab === 'routes') {
        const computeNextRouteId = (): string => {
          const prefix = 'RT';
          const nums = (routes || [])
            .map((r: any) => String(r?.routeId || ''))
            .map((id: string) => {
              const m = id.match(/^RT(\d{3,})$/i);
              return m ? parseInt(m[1], 10) : 0;
            });
          const max = nums.length ? Math.max(...nums) : 0;
          const next = (max || 0) + 1;
          return `${prefix}${String(next).padStart(3, '0')}`;
        };
        if (!data.routeId) {
          data.routeId = computeNextRouteId();
        }
      }

      // For routes, convert Firebase document ID back to custom warehouse ID
      if (activeTab === 'routes' && data.warehouseId) {
        const selectedWarehouse = warehouses.find(w => w.id === data.warehouseId);
        if (selectedWarehouse) {
          data.warehouseId = selectedWarehouse.warehouseId; // Use custom warehouse ID
          data.warehouseName = selectedWarehouse.name;
        }
      }

      // For areas, convert Firebase document ID back to custom warehouse ID
      if (activeTab === 'areas' && data.warehouseId) {
        const selectedWarehouse = warehouses.find(w => w.id === data.warehouseId);
        if (selectedWarehouse) {
          data.warehouseId = selectedWarehouse.warehouseId; // Use custom warehouse ID
          data.warehouseName = selectedWarehouse.name;
        }
      }

      // Set correct status/isActive field based on entity type
      if (activeTab === 'outlets') {
        data.status = formData.status || 'active';
        // Remove isActive if it exists since outlets use status
        delete data.isActive;
      } else {
        data.isActive = formData.isActive !== undefined ? formData.isActive : true;
        // Remove status if it exists since other entities use isActive
        delete data.status;
      }

      if (editingItem) {
        // For outlets, use documentId to handle the Firebase document ID vs custom ID issue
        const documentIdToUse = activeTab === 'outlets' 
          ? (editingItem.documentId || editingItem.id)
          : editingItem.id;
        
        await updateDocument(collectionName, documentIdToUse, data);
        
        // Cascading deactivation logic
        if (editingItem && !data.isActive && activeTab !== 'outlets') {
          await handleCascadingDeactivation(activeTab, editingItem.id);
        } else if (editingItem && data.status === 'inactive' && activeTab === 'outlets') {
          // Outlets don't have children, so no cascading needed
        }
        
        alert('Item updated successfully!');
      } else {
        const newDocumentId = await createDocument(collectionName, data);
        
        // Auto-assign route for new localities
        if (activeTab === 'localities' && newDocumentId) {
          await autoAssignRouteToNewLocality(newDocumentId, data);
        }
        
        alert('Item created successfully!');
      }

      loadNetworkData();
      setShowEditModal(false);
      setShowAddModal(false);
      setEditingItem(null);
      setFormData({});
    } catch (error) {
      console.error('Error saving item:', error);
      alert('Error saving item. Please try again.');
    }
  };

  const handleCascadingDeactivation = async (entityType: string, entityId: string) => {
    try {
      let cascadePromises: Promise<void>[] = [];
      
      if (entityType === 'cities') {
        // Deactivate all areas in warehouses in this city
        const cityWarehouses = warehouses.filter(warehouse => (warehouse as any).cityCode === entityId);
        const warehouseIds = cityWarehouses.map(warehouse => warehouse.id);
        const cityAreas = areas.filter(area => warehouseIds.includes(area.warehouseId));
        cascadePromises = cityAreas.map(area => 
          updateDocument('areas', area.id, { ...area, isActive: false, updatedAt: new Date(), updatedBy: currentUser?.id || '' })
        );
        
        // Deactivate all localities in these areas
        const ids = cityAreas.map(area => area.id);
        const cityLocalities = localities.filter(locality => ids.includes(locality.id));
        cascadePromises.push(...cityLocalities.map(locality => 
          updateDocument('localities', locality.id, { ...locality, isActive: false, updatedAt: new Date(), updatedBy: currentUser?.id || '' })
        ));
        
        // Deactivate all outlets in these localities
        const localityIds = cityLocalities.map(locality => locality.id);
        const cityOutlets = outlets.filter(outlet => {
          const hierarchy = buildOutletHierarchy(outlet, localities, areas, warehouses, cities, routes);
          return hierarchy.city?.id === entityId;
        });
        cascadePromises.push(...cityOutlets.map(outlet => 
          updateDocument('outlets', outlet.documentId || outlet.id, { ...outlet, status: 'inactive', updatedAt: new Date(), updatedBy: currentUser?.id || '' })
        ));
      } 
      else if (entityType === 'areas') {
        // Deactivate all localities in this area
        const areaLocalities = localities.filter(locality => locality.id === entityId);
        cascadePromises = areaLocalities.map(locality => 
          updateDocument('localities', locality.id, { ...locality, isActive: false, updatedAt: new Date(), updatedBy: currentUser?.id || '' })
        );
        
        // Deactivate all outlets in these localities
        const localityIds = areaLocalities.map(locality => locality.id);
        const areaOutlets = outlets.filter(outlet => {
          const hierarchy = buildOutletHierarchy(outlet, localities, areas, warehouses, cities, routes);
          return hierarchy.area?.id === entityId;
        });
        cascadePromises.push(...areaOutlets.map(outlet => 
          updateDocument('outlets', outlet.documentId || outlet.id, { ...outlet, status: 'inactive', updatedAt: new Date(), updatedBy: currentUser?.id || '' })
        ));
      } 
      else if (entityType === 'localities') {
        // Deactivate all outlets in this locality
        const localityOutlets = outlets.filter(outlet => outlet.localityId === entityId);
        cascadePromises = localityOutlets.map(outlet => 
          updateDocument('outlets', outlet.documentId || outlet.id, { ...outlet, status: 'inactive', updatedAt: new Date(), updatedBy: currentUser?.id || '' })
        );
      }
      
      await Promise.all(cascadePromises);
      
      if (cascadePromises.length > 0) {
        console.log(`Cascaded deactivation: ${cascadePromises.length} child entities deactivated`);
      }
    } catch (error) {
      console.error('Error in cascading deactivation:', error);
      alert('Warning: Some child entities may not have been deactivated. Please refresh and check.');
    }
  };

  // Auto-assign route to newly created locality
  const autoAssignRouteToNewLocality = async (localityId: string, localityData: any) => {
    try {
      console.log('🚀 Starting auto-route assignment for locality:', {
        localityId,
        localityName: localityData.name,
        areaId: localityData.areaId
      });

      // Find the area for this locality
      const area = areas.find(a => a.id === localityData.areaId);
      if (!area) {
        console.log('❌ No area found for auto-route assignment. Available areas:', areas.map(a => ({id: a.id, name: a.name})));
        return;
      }

      console.log('✅ Found area:', {
        areaId: area.id,
        areaName: area.name,
        areaWarehouseId: area.warehouseId
      });

      // Find the warehouse for this area (use dual lookup for compatibility)
      const warehouse = warehouses.find(w => w.warehouseId === area.warehouseId) || 
                       warehouses.find(w => w.id === area.warehouseId);
      if (!warehouse) {
        console.log('❌ No warehouse found for auto-route assignment. Available warehouses:', warehouses.map(w => ({id: w.id, warehouseId: w.warehouseId, name: w.name})));
        return;
      }

      console.log('✅ Found warehouse:', {
        warehouseId: warehouse.id,
        customWarehouseId: warehouse.warehouseId,
        warehouseName: warehouse.name
      });

      // Find active routes for this warehouse (use dual lookup for compatibility)
      const eligibleRoutes = routes.filter(route => 
        (route.warehouseId === area.warehouseId || route.warehouseId === warehouse.warehouseId) && 
        route.isActive
      );

      console.log('🔍 Route matching:', {
        areaWarehouseId: area.warehouseId,
        warehouseCustomId: warehouse.warehouseId,
        totalRoutes: routes.length,
        eligibleRoutes: eligibleRoutes.length,
        allRoutes: routes.map(r => ({id: r.id, name: r.name, warehouseId: r.warehouseId, isActive: r.isActive}))
      });

      if (eligibleRoutes.length === 0) {
        console.log('❌ No active routes found for auto-assignment in warehouse:', warehouse.name);
        return;
      }

      // Prefer the user's present route (routeFilter) if available and eligible; fallback to first eligible route
      let targetRoute = eligibleRoutes[0];
      if (routeFilter) {
        const preferred = routes.find(r => r.name === routeFilter);
        if (preferred && eligibleRoutes.some(r => r.routeId === preferred.routeId)) {
          targetRoute = preferred;
        }
      }

      console.log('🎯 Attempting to assign route:', {
        routeId: targetRoute.routeId,
        routeName: targetRoute.name,
        localityId
      });

      // Assign the route to the locality
      await handleLocalityRouteAssign(localityId, targetRoute.routeId);
      
      console.log(`✅ Auto-assigned locality "${localityData.name}" to route "${targetRoute.name}"`);
    } catch (error) {
      console.error('❌ Error in auto-route assignment:', error);
      // Don't show error to user since this is a nice-to-have feature
    }
  };

  const handleLocalityRouteAssign = async (localityId: string, routeId: string | null) => {
    try {
      setNetworkLoading(true);
      
      const locality = localities.find(l => l.id === localityId);
      if (!locality) return;

      const route = routes.find(r => r.routeId === routeId);
      
      // Step 1: Update locality with route assignment (or null for unassignment)
      const localityUpdateData = {
        ...locality,
        routeId: routeId || null,
        routeName: route?.name || null,
        updatedAt: new Date(),
        updatedBy: currentUser?.id || ''
      };

      await updateDocument('localities', localityId, localityUpdateData);

      // Step 2: Find ALL outlets in this locality
      const outletsInLocality = outlets.filter(outlet => 
        outlet.localityId === localityId
      );

      // Step 3: Batch update ALL outlets with new route assignment
      // If the route actually changed, also clear outletSequence as per business rule
      const isRouteChange = (locality.routeId || null) !== (routeId || null);
      const outletUpdatePromises = outletsInLocality.map(outlet => {
        const updates: any = {
          ...outlet,
          routeId: routeId || null,
          routeName: route?.name || null,
          updatedAt: new Date(),
          updatedBy: currentUser?.id || ''
        };
        if (isRouteChange) {
          updates.outletSequence = null;
        }
        return updateDocument('outlets', outlet.documentId || outlet.id, updates);
      });

      // Step 4: Wait for ALL outlet updates to complete
      await Promise.all(outletUpdatePromises);

      // Step 5: Refresh data and show success
      loadNetworkData();
      
  const actionType = routeId ? 'assigned' : 'unassigned';
      const routeName = route?.name || 'route';
  alert(`Route ${actionType} successfully! Updated ${outletsInLocality.length} outlets in locality "${locality.name}"${isRouteChange ? ' (sequences cleared)' : ''}.`);
      
    } catch (error) {
      console.error('Error assigning route:', error);
      alert('Error updating route assignment. Please try again.');
    } finally {
      setNetworkLoading(false);
    }
  };

  // Migration system to add sequential serial numbers to outlets only
  const prepareMigrationData = async () => {
    try {
      setMigrationLoading(true);
      
      // Load outlets data fresh to ensure we have the latest
      const outletsData = await getDocuments('outlets');

      // Sort outlets by creation date (chronological order)
      const sortByDate = (a: any, b: any) => {
        const aDate = a.createdAt?.toDate ? a.createdAt.toDate() : new Date(a.createdAt || 0);
        const bDate = b.createdAt?.toDate ? b.createdAt.toDate() : new Date(b.createdAt || 0);
        return aDate.getTime() - bDate.getTime();
      };

      const sortedOutlets = [...outletsData].sort(sortByDate);

      // Create migration mapping with sequential serial numbers starting from 100001
      let serialCounter = 100001;
      const outletMigrationData: any = {};

      // Build migration map for outlets only
      sortedOutlets.forEach(outlet => {
        outletMigrationData[outlet.id] = {
          oldId: outlet.id,
          serialNumber: serialCounter,
          item: outlet
        };
        serialCounter++;
      });

      const migrationData = {
        outlets: outletMigrationData,
        totalItems: serialCounter - 100001,
        startSerial: 100001,
        endSerial: serialCounter - 1
      };

      setMigrationData(migrationData);
      
    } catch (error) {
      console.error('Error preparing migration data:', error);
      alert('Error preparing migration data. Please try again.');
    } finally {
      setMigrationLoading(false);
    }
  };

  const executeMigration = async () => {
    if (!migrationData || !window.confirm('This will replace the internal ID field in outlet documents with sequential serial numbers. Document IDs and all relationships will remain unchanged. Continue?')) {
      return;
    }

    try {
      setMigrationLoading(true);
      
      console.log('Starting outlet ID field replacement migration...');
      
      // Simple update operation - just replace the internal 'id' field
      const updatePromises: Promise<void>[] = [];
      
      for (const [documentId, migration] of Object.entries(migrationData.outlets)) {
        const migrationItem = migration as { oldId: string; serialNumber: number; item: any };
        
        // Update only the internal 'id' field with the sequential serial number
        updatePromises.push(
          updateDocument('outlets', documentId, {
            id: migrationItem.serialNumber.toString(), // Replace internal id field
            migratedAt: new Date(),
            migratedBy: currentUser?.id || '',
            originalId: documentId // Keep track of original Firestore document ID
          })
        );
      }

      // Execute all updates in batches to avoid overwhelming Firebase
      const batchSize = 50;
      for (let i = 0; i < updatePromises.length; i += batchSize) {
        const batch = updatePromises.slice(i, i + batchSize);
        await Promise.all(batch);
        console.log(`Updated batch ${Math.floor(i / batchSize) + 1}/${Math.ceil(updatePromises.length / batchSize)}`);
      }

      console.log('Migration completed successfully!');
      
      // Refresh all data
      await loadNetworkData();
      
      // Close modal and reset state
      setShowMigrationModal(false);
      setMigrationData(null);
      
      alert(`Outlet ID field migration completed successfully! 
      
• Total outlets updated: ${migrationData.totalItems}
• Sequential IDs assigned: ${migrationData.startSerial} to ${migrationData.endSerial}
• All Firestore document IDs preserved
• All relationships intact

The internal 'id' field in each outlet now shows user-friendly sequential numbers while keeping all system functionality unchanged.`);
      
    } catch (error: any) {
      console.error('Error during migration:', error);
      alert(`Error during migration: ${error?.message || 'Unknown error'}

The migration was interrupted. Some outlets may have been updated while others were not. Please check your data and try again if needed.`);
    } finally {
      setMigrationLoading(false);
    }
  };

  // Cleanup function to remove redundant fields from outlets
  const executeOutletCleanup = async () => {
    try {
      setCleanupLoading(true);
      
      console.log('Starting outlet image fields migration...');
      
      // Load outlets data fresh to ensure we have the latest
      const outletsData = await getDocuments('outlets');
      
      if (outletsData.length === 0) {
        alert('No outlets found to migrate.');
        return;
      }

      const updatePromises: Promise<void>[] = [];
      let processedCount = 0;
      let migratedFields = {
        photos: 0,
        photoURL: 0,
        visitingCardImage: 0,
        wtfStickerImage: 0
      };

      for (const outlet of outletsData) {
        const updates: any = {};
        let hasUpdates = false;
        
        // Task 1: Migrate photos → outletInteriorExteriorPhotos
        if (outlet.photos && !outlet.outletInteriorExteriorPhotos) {
          updates.outletInteriorExteriorPhotos = outlet.photos;
          updates.photos = deleteField();
          migratedFields.photos++;
          hasUpdates = true;
        }
        
        // Task 2: Migrate photoURL → mainOutletDisplayPhoto
        if (outlet.photoURL && !outlet.mainOutletDisplayPhoto) {
          updates.mainOutletDisplayPhoto = outlet.photoURL;
          updates.photoURL = deleteField();
          migratedFields.photoURL++;
          hasUpdates = true;
        }
        
        // Task 3: Migrate visitingCardImage → businessCardPhoto
        if (outlet.visitingCardImage && !outlet.businessCardPhoto) {
          updates.businessCardPhoto = outlet.visitingCardImage;
          updates.visitingCardImage = deleteField();
          migratedFields.visitingCardImage++;
          hasUpdates = true;
        }
        
        // Task 4: Migrate wtfStickerImage → wtfBrandingStickerPhoto
        if (outlet.wtfStickerImage && !outlet.wtfBrandingStickerPhoto) {
          updates.wtfBrandingStickerPhoto = outlet.wtfStickerImage;
          updates.wtfStickerImage = deleteField();
          migratedFields.wtfStickerImage++;
          hasUpdates = true;
        }

        // Only update if there are changes
        if (hasUpdates) {
          updatePromises.push(
            updateDocument('outlets', outlet.documentId || outlet.id, updates)
          );
          processedCount++;
        }
      }

      if (updatePromises.length === 0) {
        alert('No image fields found to migrate. All outlets already use the new field names.');
        return;
      }

      // Execute all updates in batches to avoid overwhelming Firebase
      const batchSize = 50;
      for (let i = 0; i < updatePromises.length; i += batchSize) {
        const batch = updatePromises.slice(i, i + batchSize);
        await Promise.all(batch);
        console.log(`Cleaned batch ${Math.floor(i / batchSize) + 1}/${Math.ceil(updatePromises.length / batchSize)}`);
      }

      console.log('Image fields migration completed successfully!');
      
      // Refresh all data
      await loadNetworkData();
      
      alert(`Outlet image fields migration completed successfully!

• Total outlets processed: ${processedCount}
• Fields migrated:
  - photos → outletInteriorExteriorPhotos: ${migratedFields.photos} outlets
  - photoURL → mainOutletDisplayPhoto: ${migratedFields.photoURL} outlets  
  - visitingCardImage → businessCardPhoto: ${migratedFields.visitingCardImage} outlets
  - wtfStickerImage → wtfBrandingStickerPhoto: ${migratedFields.wtfStickerImage} outlets

All outlets now use descriptive field names for better data organization.`);
      
    } catch (error: any) {
      console.error('Error during image fields migration:', error);
      alert(`Error during migration: ${error?.message || 'Unknown error'}

The migration was interrupted. Some outlets may have been processed while others were not. Please check your data and try again if needed.`);
    } finally {
      setCleanupLoading(false);
    }
  };

  // Backup and drop redundant/unused fields from outlets
  const [dropLoading, setDropLoading] = useState(false);
  const [showDropFieldsModal, setShowDropFieldsModal] = useState(false);
  const executeOutletFieldDrop = async () => {
    try {
      setDropLoading(true);

      // 1) Fetch latest outlets
      const outletsData = await getDocuments('outlets');
      if (outletsData.length === 0) {
        alert('No outlets found. Nothing to drop.');
        return;
      }

      // 2) Create timestamped backup collection name
      const now = new Date();
      const pad = (n: number) => n.toString().padStart(2, '0');
      const backupName = `outlets_backup_${now.getFullYear()}${pad(now.getMonth() + 1)}${pad(now.getDate())}_${pad(now.getHours())}${pad(now.getMinutes())}${pad(now.getSeconds())}`;

      // 3) Prepare fields to remove
      const fieldsToDelete = [
        'addedByName',
        'areaId',
        'areaName',
        'city',
        'classificationId',
        'classificationName',
        'createdAt',
        'createdBy',
        'localityName',
        'pincode',
        'routeName',
        'serialNumber',
        'updatedByName',
        'photos',
        'photoURL',
        'visitingCardImage',
        'wtfStickerImage'
      ] as const;

      let totalDocsBackedUp = 0;
      let totalDocsUpdated = 0;
      const fieldDropCount: Record<string, number> = Object.fromEntries(fieldsToDelete.map(f => [f, 0]));

      // 4) Backup and update in batches
      const backupPromises: Promise<void>[] = [];
      const updatePromises: Promise<void>[] = [];

      for (const outlet of outletsData) {
        const docId = outlet.documentId || outlet.id;
        if (!docId) continue;

        // Backup: copy full doc into backup collection with same docId
        backupPromises.push(
          fsSetDoc(
            fsDoc(db, backupName, docId),
            {
              ...outlet,
              backupMeta: {
                sourceCollection: 'outlets',
                originalDocumentId: docId,
                backedUpAt: fsServerTimestamp(),
                backedUpBy: currentUser?.id || 'system'
              }
            }
          ).then(() => { totalDocsBackedUp++; })
        );

        // Build deletion updates (only include fields that are present to reduce writes)
        const updates: any = {};
        let hasUpdates = false;
        for (const f of fieldsToDelete) {
          if (outlet[f as keyof typeof outlet] !== undefined) {
            updates[f] = deleteField();
            fieldDropCount[f]++;
            hasUpdates = true;
          }
        }
        if (hasUpdates) {
          updatePromises.push(
            updateDocument('outlets', docId, updates).then(() => { totalDocsUpdated++; })
          );
        }
      }

      // Execute backups in batches of 50
      const runBatches = async (promises: Promise<void>[], label: string) => {
        const batchSize = 50;
        for (let i = 0; i < promises.length; i += batchSize) {
          const batch = promises.slice(i, i + batchSize);
          await Promise.all(batch);
          console.log(`${label}: Completed batch ${Math.floor(i / batchSize) + 1}/${Math.ceil(promises.length / batchSize)}`);
        }
      };

      await runBatches(backupPromises, 'Backup');
      await runBatches(updatePromises, 'Field drop');

      // refresh
      await loadNetworkData();

      // Summary
      const droppedSummary = Object.entries(fieldDropCount)
        .filter(([, v]) => v > 0)
        .map(([k, v]) => `  - ${k}: ${v} documents`)
        .join('\n');

      alert(`Outlets backup and field drop completed.\n\nBackup collection: ${backupName}\nTotal docs backed up: ${totalDocsBackedUp}\nTotal docs updated: ${totalDocsUpdated}\n\nFields dropped:\n${droppedSummary || '  - None (no docs contained the specified fields)'}`);

      setShowDropFieldsModal(false);
    } catch (err: any) {
      console.error('Error during outlet field drop:', err);
      alert(`Error during field drop: ${err?.message || 'Unknown error'}`);
    } finally {
      setDropLoading(false);
    }
  };

  // Backup and drop redundant/unused fields from localities
  const [dropLocalitiesLoading, setDropLocalitiesLoading] = useState(false);
  const [showDropLocalitiesModal, setShowDropLocalitiesModal] = useState(false);
  const executeLocalityFieldDrop = async () => {
    try {
      setDropLocalitiesLoading(true);

      // 1) Fetch latest localities
      const localitiesData = await getDocuments('localities');
      if (localitiesData.length === 0) {
        alert('No localities found. Nothing to drop.');
        return;
      }

      // 2) Create timestamped backup collection name
      const now = new Date();
      const pad = (n: number) => n.toString().padStart(2, '0');
      const backupName = `localities_backup_${now.getFullYear()}${pad(now.getMonth() + 1)}${pad(now.getDate())}_${pad(now.getHours())}${pad(now.getMinutes())}${pad(now.getSeconds())}`;

      // 3) Prepare fields to remove for Localities
      const fieldsToDelete = [
        'areaName',
        'cityId',
        'cityName',
        'geocoded',
        'geocodedAt',
        'geocodedBy',
        'routeDocumentId',
        'routeName',
        'source'
      ] as const;

      let totalDocsBackedUp = 0;
      let totalDocsUpdated = 0;
      const fieldDropCount: Record<string, number> = Object.fromEntries(fieldsToDelete.map(f => [f, 0]));

      // 4) Backup and update in batches
      const backupPromises: Promise<void>[] = [];
      const updatePromises: Promise<void>[] = [];

      for (const loc of localitiesData) {
        const docId = (loc as any).documentId || (loc as any).id;
        if (!docId) continue;

        // Backup: copy full doc into backup collection with same docId
        backupPromises.push(
          fsSetDoc(
            fsDoc(db, backupName, docId),
            {
              ...loc,
              backupMeta: {
                sourceCollection: 'localities',
                originalDocumentId: docId,
                backedUpAt: fsServerTimestamp(),
                backedUpBy: currentUser?.id || 'system'
              }
            }
          ).then(() => { totalDocsBackedUp++; })
        );

        // Build deletion updates
        const updates: any = {};
        let hasUpdates = false;
        for (const f of fieldsToDelete) {
          if ((loc as any)[f] !== undefined) {
            updates[f] = deleteField();
            fieldDropCount[f]++;
            hasUpdates = true;
          }
        }
        if (hasUpdates) {
          updatePromises.push(
            updateDocument('localities', docId, updates).then(() => { totalDocsUpdated++; })
          );
        }
      }

      // Execute backups/updates in batches of 50
      const runBatches = async (promises: Promise<void>[], label: string) => {
        const batchSize = 50;
        for (let i = 0; i < promises.length; i += batchSize) {
          const batch = promises.slice(i, i + batchSize);
          await Promise.all(batch);
          console.log(`${label}: Completed batch ${Math.floor(i / batchSize) + 1}/${Math.ceil(promises.length / batchSize)}`);
        }
      };

      await runBatches(backupPromises, 'Localities Backup');
      await runBatches(updatePromises, 'Localities Field drop');

      // refresh
      await loadNetworkData();

      // Summary
      const droppedSummary = Object.entries(fieldDropCount)
        .filter(([, v]) => v > 0)
        .map(([k, v]) => `  - ${k}: ${v} documents`)
        .join('\n');

      alert(`Localities backup and field drop completed.\n\nBackup collection: ${backupName}\nTotal docs backed up: ${totalDocsBackedUp}\nTotal docs updated: ${totalDocsUpdated}\n\nFields dropped:\n${droppedSummary || '  - None (no docs contained the specified fields)'}`);

      setShowDropLocalitiesModal(false);
    } catch (err: any) {
      console.error('Error during locality field drop:', err);
      alert(`Error during locality field drop: ${err?.message || 'Unknown error'}`);
    } finally {
      setDropLocalitiesLoading(false);
    }
  };

  // Polygon extraction functions
  const handleExtractPolygon = (type: 'area' | 'locality', id: string, name: string) => {
    setPolygonExtractionTarget({ type, id, name });
    setShowPolygonExtractor(true);
  };

  const handlePolygonExtracted = async (polygon: number[][], metadata: any) => {
    if (!polygonExtractionTarget) return;

    try {
      const { type, id, name } = polygonExtractionTarget;
      
      // Convert pixel coordinates to normalized coordinates (0-1 range)
      const normalizedCoordinates = polygon.map(([x, y]) => ({
        lat: y / metadata.image_dimensions.height,
        lng: x / metadata.image_dimensions.width
      }));

      const polygonBoundary: Omit<PolygonBoundary, 'id'> = {
        coordinates: normalizedCoordinates,
        type,
        entityId: id,
        entityName: name,
        extractionMetadata: {
          ...metadata,
          extracted_at: new Date(),
          extracted_by: currentUser?.id || ''
        },
        isActive: true,
        createdAt: new Date(),
        createdBy: currentUser?.id || ''
      };

      const boundaryId = await createDocument('polygon_boundaries', polygonBoundary);
      
      // Refresh polygon boundaries data
      const updatedBoundaries = await getDocuments('polygon_boundaries');
      setPolygonBoundaries(updatedBoundaries as PolygonBoundary[]);
      
      alert(`Polygon boundary extracted and saved successfully for ${name}!`);
      
    } catch (error) {
      console.error('Error saving polygon boundary:', error);
      alert('Error saving polygon boundary. Please try again.');
    }
  };

  const handleDeletePolygonBoundary = async (boundaryId: string) => {
    if (!window.confirm('Are you sure you want to delete this polygon boundary?')) return;
    
    try {
      await deleteDocument('polygon_boundaries', boundaryId);
      
      // Refresh polygon boundaries data
      const updatedBoundaries = await getDocuments('polygon_boundaries');
      setPolygonBoundaries(updatedBoundaries as PolygonBoundary[]);
      
      alert('Polygon boundary deleted successfully!');
    } catch (error) {
      console.error('Error deleting polygon boundary:', error);
      alert('Error deleting polygon boundary. Please try again.');
    }
  };

  // Route optimization handler
  const handleOptimizeRoute = async (routeNameOverride?: string) => {
    const targetRouteName = routeNameOverride ?? routeFilter;
    if (!targetRouteName) {
      alert('Please select a route first to optimize.');
      return;
    }

    setIsOptimizingRoute(true);
    setOptimizationMessage('Resetting existing sequences...');

    try {
      // Step 1: Reset all outlet sequences for this route to null
      setOptimizationMessage('Clearing existing outlet sequences...');
      
      const allRouteOutlets = filterAndSortData(outlets).filter(outlet => {
        const hierarchy = buildOutletHierarchy(outlet, localities, areas, warehouses, cities, routes);
        return hierarchy.route?.name === targetRouteName;
      });

      // Clear sequences for all outlets in this route first
      if (allRouteOutlets.length > 0) {
        const clearPromises = allRouteOutlets.map(outlet =>
          updateDocument('outlets', outlet.documentId || outlet.id, {
            outletSequence: null,
            updatedAt: new Date(),
            updatedBy: currentUser?.id || 'system'
          })
        );
        await Promise.all(clearPromises);
      }

      // Step 2: Get only ACTIVE outlets with coordinates for optimization
      setOptimizationMessage('Finding active outlets for optimization...');
      
      const activeRouteOutlets = allRouteOutlets.filter(outlet => 
        outlet.status === 'active' && outlet.latitude && outlet.longitude
      );

      if (activeRouteOutlets.length === 0) {
        throw new Error('No active outlets with coordinates found for this route.');
      }

      // Find the warehouse for this route
      const selectedRoute = routes.find(route => route.name === targetRouteName);
      if (!selectedRoute) {
        throw new Error('Route not found.');
      }

      const routeWarehouse = warehouses.find(wh => wh.warehouseId === selectedRoute.warehouseId);
      if (!routeWarehouse || !routeWarehouse.latitude || !routeWarehouse.longitude) {
        throw new Error('Warehouse coordinates not found. Please add coordinates to the warehouse.');
      }

  setOptimizationMessage(`Calculating optimal sequence for ${activeRouteOutlets.length} active outlets...`);

      // Run TSP optimization on ACTIVE outlets only
      const optimizedOutlets = solveTSP(activeRouteOutlets, routeWarehouse.latitude, routeWarehouse.longitude);

      setOptimizationMessage('Saving optimized sequence...');

      // Update outlet sequences in the database (only for active outlets)
      const updatePromises = optimizedOutlets.map((outlet, index) => {
        const sequenceNumber = index + 1;
        return updateDocument('outlets', outlet.documentId || outlet.id, {
          outletSequence: sequenceNumber,
          updatedAt: new Date(),
          updatedBy: currentUser?.id || 'system'
        });
      });

      await Promise.all(updatePromises);

      // Refresh outlets data to show updated sequences
      await loadNetworkData();

  setOptimizationMessage(`✅ Route optimized! Sequence assigned to ${optimizedOutlets.length} active outlets.`);
      setShowOptimizedRoute(true);

      // Clear message after 10 seconds but keep route visible
      setTimeout(() => {
        setOptimizationMessage('');
      }, 10000);

    } catch (error) {
      console.error('Route optimization error:', error);
      setOptimizationMessage(`❌ Error: ${error instanceof Error ? error.message : 'Optimization failed'}`);
      setTimeout(() => setOptimizationMessage(''), 5000);
    } finally {
      setIsOptimizingRoute(false);
    }
  };

  // Rebuild Localities workflow
  const handleRebuildLocalities = async () => {
    if (!window.confirm('This will backup outlets and localities, then iterate all outlets to rebuild locality assignments. Proceed?')) return;
    setRebuildRunning(true);
    try {
      // Load fresh snapshots
      const [allOutlets, allAreas, allLocalities, allWarehouses, allCities] = await Promise.all([
        getDocuments('outlets'),
        getDocuments('areas'),
        getDocuments('localities'),
        getDocuments('warehouses'),
        getDocuments('cities')
      ]);

      // Backup collections
      const now = new Date();
      const pad = (n: number) => n.toString().padStart(2, '0');
      const ts = `${now.getFullYear()}${pad(now.getMonth()+1)}${pad(now.getDate())}_${pad(now.getHours())}${pad(now.getMinutes())}${pad(now.getSeconds())}`;
      const outletsBackup = `outlets_backup_${ts}`;
      const localitiesBackup = `localities_backup_${ts}`;

  setRebuildProgress({ total: allOutlets.length, completed: 0, failed: 0, pending: allOutlets.length, renamed: 0, retained: 0, lastMessage: 'Creating backups…' });

      // Backup outlets and localities (in batches)
      const backupBatchSize = 50;
      if (!rebuildDryRun) {
        const outletBackupPromises: Promise<any>[] = [];
        for (const outlet of allOutlets) {
          const docId = outlet.documentId || outlet.id;
          if (!docId) continue;
          outletBackupPromises.push(
            fsSetDoc(
              fsDoc(db, outletsBackup, docId),
              { ...outlet, backupMeta: { sourceCollection: 'outlets', originalDocumentId: docId, backedUpAt: fsServerTimestamp(), backedUpBy: currentUser?.id || 'system' } }
            )
          );
        }
        for (let i = 0; i < outletBackupPromises.length; i += backupBatchSize) {
          await Promise.all(outletBackupPromises.slice(i, i + backupBatchSize));
        }
      }

      if (!rebuildDryRun) {
        const localityBackupPromises: Promise<any>[] = [];
        for (const loc of allLocalities) {
          const docId = (loc as any).documentId || (loc as any).id;
          if (!docId) continue;
          localityBackupPromises.push(
            fsSetDoc(
              fsDoc(db, localitiesBackup, docId),
              { ...loc, backupMeta: { sourceCollection: 'localities', originalDocumentId: docId, backedUpAt: fsServerTimestamp(), backedUpBy: currentUser?.id || 'system' } }
            )
          );
        }
        for (let i = 0; i < localityBackupPromises.length; i += backupBatchSize) {
          await Promise.all(localityBackupPromises.slice(i, i + backupBatchSize));
        }

        // Clear existing localities before rebuild (actual run only)
        setRebuildProgress(prev => ({ ...prev, lastMessage: 'Clearing existing localities…' }));
        const locs = await getDocuments('localities');
        for (let i = 0; i < locs.length; i += 400) {
          const batch = fsWriteBatch(db);
          const slice = locs.slice(i, i + 400);
          slice.forEach((loc: any) => {
            const lid = loc.documentId || loc.id;
            if (lid) batch.delete(fsDoc(db, 'localities', lid));
          });
          await batch.commit();
        }
        // Reset local in-memory structures post-clear
        (allLocalities as any[]).length = 0;
      }

      // Build quick lookup maps
  const areasById = new Map(allAreas.map((a: any) => [a.id, a]));
      const localitiesById = new Map((allLocalities as any[]).map(l => [l.id, l]));
      const localitiesByNameKey = new Map<string, any>();
      for (const l of allLocalities as any[]) {
        const areaName = areasById.get(l.areaId)?.name || l.areaName || '';
        const key = `${(areaName || '').toLowerCase()}::${(l.name || '').toLowerCase()}`;
        if (key.trim()) localitiesByNameKey.set(key, l);
      }
      // Track whether we cleared localities to avoid double-deletes later
      const localitiesCleared = !rebuildDryRun; // true if we cleared in actual run

      // Deterministic English-only validation
      const isEnglishValid = (raw?: string): boolean => {
        if (!raw) return false;
        const s = String(raw).trim();
        if (s.length < 2 || s.length > 64) return false;
        if (/[^\x20-\x7E]/.test(s)) return false; // ASCII printable
        if (!/[A-Za-z]/.test(s)) return false; // must contain letters
        // Balanced parentheses and no dangling "(E"
        let depth = 0;
        for (const ch of s) {
          if (ch === '(') depth++; else if (ch === ')') { depth--; if (depth < 0) return false; }
        }
        if (depth !== 0) return false;
        if (/\(\s*[A-Za-z]?\s*$/.test(s)) return false;
        return true;
      };

      // Helper: ensure area exists; if not, create
      const ensureArea = async (areaName: string): Promise<string> => {
        const existing = allAreas.find((a: any) => (a.name || '').toLowerCase() === areaName.toLowerCase());
        if (existing) return existing.id;
        // Fallback: attach to the first warehouse
        const firstWh = allWarehouses[0];
        const newAreaId = await createDocument('areas', {
          name: areaName,
          warehouseId: 'MUM-WH02',
          isActive: true,
          googlePlaceId: null,
          createdBy: currentUser?.id || 'system',
          updatedBy: currentUser?.id || 'system'
        });
        try { await updateDocument('areas', newAreaId, { id: newAreaId }); } catch {}
        const areaObj = { id: newAreaId, name: areaName } as any;
        allAreas.push(areaObj);
        areasById.set(newAreaId, areaObj);
        return newAreaId;
      };

      // Helper: ensure locality exists under area; returns locality id
      const ensureLocality = async (areaId: string, areaName: string, localityName: string, pincode?: string): Promise<string> => {
        const key = `${(areaName || '').toLowerCase()}::${(localityName || '').toLowerCase()}`;
        const existing = localitiesByNameKey.get(key);
        if (existing) return existing.id;
        // derive city from area's warehouse
        const newLocId = await createDocument('localities', {
          name: localityName,
          areaId,
          pincode: pincode || null,
          isActive: true,
          createdBy: currentUser?.id || 'system',
          updatedBy: currentUser?.id || 'system'
        });
        const locObj = { id: newLocId, name: localityName, areaId } as any;
        localitiesByNameKey.set(key, locObj);
        return newLocId;
      };

      // Ensure Google Maps is available
      await loadGoogleMaps();

    // Iterate outlets with throttling
  const throttleMs = rebuildDryRun ? 400 : 0; // no throttling on actual run
    const results = { completed: 0, failed: 0 };
    let renamedCount = 0;
    let retainedCount = 0;
      for (let idx = 0; idx < allOutlets.length; idx++) {
        const outlet = allOutlets[idx] as Outlet;
        const docId = outlet.documentId || outlet.id;
        setRebuildProgress(prev => ({ ...prev, total: allOutlets.length, completed: results.completed, failed: results.failed, pending: allOutlets.length - results.completed - results.failed, lastMessage: `Processing outlet ${idx + 1}/${allOutlets.length} (${outlet.name || outlet.id})` }));
        try {
          if (!outlet.latitude || !outlet.longitude) {
            results.failed++;
            continue;
          }

          // Attempt to reuse dry-run sample to avoid fresh Google calls in actual run
          let af: any | null = null;
          let parsed: any = { success: false };
          if (!rebuildDryRun) {
            try {
              const latDocs = await getDocumentsByQuery('address_samples', 'coordinates.lat', '==', outlet.latitude);
              const epsilon = 1e-6;
              const matches = (latDocs || []).filter((d: any) => d?.coordinates && typeof d.coordinates.lng === 'number' && Math.abs(d.coordinates.lng - outlet.longitude!) < epsilon);
              const withParser = matches.filter((d: any) => !!d.parserOutput);
              if (withParser.length > 0) {
                withParser.sort((a: any, b: any) => {
                  const ad = a.createdAt?.toDate ? a.createdAt.toDate() : (a.createdAt ? new Date(a.createdAt) : new Date(0));
                  const bd = b.createdAt?.toDate ? b.createdAt.toDate() : (b.createdAt ? new Date(b.createdAt) : new Date(0));
                  return bd.getTime() - ad.getTime();
                });
                af = withParser[0].parserOutput;
                parsed = { success: true, addressFields: af };
              }
            } catch {}
          }

          // If no sample available (or dry run), parse via Google
          if (!parsed.success) {
            // Actual run: do not call Google, skip if no cached sample
            if (!rebuildDryRun) { results.failed++; continue; }
            // Dry-run: allow Google parsing
            parsed = await geocodingService.parseForOutletFields(outlet.latitude, outlet.longitude, undefined);
          }

          if (!parsed.success || !parsed.addressFields) {
            // Even on failure, log the response if possible (dry run only)
            if (rebuildDryRun) {
              try {
                const rev = await geocodingService.reverseGeocode(outlet.latitude, outlet.longitude);
                if (rev.success) await logAddressComponentsSample('LocalityRebuild', outlet.latitude, outlet.longitude, rev.rawResponse, undefined);
              } catch {}
            }
            results.failed++;
            continue;
          }
          af = af || parsed.addressFields;

          // Log sample for audit using the rawResponse we already have (dry run only)
          if (rebuildDryRun) {
            try {
              if (parsed.rawResponse) {
                await logAddressComponentsSample('LocalityRebuild', outlet.latitude, outlet.longitude, parsed.rawResponse, af);
              }
            } catch {}
          }

          // Determine area and locality names from parser
          const areaName = af.suggestedAreaName?.trim();
          const localityName = af.suggestedLocalityName?.trim();

          if (!areaName || !localityName) {
            results.failed++;
            continue;
          }

          // English-only gating
          if (!isEnglishValid(localityName)) { results.failed++; continue; }
          // Area resolve/create (English-only)
          const areaId = await ensureArea(areaName);
          if (!areaId) { results.failed++; continue; }
          const newLocalityId = await ensureLocality(areaId, areasById.get(areaId)?.name || areaName, localityName, af.pincode);

          // If outlet currently has a locality and it differs by name/area, delete the old locality
          const currentLocalityId = (outlet as any).localityId;
          if (!localitiesCleared && currentLocalityId && currentLocalityId !== newLocalityId) {
            const currentLoc = localitiesById.get(currentLocalityId);
            if (currentLoc) {
              const currentAreaName = areasById.get(currentLoc.areaId)?.name || currentLoc.areaName || '';
              const isSameByName = (currentLoc.name || '').trim().toLowerCase() === localityName.toLowerCase();
              const isSameArea = (currentAreaName || '').trim().toLowerCase() === areaName.toLowerCase();
              const isDifferent = !(isSameByName && isSameArea);
              if (isDifferent) {
                try {
                  if (!rebuildDryRun) {
                    // Null-out all outlets referencing this locality
                    const batch = fsWriteBatch(db);
                    const q = fsQuery(fsCollection(db, 'outlets'), fsWhere('localityId', '==', currentLocalityId));
                    const snap = await fsGetDocs(q);
                    snap.forEach((docSnap) => {
                      batch.update(docSnap.ref, {
                        localityId: null,
                        localityName: null,
                        updatedAt: fsServerTimestamp(),
                        updatedBy: currentUser?.id || 'system'
                      } as any);
                    });
                    if (snap.size > 0) {
                      await batch.commit();
                    }
                    // Delete the locality document itself
                    await deleteDocument('localities', currentLocalityId);
                  }
                  renamedCount++;
                  setRebuildProgress(prev => ({ ...prev, renamed: renamedCount }));
                } catch (cleanupErr) {
                  console.warn('Failed to cleanup/delete old locality for outlet', outlet.id, cleanupErr);
                }
              } else {
                retainedCount++;
                setRebuildProgress(prev => ({ ...prev, retained: retainedCount }));
              }
            }
          } else {
            // No prior locality or already the same locality id
            retainedCount++;
            setRebuildProgress(prev => ({ ...prev, retained: retainedCount }));
          }

          // Update outlet with required fields
          const updatePayload: Partial<Outlet> & { updatedAt: any; updatedBy: string } = {
            localityId: newLocalityId,
            streetName: af.streetName || outlet.streetName || '',
            subLocality: af.subLocality || outlet.subLocality || '',
            updatedAt: new Date(),
            updatedBy: currentUser?.id || 'system'
          } as any;
          if (!rebuildDryRun) {
            await updateDocument('outlets', docId, updatePayload);
          }
          results.completed++;
        } catch (err) {
          console.warn('Rebuild error for outlet', outlet.id, err);
          results.failed++;
        }

        // Respect throttle
        await new Promise(res => setTimeout(res, throttleMs));
      }

      setRebuildProgress(prev => ({ ...prev, completed: results.completed, failed: results.failed, pending: Math.max(0, allOutlets.length - results.completed - results.failed), renamed: renamedCount, retained: retainedCount, lastMessage: 'Rebuild completed.' }));
      if (!rebuildDryRun) {
        await loadNetworkData();
      }
      alert(`Rebuild ${rebuildDryRun ? '(dry run) ' : ''}done. Completed: ${results.completed}, Renamed: ${renamedCount}, Retained: ${retainedCount}, Failed: ${results.failed}`);
    } catch (e: any) {
      console.error('Rebuild Localities failed:', e);
      alert(`Rebuild failed: ${e?.message || 'Unknown error'}`);
    } finally {
      setRebuildRunning(false);
    }
  };

  const getPolygonBoundariesForEntity = (type: 'area' | 'locality', entityId: string) => {
    return polygonBoundaries.filter(boundary => 
      boundary.type === type && 
      boundary.entityId === entityId && 
      boundary.isActive
    );
  };

  // Filter and Sort functions - returns full filtered and sorted dataset (hoisted to avoid TDZ)
  function filterAndSortData(data: any[]) {
    let filteredData = [...data];

    // Apply search filter for non-outlets tabs
    if (searchTerm.trim() && activeTab !== 'outlets') {
      filteredData = filteredData.filter(item => {
        const searchableFields = ['name', 'description', 'mobile', 'address', 'pincode'];
        return searchableFields.some(field => 
          item[field]?.toString().toLowerCase().includes(searchTerm.toLowerCase())
        );
      });
    }

    // Apply status filter for non-outlets tabs
    if (statusFilter !== 'all' && activeTab !== 'outlets') {
      filteredData = filteredData.filter(item => {
        // Handle entities with isActive field
        const isActive = item.isActive === true;
        return statusFilter === 'active' ? isActive : !isActive;
      });
    }

    // Apply location filter (hierarchical) for non-outlets tabs
    if (locationFilter && activeTab !== 'cities' && activeTab !== 'outlets') {
      filteredData = filteredData.filter(item => {
        switch (activeTab) {
          case 'areas':
            const warehouse = warehouses.find(w => w.id === item.warehouseId);
            const city = cities.find(c => c.cityCode === warehouse?.cityCode);
            return city?.name.toLowerCase().includes(locationFilter.toLowerCase());
          case 'localities':
            const area = areas.find(a => a.id === item.id);
            return area?.name.toLowerCase().includes(locationFilter.toLowerCase());
          default:
            return true;
        }
      });
    }

    // Apply outlet-specific filters when on outlets tab
    if (activeTab === 'outlets') {
      // Status filter for outlets
      if (statusOutletFilter !== 'all') {
        filteredData = filteredData.filter(item => item.status === statusOutletFilter);
      }

      // City filter
      if (cityFilter.trim()) {
        filteredData = filteredData.filter(item => {
          const hierarchy = buildOutletHierarchy(item, localities, areas, warehouses, cities, routes);
          return hierarchy.city?.name?.toLowerCase().includes(cityFilter.toLowerCase());
        });
      }

      // Warehouse filter
      if (warehouseFilter.trim()) {
        filteredData = filteredData.filter(item => {
          const hierarchy = buildOutletHierarchy(item, localities, areas, warehouses, cities, routes);
          return hierarchy.warehouse?.name?.toLowerCase().includes(warehouseFilter.toLowerCase());
        });
      }

      // Area filter
      if (areaFilter.trim()) {
        filteredData = filteredData.filter(item => {
          const hierarchy = buildOutletHierarchy(item, localities, areas, warehouses, cities, routes);
          return hierarchy.area?.name?.toLowerCase().includes(areaFilter.toLowerCase());
        });
      }

      // Locality filter
      if (localityFilter.trim()) {
        filteredData = filteredData.filter(item => {
          const hierarchy = buildOutletHierarchy(item, localities, areas, warehouses, cities, routes);
          return hierarchy.locality?.name?.toLowerCase().includes(localityFilter.toLowerCase());
        });
      }

      // Route filter
      if (routeFilter.trim()) {
        filteredData = filteredData.filter(item => {
          const hierarchy = buildOutletHierarchy(item, localities, areas, warehouses, cities, routes);
          return hierarchy.route?.name?.toLowerCase().includes(routeFilter.toLowerCase());
        });
      }

      // Pincode filter (derive from locality.pincode)
      if (pincodeFilter.length > 0) {
        filteredData = filteredData.filter(item => {
          const hierarchy = buildOutletHierarchy(item, localities, areas, warehouses, cities, routes);
          const pin = hierarchy.locality?.pincode?.toString() || '';
          return pin && pincodeFilter.some(pincode => pin.toLowerCase().includes(pincode.toLowerCase()));
        });
      }

      // Exotic fruits filter
      if (exoticFruitsFilter !== 'all') {
        filteredData = filteredData.filter(item => {
          const hasExotic = (item as any).hasExoticFruits === true;
          return exoticFruitsFilter === 'yes' ? hasExotic : !hasExotic;
        });
      }

      // Date range filter (use addedDate only)
      if (createdDateFromFilter || createdDateToFilter) {
        filteredData = filteredData.filter(item => {
          const itemDate = item.addedDate;
          if (!itemDate) return !createdDateFromFilter && !createdDateToFilter;

          const date = itemDate.toDate ? itemDate.toDate() : new Date(itemDate);
          const dateStr = date.toISOString().split('T')[0];

          const matchesFromDate = createdDateFromFilter ? (dateStr >= createdDateFromFilter) : true;
          const matchesToDate = createdDateToFilter ? (dateStr <= createdDateToFilter) : true;
          return matchesFromDate && matchesToDate;
        });
      }
    }

    // Apply sorting
    filteredData.sort((a, b) => {
      let aValue = a[sortField];
      let bValue = b[sortField];

      // Handle nested values for display
      if (sortField === 'warehouseName' && activeTab === 'areas') {
        const aWarehouse = warehouses.find(w => w.id === a.warehouseId) || 
                          warehouses.find(w => w.warehouseId === a.warehouseId);
        const bWarehouse = warehouses.find(w => w.id === b.warehouseId) || 
                          warehouses.find(w => w.warehouseId === b.warehouseId);
        aValue = aWarehouse?.name || '';
        bValue = bWarehouse?.name || '';
      } else if (sortField === 'areaName' && activeTab === 'localities') {
        const aArea = areas.find(ar => ar.id === a.areaId);
        const bArea = areas.find(ar => ar.id === b.areaId);
        aValue = aArea?.name || a.areaName || '';
        bValue = bArea?.name || b.areaName || '';
      } else if (sortField === 'routeName' && activeTab === 'localities') {
        const aRoute = routes.find(r => r.routeId === a.routeId);
        const bRoute = routes.find(r => r.routeId === b.routeId);
        aValue = aRoute?.name || 'No route assigned';
        bValue = bRoute?.name || 'No route assigned';
      } else if (sortField === 'cityName' && activeTab === 'outlets') {
        const aHierarchy = buildOutletHierarchy(a, localities, areas, warehouses, cities, routes);
        const bHierarchy = buildOutletHierarchy(b, localities, areas, warehouses, cities, routes);
        aValue = aHierarchy.cityName;
        bValue = bHierarchy.cityName;
      } else if (sortField === 'warehouseName' && activeTab === 'outlets') {
        const aHierarchy = buildOutletHierarchy(a, localities, areas, warehouses, cities, routes);
        const bHierarchy = buildOutletHierarchy(b, localities, areas, warehouses, cities, routes);
        aValue = aHierarchy.warehouseName;
        bValue = bHierarchy.warehouseName;
      } else if (sortField === 'localityName' && activeTab === 'outlets') {
        const aHierarchy = buildOutletHierarchy(a, localities, areas, warehouses, cities, routes);
        const bHierarchy = buildOutletHierarchy(b, localities, areas, warehouses, cities, routes);
        aValue = aHierarchy.locality?.name || 'Unknown';
        bValue = bHierarchy.locality?.name || 'Unknown';
      } else if (sortField === 'classificationName' && activeTab === 'outlets') {
        const aClassification = classifications.find(cl => cl.id === a.classificationId);
        const bClassification = classifications.find(cl => cl.id === b.classificationId);
        aValue = aClassification?.name || 'No Classification';
        bValue = bClassification?.name || 'No Classification';
      } else if (sortField === 'mobile' && activeTab === 'outlets') {
        aValue = a.mobile || '';
        bValue = b.mobile || '';
      } else if (sortField === 'pincode' && activeTab === 'outlets') {
        const aHierarchy = buildOutletHierarchy(a, localities, areas, warehouses, cities, routes);
        const bHierarchy = buildOutletHierarchy(b, localities, areas, warehouses, cities, routes);
        aValue = aHierarchy.locality?.pincode || '';
        bValue = bHierarchy.locality?.pincode || '';
      } else if (sortField === 'outletSequence' && activeTab === 'outlets') {
        aValue = a.outletSequence || 999; // Put non-sequenced items at the end
        bValue = b.outletSequence || 999;
      } else if (sortField === 'hasExoticFruits' && activeTab === 'outlets') {
        // Sort by boolean value - true first when ascending
        const aHasExotic = (a as any).hasExoticFruits === true;
        const bHasExotic = (b as any).hasExoticFruits === true;
        if (sortDirection === 'asc') {
          if (aHasExotic && !bHasExotic) return -1;
          if (!aHasExotic && bHasExotic) return 1;
          return 0;
        } else {
          if (aHasExotic && !bHasExotic) return 1;
          if (!aHasExotic && bHasExotic) return -1;
          return 0;
        }
      } else if (sortField === 'status' && activeTab === 'outlets') {
        aValue = a.status || 'inactive';
        bValue = b.status || 'inactive';
      } else if (sortField === 'id' && activeTab === 'outlets') {
        // Handle outlet ID sorting - handle both numeric and alphanumeric IDs
        const aId = (a as any).id || '';
        const bId = (b as any).id || '';
        
        // If both are numeric, sort numerically
        const aNum = parseInt(aId.toString().replace(/\D/g, ''));
        const bNum = parseInt(bId.toString().replace(/\D/g, ''));
        
        if (!isNaN(aNum) && !isNaN(bNum)) {
          if (sortDirection === 'asc') {
            return aNum - bNum;
          } else {
            return bNum - aNum;
          }
        } else {
          // Fall back to string comparison for alphanumeric IDs
          aValue = aId.toString().toLowerCase();
          bValue = bId.toString().toLowerCase();
        }
      } else if (sortField === 'createdDate' && activeTab === 'outlets') {
        // Handle date sorting (use addedDate only)
        const aDate = (a as any).addedDate;
        const bDate = (b as any).addedDate;

        if (aDate && bDate) {
          const aTime = aDate.toDate ? aDate.toDate().getTime() : new Date(aDate).getTime();
          const bTime = bDate.toDate ? bDate.toDate().getTime() : new Date(bDate).getTime();
          
          if (sortDirection === 'asc') {
            return aTime - bTime;
          } else {
            return bTime - aTime;
          }
        } else if (aDate) {
          return sortDirection === 'asc' ? -1 : 1;
        } else if (bDate) {
          return sortDirection === 'asc' ? 1 : -1;
        } else {
          return 0;
        }
      }

      // Convert to strings for comparison (skip for dates which are handled above)
      if (sortField !== 'createdDate' || activeTab !== 'outlets') {
        aValue = aValue?.toString().toLowerCase() || '';
        bValue = bValue?.toString().toLowerCase() || '';

        if (sortDirection === 'asc') {
          return aValue.localeCompare(bValue);
        } else {
          return bValue.localeCompare(aValue);
        }
      }
      
      return 0;
    });

    return filteredData;
  }

  // Get paginated data from the full filtered and sorted dataset
  const getPaginatedData = (fullData: any[]) => {
    const totalItems = fullData.length;
    const totalPages = Math.ceil(totalItems / itemsPerPage);
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    const paginatedData = fullData.slice(startIndex, endIndex);
    
    return {
      data: paginatedData,
      totalItems,
      totalPages,
      currentPage,
      startIndex,
      endIndex: Math.min(endIndex, totalItems)
    };
  };

  const handleSort = (field: string) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const clearFilters = () => {
    setSearchTerm('');
    setStatusFilter('all');
    setLocationFilter('');
    setSortField('name');
    setSortDirection('asc');
    
    // Clear outlet-specific filters
    setCityFilter('');
    setWarehouseFilter('');
    setAreaFilter('');
    setLocalityFilter('');
    setRouteFilter('');
                          setPincodeFilter([]);
    setExoticFruitsFilter('all');
    setStatusOutletFilter('all');
    setCreatedDateFromFilter('');
    setCreatedDateToFilter('');
  };

  // Reset filters when switching tabs
  useEffect(() => {
    clearFilters();
    // Set default sort for outlets to ID descending
    if (activeTab === 'outlets') {
      setSortField('id');
      setSortDirection('desc');
    }
    // Reset pagination when switching tabs
    setCurrentPage(1);
  }, [activeTab]);

  // Reset to page 1 when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, statusFilter, locationFilter, sortField, sortDirection, 
      cityFilter, warehouseFilter, areaFilter, localityFilter, routeFilter, 
      pincodeFilter, exoticFruitsFilter, statusOutletFilter, 
      createdDateFromFilter, createdDateToFilter]);

  // Calculate filtered outlets for map with proper memoization
  const filteredOutletsForMap = useMemo(() => {
    return filterAndSortData(outlets);
  }, [outlets, cityFilter, warehouseFilter, areaFilter, localityFilter, routeFilter, 
      pincodeFilter, exoticFruitsFilter, statusOutletFilter, createdDateFromFilter, 
      createdDateToFilter, sortField, sortDirection]);

  // Localities migration tools removed after completion

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold text-gray-800 mb-2">Distribution Network</h2>
        <p className="text-gray-600">Manage cities, areas, routes and outlets</p>
      </div>

      {/* Tabs */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200">
        <div className="border-b border-gray-200">
          <nav className="flex space-x-8 px-6" aria-label="Tabs">
            {[
              { key: 'cities', label: 'Cities', icon: Building2 },
              { key: 'warehouses', label: 'Warehouses', icon: Building2 },
              { key: 'areas', label: 'Areas', icon: MapPin },
              { key: 'routes', label: 'Routes', icon: Truck },
              { key: 'localities', label: 'Localities', icon: MapPin },
              { key: 'classifications', label: 'Classifications', icon: Database },
              { key: 'outlets', label: 'Outlets', icon: MapPin }
            ].map((tab) => {
              const Icon = tab.icon;
              return (
                <button
                  key={tab.key}
                  onClick={() => {
                    setActiveTab(tab.key as any);
                    // Reset subviews when switching top-level tabs
                    setShowAreaRouting(false);
                    setShowLocalityRouting(false);
                  }}
                  className={`flex items-center space-x-2 py-4 px-1 border-b-2 font-medium text-sm ${
                    activeTab === tab.key
                      ? 'border-blue-500 text-blue-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  <Icon className="w-4 h-4" />
                  <span>{tab.label}</span>
                </button>
              );
            })}
          </nav>
        </div>
        
  <div className="p-6">
          {networkLoading ? (
            <div className="text-center py-8">
              <div className="w-8 h-8 border-2 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto mb-2"></div>
              <p className="text-gray-600">Loading data...</p>
            </div>
          ) : (
            <>
              {/* Action Bar */}
              <div className="flex justify-between items-center mb-6">
                <div className="flex items-center space-x-4">
                  <h3 className="text-lg font-semibold text-gray-800">
                    {activeTab === 'cities' && 'Cities'}
                    {activeTab === 'warehouses' && 'Warehouses'}
                    {activeTab === 'areas' && (showAreaRouting ? 'Area Routing' : 'Areas')}
                    {activeTab === 'routes' && 'Routes'}
                    {activeTab === 'localities' && (showLocalityRouting ? 'Locality Routing' : 'Localities')}
                    {activeTab === 'classifications' && 'Classifications'}
                    {activeTab === 'outlets' && 'Outlets'}
                  </h3>
                  <span className="bg-blue-100 text-blue-800 text-xs font-medium px-2.5 py-0.5 rounded-full">
                    {activeTab === 'cities' && cities.length}
                    {activeTab === 'warehouses' && warehouses.length}
                    {activeTab === 'areas' && areas.length}
                    {activeTab === 'routes' && routes.length}
                    {activeTab === 'localities' && localities.length}
                    {activeTab === 'classifications' && classifications.length}
                    {activeTab === 'outlets' && outlets.length}
                    {' items'}
                  </span>
                </div>
                
                <div className="flex items-center space-x-3">
                  {/* In-view routing triggers */}
                  {activeTab === 'areas' && (
                    <button
                      onClick={() => setShowAreaRouting(!showAreaRouting)}
                      className={`flex items-center space-x-2 px-4 py-2 rounded-lg transition-colors border ${showAreaRouting ? 'bg-blue-50 text-blue-700 border-blue-300' : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'}`}
                      title="Open Area Routing"
                    >
                      <RouteIcon className="w-4 h-4" />
                      <span>{showAreaRouting ? 'Back to Areas' : 'Area Routing'}</span>
                    </button>
                  )}
                  {activeTab === 'localities' && (
                    <button
                      onClick={() => setShowLocalityRouting(!showLocalityRouting)}
                      className={`flex items-center space-x-2 px-4 py-2 rounded-lg transition-colors border ${showLocalityRouting ? 'bg-blue-50 text-blue-700 border-blue-300' : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'}`}
                      title="Open Locality Routing"
                    >
                      <RouteIcon className="w-4 h-4" />
                      <span>{showLocalityRouting ? 'Back to Localities' : 'Locality Routing'}</span>
                    </button>
                  )}
                  {/* Localities migration tools removed */}
                  {activeTab === 'outlets' && (
                    <div className="flex bg-gray-100 rounded-lg p-1">
                      <button
                        onClick={() => setOutletViewTab('list')}
                        className={`flex items-center space-x-2 px-3 py-1 rounded-md text-sm font-medium transition-colors ${
                          outletViewTab === 'list'
                            ? 'bg-white text-gray-900 shadow-sm'
                            : 'text-gray-600 hover:text-gray-900'
                        }`}
                      >
                        <List className="w-4 h-4" />
                        <span>List</span>
                      </button>
                      <button
                        onClick={() => setOutletViewTab('map')}
                        className={`flex items-center space-x-2 px-3 py-1 rounded-md text-sm font-medium transition-colors ${
                          outletViewTab === 'map'
                            ? 'bg-white text-gray-900 shadow-sm'
                            : 'text-gray-600 hover:text-gray-900'
                        }`}
                      >
                        <MapIcon className="w-4 h-4" />
                        <span>Map</span>
                      </button>
                    </div>
                  )}

                  {/* Boundary Controls removed for Outlets map view */}

                  {/* Add Button - hide during routing subviews and on outlets */}
                  {activeTab !== 'outlets' && !showLocalityRouting && !showAreaRouting && (
                    <button
                      onClick={() => {
                        setEditingItem(null);
                        if (activeTab === 'routes') {
                          setFormData({ routeId: computeNextRouteId(), isActive: true });
                        } else {
                          setFormData({});
                        }
                        setShowAddModal(true);
                      }}
                      className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                    >
                      <Plus className="w-4 h-4" />
                      <span>Add {
                        activeTab === 'cities' ? 'City' :
                        activeTab === 'warehouses' ? 'Warehouse' :
                        activeTab === 'areas' ? 'Area' :
                        activeTab === 'routes' ? 'Route' :
                        activeTab === 'localities' ? 'Locality' :
                        activeTab === 'classifications' ? 'Classification' :
                        'Item'
                      }</span>
                    </button>
                  )}

                  {/* Add Outlet button removed from Outlets */}
                  
                  <button
                    onClick={handleDownload}
                    className="flex items-center space-x-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
                  >
                    <Download className="w-4 h-4" />
                    <span>Download</span>
                  </button>
                  
                  {/* Outlets maintenance buttons removed (Migrate Image Fields, Drop Redundant Fields) */}

                  {activeTab === 'localities' && (
                    <button
                      onClick={() => setShowDropLocalitiesModal(true)}
                      className="flex items-center space-x-2 px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 transition-colors"
                    >
                      <Trash2 className="w-4 h-4" />
                      <span>Drop Locality Fields</span>
                    </button>
                  )}
                  {activeTab === 'localities' && (
                    <button
                      onClick={() => setShowRebuildModal(true)}
                      disabled={rebuildRunning}
                      className={`flex items-center space-x-2 px-4 py-2 rounded-lg transition-colors ${rebuildRunning ? 'bg-gray-300 text-gray-600 cursor-not-allowed' : 'bg-purple-600 text-white hover:bg-purple-700'}`}
                      title="Rebuild the Locality collection from outlet addresses"
                    >
                      <Sparkles className="w-4 h-4" />
                      <span>{rebuildRunning ? 'Rebuilding…' : 'Rebuild Localities'}</span>
                    </button>
                  )}
                </div>
              </div>



              {/* Content based on active tab */}
              {activeTab === 'outlets' && (
                <>
                  {/* Filter Section - Show for both List and Map views */}
                  <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 mb-4">
                    <div className="grid grid-cols-4 gap-4">
                      {/* City Filter */}
                      <div className="flex flex-col">
                        <label className="text-xs font-medium text-gray-500 uppercase tracking-wider mb-1">
                          City
                        </label>
                        <select
                          value={cityFilter}
                          onChange={(e) => setCityFilter(e.target.value)}
                          className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
                        >
                          <option value="">All Cities</option>
                          {Array.from(new Set(outlets.map(outlet => {
                            const hierarchy = buildOutletHierarchy(outlet, localities, areas, warehouses, cities, routes);
                            return hierarchy.city?.name;
                          }).filter(Boolean))).sort().map(cityName => (
                            <option key={cityName} value={cityName}>{cityName}</option>
                          ))}
                        </select>
                      </div>

                      {/* Warehouse Filter */}
                      <div className="flex flex-col">
                        <label className="text-xs font-medium text-gray-500 uppercase tracking-wider mb-1">
                          Warehouse
                        </label>
                        <select
                          value={warehouseFilter}
                          onChange={(e) => setWarehouseFilter(e.target.value)}
                          className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
                        >
                          <option value="">All Warehouses</option>
                          {Array.from(new Set(outlets.map(outlet => {
                            const hierarchy = buildOutletHierarchy(outlet, localities, areas, warehouses, cities, routes);
                            return hierarchy.warehouse?.name;
                          }).filter(Boolean))).sort().map(warehouseName => (
                            <option key={warehouseName} value={warehouseName}>{warehouseName}</option>
                          ))}
                        </select>
                      </div>

                      {/* Area Filter */}
                      <div className="flex flex-col">
                        <label className="text-xs font-medium text-gray-500 uppercase tracking-wider mb-1">
                          Area
                        </label>
                        <select
                          value={areaFilter}
                          onChange={(e) => setAreaFilter(e.target.value)}
                          className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
                        >
                          <option value="">All Areas</option>
                          {Array.from(new Set(outlets.map(outlet => {
                            const hierarchy = buildOutletHierarchy(outlet, localities, areas, warehouses, cities, routes);
                            return hierarchy.area?.name;
                          }).filter(Boolean))).sort().map(areaName => (
                            <option key={areaName} value={areaName}>{areaName}</option>
                          ))}
                        </select>
                      </div>

                      {/* Locality Filter */}
                      <div className="flex flex-col">
                        <label className="text-xs font-medium text-gray-500 uppercase tracking-wider mb-1">
                          Locality
                        </label>
                        <select
                          value={localityFilter}
                          onChange={(e) => setLocalityFilter(e.target.value)}
                          className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
                        >
                          <option value="">All Localities</option>
                          {Array.from(new Set(outlets.map(outlet => {
                            const hierarchy = buildOutletHierarchy(outlet, localities, areas, warehouses, cities, routes);
                            return hierarchy.locality?.name;
                          }).filter(Boolean))).sort().map(localityName => (
                            <option key={localityName} value={localityName}>{localityName}</option>
                          ))}
                        </select>
                      </div>

                      {/* Route Filter */}
                      <div className="flex flex-col">
                        <label className="text-xs font-medium text-gray-500 uppercase tracking-wider mb-1">
                          Route
                        </label>
                        <select
                          value={routeFilter}
                          onChange={(e) => setRouteFilter(e.target.value)}
                          className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
                        >
                          <option value="">All Routes</option>
                          {Array.from(new Set(outlets.map(outlet => {
                            const hierarchy = buildOutletHierarchy(outlet, localities, areas, warehouses, cities, routes);
                            return hierarchy.route?.name;
                          }).filter(Boolean))).sort().map(routeName => (
                            <option key={routeName} value={routeName}>{routeName}</option>
                          ))}
                        </select>
                      </div>

                      {/* Date Range Filter */}
                      <div className="flex flex-col">
                        <label className="text-xs font-medium text-gray-500 uppercase tracking-wider mb-1">
                          Created Date
                        </label>
                        <div className="relative">
                          <button
                            onClick={() => setShowDateRangePicker(!showDateRangePicker)}
                            className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500 text-left"
                          >
                            {createdDateFromFilter || createdDateToFilter 
                              ? `${createdDateFromFilter || 'Start'} - ${createdDateToFilter || 'End'}`
                              : 'All Dates'
                            }
                          </button>
                          
                          {showDateRangePicker && (
                            <div className="absolute top-full left-0 mt-1 bg-white border border-gray-300 rounded-lg shadow-lg p-4 z-50 min-w-[300px] date-range-picker">
                              <div className="flex flex-col space-y-3">
                                <div className="flex space-x-2">
                                  <div className="flex-1">
                                    <label className="block text-xs font-medium text-gray-700 mb-1">Start Date</label>
                                    <input
                                      type="date"
                                      value={createdDateFromFilter}
                                      onChange={(e) => setCreatedDateFromFilter(e.target.value)}
                                      className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
                                    />
                                  </div>
                                  <div className="flex-1">
                                    <label className="block text-xs font-medium text-gray-700 mb-1">End Date</label>
                                    <input
                                      type="date"
                                      value={createdDateToFilter}
                                      onChange={(e) => setCreatedDateToFilter(e.target.value)}
                                      className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
                                    />
                                  </div>
                                </div>
                                
                                <div className="flex flex-wrap gap-1">
                                  <button
                                    onClick={() => {
                                      const today = new Date();
                                      const todayStr = today.toISOString().split('T')[0];
                                      setCreatedDateFromFilter(todayStr);
                                      setCreatedDateToFilter(todayStr);
                                    }}
                                    className="px-2 py-1 text-xs bg-gray-100 hover:bg-gray-200 rounded"
                                  >
                                    Today
                                  </button>
                                  <button
                                    onClick={() => {
                                      const today = new Date();
                                      const yesterday = new Date(today);
                                      yesterday.setDate(yesterday.getDate() - 1);
                                      const yesterdayStr = yesterday.toISOString().split('T')[0];
                                      setCreatedDateFromFilter(yesterdayStr);
                                      setCreatedDateToFilter(yesterdayStr);
                                    }}
                                    className="px-2 py-1 text-xs bg-gray-100 hover:bg-gray-200 rounded"
                                  >
                                    Yesterday
                                  </button>
                                  <button
                                    onClick={() => {
                                      const today = new Date();
                                      const weekAgo = new Date(today);
                                      weekAgo.setDate(weekAgo.getDate() - 7);
                                      setCreatedDateFromFilter(weekAgo.toISOString().split('T')[0]);
                                      setCreatedDateToFilter(today.toISOString().split('T')[0]);
                                    }}
                                    className="px-2 py-1 text-xs bg-gray-100 hover:bg-gray-200 rounded"
                                  >
                                    Last 7 Days
                                  </button>
                                  <button
                                    onClick={() => {
                                      const today = new Date();
                                      const monthAgo = new Date(today);
                                      monthAgo.setDate(monthAgo.getDate() - 30);
                                      setCreatedDateFromFilter(monthAgo.toISOString().split('T')[0]);
                                      setCreatedDateToFilter(today.toISOString().split('T')[0]);
                                    }}
                                    className="px-2 py-1 text-xs bg-gray-100 hover:bg-gray-200 rounded"
                                  >
                                    Last 30 Days
                                  </button>
                                  <button
                                    onClick={() => {
                                      setCreatedDateFromFilter('');
                                      setCreatedDateToFilter('');
                                    }}
                                    className="px-2 py-1 text-xs bg-red-100 hover:bg-red-200 text-red-700 rounded"
                                  >
                                    Clear
                                  </button>
                                </div>
                              </div>
                            </div>
                          )}
                        </div>
                      </div>

                      {/* Pincode Filter */}
                      <div className="flex flex-col">
                        <label className="text-xs font-medium text-gray-500 uppercase tracking-wider mb-1">
                          Pincode
                        </label>
                        <div className="relative">
                          <button
                            type="button"
                            onClick={() => setShowPincodeDropdown(!showPincodeDropdown)}
                            className="w-full px-3 py-2 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500 bg-white text-left flex items-center justify-between"
                          >
                            <span className="truncate">
                              {pincodeFilter.length === 0 
                                ? 'Select Pincodes' 
                                : `${pincodeFilter.length} selected`
                              }
                            </span>
                            <svg className={`w-4 h-4 transition-transform ${showPincodeDropdown ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                            </svg>
                          </button>
                          
                          {showPincodeDropdown && (
                            <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-gray-300 rounded-lg shadow-lg z-10 max-h-48 overflow-y-auto">
                              <div className="p-2">
                                <div className="space-y-1">
                                  {pincodes.filter(p => p.isActive).sort((a, b) => a.pincode.localeCompare(b.pincode)).map(pincode => (
                                    <label key={pincode.id} className="flex items-center space-x-2 cursor-pointer hover:bg-gray-50 p-1 rounded">
                                      <input
                                        type="checkbox"
                                        checked={pincodeFilter.includes(pincode.pincode)}
                                        onChange={(e) => {
                                          if (e.target.checked) {
                                            setPincodeFilter([...pincodeFilter, pincode.pincode]);
                                          } else {
                                            setPincodeFilter(pincodeFilter.filter(p => p !== pincode.pincode));
                                          }
                                        }}
                                        className="w-3 h-3 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                                      />
                                      <span className="text-sm">{pincode.pincode}</span>
                                    </label>
                                  ))}
                                </div>
                                {pincodes.filter(p => p.isActive).length === 0 && (
                                  <div className="text-sm text-gray-500 p-2">No pincodes available</div>
                                )}
                              </div>
                            </div>
                          )}
                        </div>
                      </div>

                      {/* Exotic Fruits filter removed */}

                      {/* Status Filter */}
                      <div className="flex flex-col">
                        <label className="text-xs font-medium text-gray-500 uppercase tracking-wider mb-1">
                          Status
                        </label>
                        <select
                          value={statusOutletFilter}
                          onChange={(e) => setStatusOutletFilter(e.target.value as 'all' | 'active' | 'inactive' | 'pending_approval' | 'draft')}
                          className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
                        >
                          <option value="all">All</option>
                          <option value="active">Active</option>
                          <option value="inactive">Inactive</option>
                          <option value="pending_approval">Pending</option>
                          <option value="draft">Draft</option>
                        </select>
                      </div>
                    </div>

                    {/* Clear Filters Button */}
                    <div className="mt-3 flex justify-end">
                      <button
                        onClick={() => {
                          setCityFilter('');
                          setWarehouseFilter('');
                          setAreaFilter('');
                          setLocalityFilter('');
                          setRouteFilter('');
                          setCreatedDateFromFilter('');
                          setCreatedDateToFilter('');
                          setPincodeFilter([]);
                          setExoticFruitsFilter('all');
                          setStatusOutletFilter('all');
                        }}
                        className="px-3 py-1 text-sm text-blue-600 hover:text-blue-800 font-medium"
                      >
                        Clear Filters
                      </button>
                    </div>
                  </div>
                </>
              )}

              {/* Rebuild Localities Modal */}
              {showRebuildModal && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40">
                  <div className="bg-white rounded-lg shadow-lg max-w-2xl w-full p-6">
                    <div className="flex items-center justify-between mb-4">
                      <h3 className="text-lg font-semibold text-gray-800">Rebuild Localities from Outlets</h3>
                      <button onClick={() => !rebuildRunning && setShowRebuildModal(false)} className="p-1 rounded hover:bg-gray-100" aria-label="Close">
                        <X className="w-5 h-5" />
                      </button>
                    </div>
                    <div className="space-y-3 text-sm text-gray-700">
                      <p>Steps:</p>
                      <ul className="list-disc pl-5 space-y-1">
                        <li>Backup current outlets and localities into timestamped backup collections.</li>
                        <li>Iterate all outlets, parse coordinates via Google to extract streetName and subLocality.</li>
                        <li>Log raw and parsed results into address_samples for audit.</li>
                        <li>Create area/locality if missing and update outlet with localityId, streetName, subLocality.</li>
                      </ul>
                      <div className="flex items-center gap-2 mt-2">
                        <input
                          id="dryRunToggle"
                          type="checkbox"
                          className="toggle toggle-primary"
                          checked={rebuildDryRun}
                          disabled={rebuildRunning}
                          onChange={(e) => setRebuildDryRun(e.target.checked)}
                        />
                        <label htmlFor="dryRunToggle" className="cursor-pointer select-none">Dry run (no writes)</label>
                      </div>
                      <p className="text-xs text-gray-500">Calls to Google Maps will be throttled to avoid rate limits. In dry run, no database writes occur; only address_samples are logged.</p>
                    </div>
                    <div className="mt-4 p-3 rounded bg-gray-50 border text-sm">
                      <div className="flex items-center justify-between">
                        <div className="space-y-1">
                          <div>Outlets reprocessed: <span className="font-semibold">{rebuildProgress.completed}</span> / {rebuildProgress.total}</div>
                          <div>Locality renamed: <span className="font-semibold text-purple-700">{rebuildProgress.renamed}</span> | Retained: <span className="font-semibold text-green-700">{rebuildProgress.retained}</span> | Failed: <span className="font-semibold text-red-600">{rebuildProgress.failed}</span></div>
                          <div>Pending: {rebuildProgress.pending}</div>
                          {rebuildProgress.lastMessage && <div className="text-gray-600">{rebuildProgress.lastMessage}</div>}
                        </div>
                        {rebuildRunning && <div className="w-8 h-8 border-2 border-purple-600 border-t-transparent rounded-full animate-spin" />}
                      </div>
                    </div>
                    <div className="mt-6 flex justify-end gap-3">
                      <button
                        onClick={() => setShowRebuildModal(false)}
                        disabled={rebuildRunning}
                        className={`px-4 py-2 rounded border ${rebuildRunning ? 'bg-gray-100 text-gray-400' : 'bg-white text-gray-700 hover:bg-gray-50'}`}
                      >
                        Cancel
                      </button>
                      <button
                        onClick={async () => await handleRebuildLocalities()}
                        disabled={rebuildRunning}
                        className={`px-4 py-2 rounded ${rebuildRunning ? 'bg-gray-300 text-gray-600 cursor-not-allowed' : 'bg-purple-600 text-white hover:bg-purple-700'}`}
                      >
                        {rebuildRunning ? 'Running…' : 'Start Rebuild'}
                      </button>
                    </div>
                  </div>
                </div>
              )}

              {activeTab === 'outlets' && outletViewTab === 'map' ? (
                <div className="bg-white rounded-lg shadow-sm border border-gray-200 relative">
                  <div className="h-96">
                    <MapContainer
                      key={[
                        activeTab,
                        outletViewTab,
                        cityFilter,
                        warehouseFilter,
                        areaFilter,
                        localityFilter,
                        routeFilter,
                        pincodeFilter,
                        exoticFruitsFilter,
                        statusOutletFilter,
                        createdDateFromFilter,
                        createdDateToFilter,
                        sortField,
                        sortDirection
                      ].join('-')}
                      outlets={filteredOutletsForMap}
                      localities={localities}
                      areas={areas}
                      warehouses={warehouses}
                      cities={cities}
                      routes={routes}
                      classifications={classifications}
                      pincodes={pincodes}
                      selectedPincodeFilter={pincodeFilter}
                      showAreaBoundaries={showAreaBoundaries}
                      showLocalityBoundaries={showLocalityBoundaries}
                      polygonBoundaries={polygonBoundaries}
                      showOptimizedRoute={showOptimizedRoute}
                      selectedRouteFilter={routeFilter}
                      onEditOutlet={(outlet) => {
                        setEditingOutlet(outlet);
                        setShowAddOutletModal(true);
                      }}
                    />
                  </div>
                  
                  {/* Floating Route Optimization Button */}
                  {routeFilter && (
                    <div className="absolute top-4 right-4 z-10">
                      <div className="flex flex-col items-end space-y-2">
                        <button
                          onClick={() => handleOptimizeRoute()}
                          disabled={isOptimizingRoute}
                          className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-lg shadow-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200"
                        >
                          <RouteIcon className="w-5 h-5" />
                          <span className="text-sm font-medium">
                            {isOptimizingRoute ? 'Optimizing...' : 'Optimize Route'}
                          </span>
                        </button>
                        
                        {/* Show optimized outlets count */}
                        {(() => {
                          const routeOutlets = filterAndSortData(outlets).filter(outlet => {
                            const hierarchy = buildOutletHierarchy(outlet, localities, areas, warehouses, cities, routes);
                            return hierarchy.route?.name === routeFilter;
                          });
                          // Only count ACTIVE outlets with sequences for the route toggle
                          const optimizedActiveOutlets = routeOutlets.filter(outlet => 
                            outlet.outletSequence && outlet.status === 'active'
                          );
                          
                          return optimizedActiveOutlets.length > 0 && (
                            <button
                              onClick={() => setShowOptimizedRoute(!showOptimizedRoute)}
                              className={`flex items-center space-x-2 px-3 py-1 text-xs rounded-full transition-all duration-200 ${
                                showOptimizedRoute 
                                  ? 'bg-green-100 text-green-800 hover:bg-green-200' 
                                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                              }`}
                            >
                              <span>{showOptimizedRoute ? '👁️ Hide Route' : '🗺️ Show Route'}</span>
                              <span className="font-medium">({optimizedActiveOutlets.length} stops)</span>
                            </button>
                          );
                        })()}
                        
                        {optimizationMessage && (
                          <div className="px-3 py-2 text-xs bg-gray-800 text-white rounded-lg shadow-lg max-w-xs">
                            {optimizationMessage}
                          </div>
                        )}
                        
                        {routeFilter && (
                          <div className="px-3 py-1 text-xs bg-blue-100 text-blue-800 rounded-full">
                            Route: {routeFilter}
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
                  <div className="overflow-x-auto" style={{ minWidth: '100%' }}>
                    {/* In-view Area Routing inside Areas tab */}
                    {activeTab === 'areas' && showAreaRouting ? (
                      <div className="p-0">
                        <AreaRoutingSection
                          areas={areas}
                          localities={localities}
                          outlets={outlets}
                          routes={routes}
                          warehouses={warehouses}
                          cities={cities}
                          onRefresh={loadNetworkData}
                        />
                      </div>
                    ) : (
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-gray-50">
                        <tr>
                          {activeTab === 'cities' && (
                            <>
                              <th onClick={() => handleSort('name')} className="cursor-pointer px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider hover:bg-gray-100">
                                Name {sortField === 'name' && (sortDirection === 'asc' ? '↑' : '↓')}
                              </th>
                              <th onClick={() => handleSort('state')} className="cursor-pointer px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider hover:bg-gray-100">
                                State {sortField === 'state' && (sortDirection === 'asc' ? '↑' : '↓')}
                              </th>
                              <th onClick={() => handleSort('isActive')} className="cursor-pointer px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider hover:bg-gray-100">
                                Status {sortField === 'isActive' && (sortDirection === 'asc' ? '↑' : '↓')}
                              </th>
                              <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                            </>
                          )}
                          {activeTab === 'warehouses' && (
                            <>
                              <th onClick={() => handleSort('name')} className="cursor-pointer px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider hover:bg-gray-100">
                                Name {sortField === 'name' && (sortDirection === 'asc' ? '↑' : '↓')}
                              </th>
                              <th onClick={() => handleSort('cityName')} className="cursor-pointer px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider hover:bg-gray-100">
                                City {sortField === 'cityName' && (sortDirection === 'asc' ? '↑' : '↓')}
                              </th>
                              <th onClick={() => handleSort('isActive')} className="cursor-pointer px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider hover:bg-gray-100">
                                Status {sortField === 'isActive' && (sortDirection === 'asc' ? '↑' : '↓')}
                              </th>
                              <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                            </>
                          )}
                          {activeTab === 'areas' && (
                            <>
                              <th onClick={() => handleSort('name')} className="cursor-pointer px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider hover:bg-gray-100">
                                Name {sortField === 'name' && (sortDirection === 'asc' ? '↑' : '↓')}
                              </th>
                              <th onClick={() => handleSort('warehouseName')} className="cursor-pointer px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider hover:bg-gray-100">
                                Warehouse {sortField === 'warehouseName' && (sortDirection === 'asc' ? '↑' : '↓')}
                              </th>
                              <th onClick={() => handleSort('isActive')} className="cursor-pointer px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider hover:bg-gray-100">
                                Status {sortField === 'isActive' && (sortDirection === 'asc' ? '↑' : '↓')}
                              </th>
                              <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                            </>
                          )}
                          {activeTab === 'routes' && (
                            <>
                              <th onClick={() => handleSort('name')} className="cursor-pointer px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider hover:bg-gray-100">
                                Name {sortField === 'name' && (sortDirection === 'asc' ? '↑' : '↓')}
                              </th>
                              <th onClick={() => handleSort('cityName')} className="cursor-pointer px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider hover:bg-gray-100">
                                City {sortField === 'cityName' && (sortDirection === 'asc' ? '↑' : '↓')}
                              </th>
                              <th onClick={() => handleSort('warehouseName')} className="cursor-pointer px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider hover:bg-gray-100">
                                Warehouse {sortField === 'warehouseName' && (sortDirection === 'asc' ? '↑' : '↓')}
                              </th>
                              <th onClick={() => handleSort('outletCount')} className="cursor-pointer px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider hover:bg-gray-100">
                                Outlets {sortField === 'outletCount' && (sortDirection === 'asc' ? '↑' : '↓')}
                              </th>
                              <th onClick={() => handleSort('isActive')} className="cursor-pointer px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider hover:bg-gray-100">
                                Status {sortField === 'isActive' && (sortDirection === 'asc' ? '↑' : '↓')}
                              </th>
                              <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                            </>
                          )}
                          {activeTab === 'localities' && !showLocalityRouting && (
                            <>
                              <th onClick={() => handleSort('name')} className="cursor-pointer px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider hover:bg-gray-100">
                                Name {sortField === 'name' && (sortDirection === 'asc' ? '↑' : '↓')}
                              </th>
                              <th onClick={() => handleSort('areaName')} className="cursor-pointer px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider hover:bg-gray-100">
                                Area {sortField === 'areaName' && (sortDirection === 'asc' ? '↑' : '↓')}
                              </th>
                              <th onClick={() => handleSort('pincode')} className="cursor-pointer px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider hover:bg-gray-100">
                                Pincode {sortField === 'pincode' && (sortDirection === 'asc' ? '↑' : '↓')}
                              </th>
                              <th onClick={() => handleSort('isActive')} className="cursor-pointer px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider hover:bg-gray-100">
                                Status {sortField === 'isActive' && (sortDirection === 'asc' ? '↑' : '↓')}
                              </th>
                              <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                            </>
                          )}
                          {activeTab === 'localities' && showLocalityRouting && (
                            <>
                              <th onClick={() => handleSort('name')} className="cursor-pointer px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider hover:bg-gray-100">
                                Locality {sortField === 'name' && (sortDirection === 'asc' ? '↑' : '↓')}
                              </th>
                              <th onClick={() => handleSort('areaName')} className="cursor-pointer px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider hover:bg-gray-100">
                                Area {sortField === 'areaName' && (sortDirection === 'asc' ? '↑' : '↓')}
                              </th>
                              <th onClick={() => handleSort('routeName')} className="cursor-pointer px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider hover:bg-gray-100">
                                Current Route {sortField === 'routeName' && (sortDirection === 'asc' ? '↑' : '↓')}
                              </th>
                              <th onClick={() => handleSort('outletCount')} className="cursor-pointer px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider hover:bg-gray-100">
                                Outlets {sortField === 'outletCount' && (sortDirection === 'asc' ? '↑' : '↓')}
                              </th>
                              <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Route Assignment</th>
                            </>
                          )}
                          {activeTab === 'classifications' && (
                            <>
                              <th onClick={() => handleSort('name')} className="cursor-pointer px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider hover:bg-gray-100">
                                Name {sortField === 'name' && (sortDirection === 'asc' ? '↑' : '↓')}
                              </th>
                              <th onClick={() => handleSort('description')} className="cursor-pointer px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider hover:bg-gray-100">
                                Description {sortField === 'description' && (sortDirection === 'asc' ? '↑' : '↓')}
                              </th>
                              <th onClick={() => handleSort('isActive')} className="cursor-pointer px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider hover:bg-gray-100">
                                Status {sortField === 'isActive' && (sortDirection === 'asc' ? '↑' : '↓')}
                              </th>
                              <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                            </>
                          )}
                          {activeTab === 'outlets' && (
                            <>
                              <th onClick={() => handleSort('id')} className="cursor-pointer px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider hover:bg-gray-100">
                                Outlet ID {sortField === 'id' && (sortDirection === 'asc' ? '↑' : '↓')}
                              </th>
                              <th onClick={() => handleSort('name')} className="cursor-pointer px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider hover:bg-gray-100">
                                Name {sortField === 'name' && (sortDirection === 'asc' ? '↑' : '↓')}
                              </th>
                              <th onClick={() => handleSort('cityName')} className="cursor-pointer px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider hover:bg-gray-100">
                                City {sortField === 'cityName' && (sortDirection === 'asc' ? '↑' : '↓')}
                              </th>
                              <th onClick={() => handleSort('warehouseName')} className="cursor-pointer px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider hover:bg-gray-100">
                                Warehouse {sortField === 'warehouseName' && (sortDirection === 'asc' ? '↑' : '↓')}
                              </th>
                              <th onClick={() => handleSort('areaName')} className="cursor-pointer px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider hover:bg-gray-100">
                                Area {sortField === 'areaName' && (sortDirection === 'asc' ? '↑' : '↓')}
                              </th>
                              <th onClick={() => handleSort('localityName')} className="cursor-pointer px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider hover:bg-gray-100">
                                Locality {sortField === 'localityName' && (sortDirection === 'asc' ? '↑' : '↓')}
                              </th>
                              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Route
                              </th>
                              <th onClick={() => handleSort('outletSequence')} className="cursor-pointer px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider hover:bg-gray-100">
                                Sequence {sortField === 'outletSequence' && (sortDirection === 'asc' ? '↑' : '↓')}
                              </th>
                              <th onClick={() => handleSort('pincode')} className="cursor-pointer px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider hover:bg-gray-100">
                                Pincode {sortField === 'pincode' && (sortDirection === 'asc' ? '↑' : '↓')}
                              </th>
                              <th onClick={() => handleSort('hasExoticFruits')} className="cursor-pointer px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider hover:bg-gray-100">
                                Exotic Fruits {sortField === 'hasExoticFruits' && (sortDirection === 'asc' ? '↑' : '↓')}
                              </th>
                              <th onClick={() => handleSort('createdDate')} className="cursor-pointer px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider hover:bg-gray-100">
                                Created Date {sortField === 'createdDate' && (sortDirection === 'asc' ? '↑' : '↓')}
                              </th>
                              <th onClick={() => handleSort('status')} className="cursor-pointer px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider hover:bg-gray-100">
                                Status {sortField === 'status' && (sortDirection === 'asc' ? '↑' : '↓')}
                              </th>
                              <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                            </>
                          )}
                        </tr>
                          {/* Add other tab headers as needed */}
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {/* Cities content */}
                        {activeTab === 'cities' && filterAndSortData(cities).map((city) => (
                          <tr key={city.id} className="hover:bg-gray-50">
                            <td className="px-6 py-4 whitespace-nowrap">
                              <div className="text-sm font-medium text-gray-900">{city.name}</div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <div className="text-sm text-gray-600">{city.state}</div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <span className={`inline-flex px-2 text-xs font-semibold rounded-full ${
                                city.isActive 
                                  ? 'bg-green-100 text-green-800' 
                                  : 'bg-red-100 text-red-800'
                              }`}>
                                {city.isActive ? 'Active' : 'Inactive'}
                              </span>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                              <button
                                onClick={() => handleEdit(city)}
                                className="text-blue-600 hover:text-blue-900 mr-3"
                              >
                                <Edit className="w-4 h-4" />
                              </button>
                              <button
                                onClick={() => handleDelete(city.id)}
                                className="text-red-600 hover:text-red-900"
                              >
                                <Trash2 className="w-4 h-4" />
                              </button>
                            </td>
                          </tr>
                        ))}
                        
                        {/* Warehouses content */}
                        {activeTab === 'warehouses' && filterAndSortData(warehouses).map((warehouse) => {
          const city = cities.find(c => c.cityCode === warehouse.cityCode);
                          return (
                            <tr key={warehouse.id} className="hover:bg-gray-50">
                              <td className="px-6 py-4 whitespace-nowrap">
                                <div className="text-sm font-medium text-gray-900">{warehouse.name}</div>
                                <div className="text-xs text-gray-500">ID: {warehouse.warehouseId || warehouse.id}</div>
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap">
                                <div className="text-sm text-gray-600">{city?.name || warehouse.cityCode || 'Unknown'}</div>
                                <div className="text-xs text-gray-500">Code: {warehouse.cityCode}</div>
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap">
                                <span className={`inline-flex px-2 text-xs font-semibold rounded-full ${
                                  warehouse.isActive 
                                    ? 'bg-green-100 text-green-800' 
                                    : 'bg-red-100 text-red-800'
                                }`}>
                                  {warehouse.isActive ? 'Active' : 'Inactive'}
                                </span>
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                                <button
                                  onClick={() => handleEdit(warehouse)}
                                  className="text-blue-600 hover:text-blue-900 mr-3"
                                >
                                  <Edit className="w-4 h-4" />
                                </button>
                                <button
                                  onClick={() => handleDelete(warehouse.id)}
                                  className="text-red-600 hover:text-red-900"
                                >
                                  <Trash2 className="w-4 h-4" />
                                </button>
                              </td>
                            </tr>
                          );
                        })}

                        {/* Areas content */}
                        {activeTab === 'areas' && filterAndSortData(areas).map((area) => {
                          // Fix warehouse lookup - check both document ID and warehouseId field
                          const warehouse = warehouses.find(w => w.id === area.warehouseId) || 
                                          warehouses.find(w => w.warehouseId === area.warehouseId);
                          
                          return (
                            <tr key={area.id} className="hover:bg-gray-50">
                              <td className="px-6 py-4 whitespace-nowrap">
                                <div className="text-sm font-medium text-gray-900">{area.name}</div>
                                <div className="text-xs text-gray-500">Record ID: {area.id}</div>
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap">
                                <div className="text-sm text-gray-600">{warehouse?.name || 'Unknown'}</div>
                                {warehouse && (
                                  <div className="text-xs text-gray-500">
                                    {warehouse.warehouseId} - {cities.find(c => c.cityCode === warehouse.cityCode)?.name || warehouse.cityCode}
                                  </div>
                                )}
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap">
                                <span className={`inline-flex px-2 text-xs font-semibold rounded-full ${
                                  area.isActive 
                                    ? 'bg-green-100 text-green-800' 
                                    : 'bg-red-100 text-red-800'
                                }`}>
                                  {area.isActive ? 'Active' : 'Inactive'}
                                </span>
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                                <button
                                  onClick={() => handleEdit(area)}
                                  className="text-blue-600 hover:text-blue-900 mr-3"
                                  title="Edit Area"
                                >
                                  <Edit className="w-4 h-4" />
                                </button>
                                <button
                                  onClick={() => handleExtractPolygon('area', area.id, area.name)}
                                  className="text-purple-600 hover:text-purple-900 mr-3"
                                  title="Extract Boundary from Image"
                                >
                                  <Zap className="w-4 h-4" />
                                </button>
                                <button
                                  onClick={() => handleDelete(area.id)}
                                  className="text-red-600 hover:text-red-900"
                                  title="Delete Area"
                                >
                                  <Trash2 className="w-4 h-4" />
                                </button>
                              </td>
                            </tr>
                          );
                        })}
                        
                        {/* Show empty state if no data */}
                        {activeTab === 'cities' && filterAndSortData(cities).length === 0 && (
                          <tr>
                            <td colSpan={4} className="px-6 py-4 text-center text-gray-500">
                              No cities found
                            </td>
                          </tr>
                        )}

                        {activeTab === 'warehouses' && filterAndSortData(warehouses).length === 0 && (
                          <tr>
                            <td colSpan={4} className="px-6 py-4 text-center text-gray-500">
                              No warehouses found
                            </td>
                          </tr>
                        )}

                        {activeTab === 'areas' && filterAndSortData(areas).length === 0 && (
                          <tr>
                            <td colSpan={4} className="px-6 py-4 text-center text-gray-500">
                              No areas found
                            </td>
                          </tr>
                        )}

                        {/* Routes content */}
                        {activeTab === 'routes' && filterAndSortData(routes).map((route) => {
                          // Resolve warehouse and city for this route
                          const warehouse = warehouses.find(w => w.id === route.warehouseId || w.warehouseId === route.warehouseId);
                          const city = warehouse ? cities.find(c => c.cityCode === warehouse.cityCode) : null;
                          
                          // Count outlets assigned to this route
                          const outletsInRoute = outlets.filter(outlet => {
                            const hierarchy = buildOutletHierarchy(outlet, localities, areas, warehouses, cities, routes);
                            return hierarchy.route?.routeId === route.routeId;
                          });
                          const activeOutlets = outletsInRoute.filter(outlet => outlet.status === 'active');
                          const activeOutletsCount = activeOutlets.length;
                          const unsequencedCount = activeOutlets.filter(o => !o.outletSequence || o.outletSequence <= 0).length;

                          // Breakup by Area (active outlets only), similar to Area Routing chips
                          const areaCountMap = new globalThis.Map<string, { id: string; name: string; count: number }>();
                          for (const outlet of outletsInRoute) {
                            if (outlet.status !== 'active') continue;
                            const loc = localities.find(l => l.id === outlet.localityId);
                            const ar = loc ? areas.find(a => a.id === loc.areaId) : undefined;
                            const key = ar?.id || 'unknown';
                            const name = ar?.name || 'Unknown Area';
                            const entry = areaCountMap.get(key);
                            if (entry) {
                              entry.count += 1;
                            } else {
                              areaCountMap.set(key, { id: key, name, count: 1 });
                            }
                          }
                          const areaMix: { id: string; name: string; count: number }[] = Array.from(areaCountMap.values()).sort((a, b) => b.count - a.count);
                          
                          return (
                            <tr key={route.id} className="hover:bg-gray-50">
                              <td className="px-6 py-4 whitespace-nowrap">
                                <div className="text-sm font-medium text-gray-900">{route.name}</div>
                                <div className="text-xs text-gray-500">Route ID: {route.routeId || 'MISSING'}</div>
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap">
                                <div className="text-sm text-gray-600">{city?.name || 'Unknown City'}</div>
                                <div className="text-xs text-gray-500">Code: {warehouse?.cityCode || 'N/A'}</div>
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap">
                                <div className="text-sm text-gray-600">{warehouse?.name || 'No Warehouse'}</div>
                                {warehouse && (
                                  <div className="text-xs text-gray-500">ID: {warehouse.warehouseId}</div>
                                )}
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap">
                                <div className="text-sm text-gray-900">{outletsInRoute.length} outlets</div>
                                {outletsInRoute.length > 0 && (
                                  <div className="text-xs text-gray-500 mb-1 space-x-2">
                                    <span>Active: {activeOutletsCount}</span>
                                    {activeOutletsCount > 0 && (
                                      <span className="inline-flex px-2 py-0.5 rounded-full bg-orange-50 text-orange-700 border border-orange-200">Un-sequenced {unsequencedCount}</span>
                                    )}
                                  </div>
                                )}
                                {/* Area mix chips */}
                                <div className="flex flex-wrap gap-2">
                                  {areaMix.length === 0 && (
                                    <span className="inline-flex px-2 py-1 text-xs rounded-full bg-gray-100 text-gray-600">No Area 0</span>
                                  )}
                                  {areaMix.map((m) => (
                                    <span key={m.id} className="inline-flex px-2 py-1 text-xs rounded-full bg-blue-50 text-blue-700 border border-blue-200">
                                      {m.name} {m.count}
                                    </span>
                                  ))}
                                </div>
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap">
                                <span className={`inline-flex px-2 text-xs font-semibold rounded-full ${
                                  route.isActive 
                                    ? 'bg-green-100 text-green-800' 
                                    : 'bg-red-100 text-red-800'
                                }`}>
                                  {route.isActive ? 'Active' : 'Inactive'}
                                </span>
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                                <button
                                  onClick={() => handleEdit(route)}
                                  className="text-blue-600 hover:text-blue-900 mr-3"
                                >
                                  <Edit className="w-4 h-4" />
                                </button>
                                <button
                                  onClick={() => { setRouteFilter(route.name); setModalMapReady(false); setShowRouteOptimizeModal(true); }}
                                  className="text-blue-600 hover:text-blue-800 mr-3"
                                  title="View route (open modal)"
                                >
                                  <MapIcon className="w-4 h-4" />
                                </button>
                                <button
                                  onClick={() => handleDelete(route.id)}
                                  className="text-red-600 hover:text-red-900"
                                >
                                  <Trash2 className="w-4 h-4" />
                                </button>
                              </td>
                            </tr>
                          );
                        })}

                        {activeTab === 'routes' && filterAndSortData(routes).length === 0 && (
                          <tr>
                            <td colSpan={6} className="px-6 py-4 text-center text-gray-500">
                              No routes found
                            </td>
                          </tr>
                        )}

                        {/* Localities content */}
                        {activeTab === 'localities' && !showLocalityRouting && filterAndSortData(localities).map((locality) => {
                          // Find area using CORRECT field mapping: locality.areaId -> area.id
                          const area = areas.find(a => a.id === locality.areaId);
                          // Fix warehouse lookup - check both document ID and warehouseId field
                          const warehouse = area ? (warehouses.find(w => w.warehouseId === area.warehouseId)) : null;
                          return (
                            <tr key={locality.id} className="hover:bg-gray-50">
                              <td className="px-6 py-4 whitespace-nowrap">
                                <div className="text-sm font-medium text-gray-900">{locality.name}</div>
                                <div className="text-xs text-gray-500">Record ID: {locality.id}</div>
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap">
                                <div className="text-sm text-gray-600">{area?.name || locality.areaName || 'Unknown'}</div>
                                {warehouse && (
                                  <div className="text-xs text-gray-500">
                                    {warehouse.name} - {cities.find(c => c.cityCode === warehouse.cityCode)?.name || warehouse.cityCode}
                                  </div>
                                )}
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap">
                                <div className="text-sm text-gray-600">{locality.pincode || 'N/A'}</div>
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap">
                                <span className={`inline-flex px-2 text-xs font-semibold rounded-full ${
                                  locality.isActive 
                                    ? 'bg-green-100 text-green-800' 
                                    : 'bg-red-100 text-red-800'
                                }`}>
                                  {locality.isActive ? 'Active' : 'Inactive'}
                                </span>
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                                <button
                                  onClick={() => handleEdit(locality)}
                                  className="text-blue-600 hover:text-blue-900 mr-3"
                                  title="Edit Locality"
                                >
                                  <Edit className="w-4 h-4" />
                                </button>
                                <button
                                  onClick={() => handleExtractPolygon('locality', locality.id, locality.name)}
                                  className="text-purple-600 hover:text-purple-900 mr-3"
                                  title="Extract Boundary from Image"
                                >
                                  <Zap className="w-4 h-4" />
                                </button>
                                <button
                                  onClick={() => handleDelete(locality.id)}
                                  className="text-red-600 hover:text-red-900"
                                  title="Delete Locality"
                                >
                                  <Trash2 className="w-4 h-4" />
                                </button>
                              </td>
                            </tr>
                          );
                        })}

                        {activeTab === 'localities' && !showLocalityRouting && filterAndSortData(localities).length === 0 && (
                          <tr>
                            <td colSpan={5} className="px-6 py-4 text-center text-gray-500">
                              No localities found
                            </td>
                          </tr>
                        )}

                        {/* Locality Routing content inside Localities tab */}
                        {activeTab === 'localities' && showLocalityRouting && filterAndSortData(localities).map((locality) => {
                          // Find area using CORRECT field mapping: locality.areaId -> area.id
                          const area = areas.find(a => a.id === locality.areaId);
                          const warehouse = area ? (warehouses.find(w => w.warehouseId === area.warehouseId)) : null;
                          
                          // Find current route assignment
                          const currentRoute = routes.find(r => r.routeId === locality.routeId);
                          
                          // Count outlets in this locality
                          const outletsInLocality = outlets.filter(o => o.localityId === locality.id);
                          
                          return (
                            <tr key={locality.id} className="hover:bg-gray-50">
                              <td className="px-6 py-4 whitespace-nowrap">
                                <div className="text-sm font-medium text-gray-900">{locality.name}</div>
                                <div className="text-xs text-gray-500">ID: {locality.id}</div>
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap">
                                <div className="text-sm text-gray-600">{area?.name || locality.areaName || 'Unknown'}</div>
                                {warehouse && (
                                  <div className="text-xs text-gray-500">
                                    {warehouse.name} - {cities.find(c => c.cityCode === warehouse.cityCode)?.name || warehouse.cityCode}
                                  </div>
                                )}
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap">
                                {currentRoute ? (
                                  <div>
                                    <div className="text-sm font-medium text-gray-900">{currentRoute.name}</div>
                                    <div className="text-xs text-gray-500">Route ID: {currentRoute.routeId}</div>
                                  </div>
                                ) : (
                                  <span className="text-sm text-gray-500 italic">No route assigned</span>
                                )}
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap">
                                <div className="text-sm text-gray-900">{outletsInLocality.length} outlets</div>
                                {outletsInLocality.length > 0 && (
                                  <div className="text-xs text-gray-500">
                                    Active: {outletsInLocality.filter(o => o.status === 'active').length}
                                  </div>
                                )}
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-right">
                                <select
                                  value={locality.routeId || ''}
                                  onChange={(e) => handleLocalityRouteAssign(locality.id, e.target.value || null)}
                                  className="text-sm border border-gray-300 rounded-md px-2 py-1 focus:outline-none focus:ring-2 focus:ring-blue-500"
                                  disabled={networkLoading}
                                >
                                  <option value="">No Route</option>
                                  {routes.filter(r => r.isActive && r.routeId).map(route => (
                                    <option key={route.routeId} value={route.routeId}>
                                      {route.name}
                                    </option>
                                  ))}
                                </select>
                              </td>
                            </tr>
                          );
                        })}

                        {activeTab === 'localities' && showLocalityRouting && filterAndSortData(localities).length === 0 && (
                          <tr>
                            <td colSpan={5} className="px-6 py-4 text-center text-gray-500">
                              No localities found
                            </td>
                          </tr>
                        )}

                        {/* Classifications content */}
                        {activeTab === 'classifications' && filterAndSortData(classifications).map((classification) => (
                          <tr key={classification.id} className="hover:bg-gray-50">
                            <td className="px-6 py-4 whitespace-nowrap">
                              <div className="text-sm font-medium text-gray-900">{classification.name}</div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <div className="text-sm text-gray-600">{classification.description || 'No description'}</div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <span className={`inline-flex px-2 text-xs font-semibold rounded-full ${
                                classification.isActive 
                                  ? 'bg-green-100 text-green-800' 
                                  : 'bg-red-100 text-red-800'
                              }`}>
                                {classification.isActive ? 'Active' : 'Inactive'}
                              </span>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                              <button
                                onClick={() => handleEdit(classification)}
                                className="text-blue-600 hover:text-blue-900 mr-3"
                              >
                                <Edit className="w-4 h-4" />
                              </button>
                              <button
                                onClick={() => handleDelete(classification.id)}
                                className="text-red-600 hover:text-red-900"
                              >
                                <Trash2 className="w-4 h-4" />
                              </button>
                            </td>
                          </tr>
                        ))}

                        {activeTab === 'classifications' && filterAndSortData(classifications).length === 0 && (
                          <tr>
                            <td colSpan={4} className="px-6 py-4 text-center text-gray-500">
                              No classifications found
                            </td>
                          </tr>
                        )}

                        {/* Outlets content */}
                        {activeTab === 'outlets' && getPaginatedData(filterAndSortData(outlets)).data.map((outlet) => {
                          const hierarchy = buildOutletHierarchy(outlet, localities, areas, warehouses, cities, routes);
                          const classification = classifications.find(cl => cl.id === outlet.classificationId);
                          
                          // Created Date now sourced from addedDate
                          const createdDate = outlet.addedDate
                            ? (outlet.addedDate.toDate ? outlet.addedDate.toDate() : new Date(outlet.addedDate))
                            : null;
                          
                          return (
                            <tr key={outlet.id} className="hover:bg-gray-50">
                              {/* Outlet ID */}
                              <td className="px-6 py-4 whitespace-nowrap">
                                <div className="text-sm font-medium text-gray-900">{outlet.id}</div>
                              </td>
                              
                              {/* Name */}
                              <td className="px-6 py-4 whitespace-nowrap">
                                <div className="text-sm font-medium text-gray-900">{outlet.name}</div>
                                <div className="text-xs text-gray-500">{outlet.address?.slice(0, 30)}{outlet.address?.length > 30 ? '...' : ''}</div>
                              </td>
                              
                              {/* City */}
                              <td className="px-6 py-4 whitespace-nowrap">
                                <div className="text-sm text-gray-600">{hierarchy.city?.name || 'No City Found'}</div>
                              </td>
                              
                              {/* Warehouse */}
                              <td className="px-6 py-4 whitespace-nowrap">
                                <div className="text-sm text-gray-600">{hierarchy.warehouse?.name || 'Unknown Warehouse'}</div>
                              </td>
                              
                              {/* Area */}
                              <td className="px-6 py-4 whitespace-nowrap">
                                <div className="text-sm text-gray-600">{hierarchy.area?.name || 'Unknown Area'}</div>
                              </td>
                              
                              {/* Locality */}
                              <td className="px-6 py-4 whitespace-nowrap">
                                <div className="text-sm text-gray-600">{hierarchy.locality?.name || 'Unknown'}</div>
                              </td>
                              
                              {/* Route */}
                              <td className="px-6 py-4 whitespace-nowrap">
                                <div className="text-sm text-gray-600">{hierarchy.route?.name || 'No Route'}</div>
                              </td>
                              
                              {/* Sequence */}
                              <td className="px-6 py-4 whitespace-nowrap">
                                <div className="text-sm text-gray-600">
                                  {outlet.outletSequence ? (
                                    <span className="inline-flex items-center px-2 py-1 text-xs font-medium bg-blue-100 text-blue-800 rounded-full">
                                      #{outlet.outletSequence}
                                    </span>
                                  ) : (
                                    <span className="text-gray-400">-</span>
                                  )}
                                </div>
                              </td>
                              
                              {/* Pincode derived from locality */}
                              <td className="px-6 py-4 whitespace-nowrap">
                                <div className="text-sm text-gray-600">{hierarchy.locality?.pincode || 'N/A'}</div>
                              </td>
                              
                              {/* Exotic Fruits */}
                              <td className="px-6 py-4 whitespace-nowrap">
                                <div className="text-sm text-gray-600">
                                  {(outlet as any).hasExoticFruits !== undefined ? (
                                    <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                                      (outlet as any).hasExoticFruits
                                        ? 'bg-green-100 text-green-800'
                                        : 'bg-red-100 text-red-800'
                                    }`}>
                                      {(outlet as any).hasExoticFruits ? 'Yes' : 'No'}
                                    </span>
                                  ) : (
                                    <span className="text-gray-400">N/A</span>
                                  )}
                                </div>
                              </td>
                              
                              {/* Created Date */}
                              <td className="px-6 py-4 whitespace-nowrap">
                                {createdDate ? (
                                  <div>
                                    <div className="text-sm text-gray-600">
                                      {createdDate.toLocaleDateString('en-IN')}
                                    </div>
                                    <div className="text-xs text-gray-500">
                                      {createdDate.toLocaleTimeString('en-IN', { 
                                        hour: '2-digit', 
                                        minute: '2-digit' 
                                      })}
                                    </div>
                                  </div>
                                ) : (
                                  <span className="text-sm text-gray-400">N/A</span>
                                )}
                              </td>
                              
                              {/* Status */}
                              <td className="px-6 py-4 whitespace-nowrap">
                                <span className={`inline-flex px-2 text-xs font-semibold rounded-full ${
                                  outlet.status === 'active' 
                                    ? 'bg-green-100 text-green-800' 
                                    : outlet.status === 'pending_approval'
                                    ? 'bg-yellow-100 text-yellow-800'
                                    : outlet.status === 'inactive'
                                    ? 'bg-red-100 text-red-800'
                                    : 'bg-gray-100 text-gray-800'
                                }`}>
                                  {outlet.status === 'pending_approval' ? 'Pending Approval' :
                                   outlet.status === 'active' ? 'Active' :
                                   outlet.status === 'inactive' ? 'Inactive' : 
                                   outlet.status === 'draft' ? 'Draft' : outlet.status}
                                </span>
                              </td>
                              
                              {/* Actions */}
                              <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium sticky right-0 bg-white">
                                <button
                                  onClick={() => {
                                    setEditingOutlet(outlet);
                                    setShowAddOutletModal(true);
                                  }}
                                  className="text-blue-600 hover:text-blue-900 mr-3"
                                >
                                  <Edit className="w-4 h-4" />
                                </button>
                                <button
                                  onClick={() => handleDelete(outlet.id, outlet.documentId)}
                                  className="text-red-600 hover:text-red-900"
                                >
                                  <Trash2 className="w-4 h-4" />
                                </button>
                              </td>
                            </tr>
                          );
                        })}

                        {activeTab === 'outlets' && filterAndSortData(outlets).length === 0 && (
                          <tr>
                            <td colSpan={7} className="px-6 py-4 text-center text-gray-500">
                              No outlets found
                            </td>
                          </tr>
                        )}
                        
                        {/* Add other tab content as needed */}
                      </tbody>
                    </table>
                    )}
                  </div>
                </div>
              )}

              {/* Route Optimize Modal */}
              {showRouteOptimizeModal && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40">
                  <div className="bg-white rounded-lg shadow-xl w-[92vw] max-w-5xl">
                    <div className="px-6 py-4 border-b flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <RouteIcon className="w-5 h-5 text-purple-600" />
                        <h3 className="text-lg font-semibold text-gray-800">Optimize Route</h3>
                      </div>
                      <button className="text-gray-500 hover:text-gray-700" onClick={() => setShowRouteOptimizeModal(false)}>
                        <X className="w-5 h-5" />
                      </button>
                    </div>
                    <div className="p-6 space-y-4">
                      {/* mount Map preview slightly after modal opens to avoid zero-size init */}
                      {(() => {
                        if (!modalMapReady) {
                          setTimeout(() => setModalMapReady(true), 50);
                        }
                        return null;
                      })()}
                      <div className="text-sm text-gray-700">Route: <span className="font-medium">{routeFilter}</span></div>
                      <div className="text-xs text-gray-600">This will reset and reassign outlet sequence numbers for active outlets in this route based on proximity to the warehouse.</div>

                      {/* Map preview */}
                      {modalMapReady && (
                        <div className="rounded border border-gray-200 overflow-hidden w-full" style={{ aspectRatio: '3 / 2', minHeight: '360px' }}>
                          <MapContainer
                            key={`modal-preview-${routeFilter}-${showRouteOptimizeModal}-${modalMapReady}`}
                            outlets={modalRouteOutlets}
                            localities={localities}
                            areas={areas}
                            warehouses={warehouses}
                            cities={cities}
                            routes={routes}
                            classifications={classifications}
                            pincodes={pincodes}
                            selectedPincodeFilter={[]}
                            showAreaBoundaries={false}
                            showLocalityBoundaries={false}
                            polygonBoundaries={[]}
                            showOptimizedRoute={true}
                            selectedRouteFilter={routeFilter}
                          />
                        </div>
                      )}

                      {optimizationMessage && (
                        <div className="px-3 py-2 text-xs bg-gray-100 text-gray-800 rounded border border-gray-200">{optimizationMessage}</div>
                      )}
                    </div>
                    <div className="px-6 py-4 border-t flex items-center justify-end gap-3">
                      <button className="px-4 py-2 rounded border bg-white text-gray-700 hover:bg-gray-50" onClick={() => { setShowRouteOptimizeModal(false); setModalMapReady(false); }} disabled={isOptimizingRoute}>Close</button>
                      <button className={`px-4 py-2 rounded ${isOptimizingRoute ? 'bg-gray-300 text-gray-600' : 'bg-purple-600 text-white hover:bg-purple-700'}`} onClick={async () => { await handleOptimizeRoute(routeFilter); await loadNetworkData(); }} disabled={isOptimizingRoute}>
                        {isOptimizingRoute ? 'Optimizing…' : 'Optimize Route'}
                      </button>
                    </div>
                  </div>
                </div>
              )}

              {/* Pagination Controls - Only for List View */}
              {activeTab === 'outlets' && outletViewTab === 'list' && (() => {
                const fullData = filterAndSortData(outlets);
                const paginationInfo = getPaginatedData(fullData);
                
                if (paginationInfo.totalPages > 1) {
                  return (
                    <div className="bg-white border-t border-gray-200 px-6 py-4 flex items-center justify-between">
                      <div className="flex-1 flex justify-between sm:hidden">
                        <button
                          onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                          disabled={currentPage === 1}
                          className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          Previous
                        </button>
                        <button
                          onClick={() => setCurrentPage(Math.min(paginationInfo.totalPages, currentPage + 1))}
                          disabled={currentPage === paginationInfo.totalPages}
                          className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          Next
                        </button>
                      </div>
                      <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
                        <div>
                          <p className="text-sm text-gray-700">
                            Showing{' '}
                            <span className="font-medium">{paginationInfo.startIndex + 1}</span>
                            {' '}to{' '}
                            <span className="font-medium">{paginationInfo.endIndex}</span>
                            {' '}of{' '}
                            <span className="font-medium">{paginationInfo.totalItems}</span>
                            {' '}results
                          </p>
                        </div>
                        <div>
                          <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px" aria-label="Pagination">
                            <button
                              onClick={() => setCurrentPage(1)}
                              disabled={currentPage === 1}
                              className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                              <span className="sr-only">First</span>
                              <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 20 20">
                                <path fillRule="evenodd" d="M15.707 15.707a1 1 0 01-1.414 0l-5-5a1 1 0 010-1.414l5-5a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 010 1.414zm-6 0a1 1 0 01-1.414 0l-5-5a1 1 0 010-1.414l5-5a1 1 0 011.414 1.414L5.414 10l4.293 4.293a1 1 0 010 1.414z" clipRule="evenodd" />
                              </svg>
                            </button>
                            <button
                              onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                              disabled={currentPage === 1}
                              className="relative inline-flex items-center px-2 py-2 border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                              <span className="sr-only">Previous</span>
                              <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 20 20">
                                <path fillRule="evenodd" d="M12.707 5.293a1 1 0 010 1.414L9.414 10l3.293 3.293a1 1 0 01-1.414 1.414l-4-4a1 1 0 010-1.414l4-4a1 1 0 011.414 0z" clipRule="evenodd" />
                              </svg>
                            </button>

                            {/* Page Numbers */}
                            {(() => {
                              const pages = [];
                              const totalPages = paginationInfo.totalPages;
                              let startPage = Math.max(1, currentPage - 2);
                              let endPage = Math.min(totalPages, startPage + 4);
                              
                              if (endPage - startPage < 4) {
                                startPage = Math.max(1, endPage - 4);
                              }

                              for (let i = startPage; i <= endPage; i++) {
                                pages.push(
                                  <button
                                    key={i}
                                    onClick={() => setCurrentPage(i)}
                                    className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${
                                      i === currentPage
                                        ? 'z-10 bg-blue-50 border-blue-500 text-blue-600'
                                        : 'bg-white border-gray-300 text-gray-500 hover:bg-gray-50'
                                    }`}
                                  >
                                    {i}
                                  </button>
                                );
                              }
                              
                              return pages;
                            })()}

                            <button
                              onClick={() => setCurrentPage(Math.min(paginationInfo.totalPages, currentPage + 1))}
                              disabled={currentPage === paginationInfo.totalPages}
                              className="relative inline-flex items-center px-2 py-2 border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                              <span className="sr-only">Next</span>
                              <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 20 20">
                                <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd" />
                              </svg>
                            </button>
                            <button
                              onClick={() => setCurrentPage(paginationInfo.totalPages)}
                              disabled={currentPage === paginationInfo.totalPages}
                              className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                              <span className="sr-only">Last</span>
                              <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 20 20">
                                <path fillRule="evenodd" d="M10.293 15.707a1 1 0 010-1.414L14.586 10l-4.293-4.293a1 1 0 111.414-1.414l5 5a1 1 0 010 1.414l-5 5a1 1 0 01-1.414 0zm-6 0a1 1 0 010-1.414L8.586 10 4.293 5.707a1 1 0 011.414-1.414l5 5a1 1 0 010 1.414l-5 5a1 1 0 01-1.414 0z" clipRule="evenodd" />
                              </svg>
                            </button>
                          </nav>
                        </div>
                      </div>
                    </div>
                  );
                }
                return null;
              })()}

              {/* Map View Info - Only for Map View */}
              {activeTab === 'outlets' && outletViewTab === 'map' && (
                <div className="bg-white border-t border-gray-200 px-6 py-3">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-4">
                      <p className="text-sm text-gray-700">
                        Displaying all{' '}
                        <span className="font-medium text-blue-600">{filterAndSortData(outlets).length}</span>
                        {' '}outlets on map with smart clustering
                      </p>
                      {(showAreaBoundaries || showLocalityBoundaries) && (
                        <div className="flex items-center space-x-2 text-sm text-gray-600">
                          <span>•</span>
                          <span>
                            {showAreaBoundaries && showLocalityBoundaries ? 'Area & locality boundaries enabled' :
                             showAreaBoundaries ? 'Area boundaries enabled' :
                             'Locality boundaries enabled'}
                          </span>
                        </div>
                      )}
                    </div>
                    <div className="flex items-center space-x-4">
                      {(showAreaBoundaries || showLocalityBoundaries) && (
                        <div className="flex items-center space-x-3 text-xs">
                          {showAreaBoundaries && (
                            <div className="flex items-center space-x-1">
                              <div className="w-3 h-3 bg-blue-500 rounded opacity-60"></div>
                              <span className="text-gray-500">Areas</span>
                            </div>
                          )}
                          {showLocalityBoundaries && (
                            <div className="flex items-center space-x-1">
                              <div className="w-3 h-3 bg-green-500 rounded opacity-60"></div>
                              <span className="text-gray-500">Localities</span>
                            </div>
                          )}
                        </div>
                      )}
                      <div className="text-xs text-gray-500">
                        Zoom in to see individual outlets
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {/* Add/Edit Modal */}
      {(showAddModal || showEditModal) && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold">
                {editingItem ? 'Edit' : 'Add'} {
                  activeTab === 'cities' ? 'City' :
                  activeTab === 'warehouses' ? 'Warehouse' :
                  activeTab === 'areas' ? 'Area' :
                  activeTab === 'routes' ? 'Route' :
                  activeTab === 'localities' ? 'Locality' :
                  activeTab === 'classifications' ? 'Classification' :
                  'Item'
                }
              </h3>
              <button
                onClick={() => {
                  setShowAddModal(false);
                  setShowEditModal(false);
                  setEditingItem(null);
                  setFormData({});
                }}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            <form onSubmit={(e) => { e.preventDefault(); handleSave(); }}>
              <div className="space-y-4">
                {/* City Form */}
                {activeTab === 'cities' && (
                  <>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        City Name *
                      </label>
                      <input
                        type="text"
                        value={formData.name || ''}
                        onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        State *
                      </label>
                      <input
                        type="text"
                        value={formData.state || ''}
                        onChange={(e) => setFormData({ ...formData, state: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        City Code *
                      </label>
                      <input
                        type="text"
                        value={formData.cityCode || ''}
                        onChange={(e) => setFormData({ ...formData, cityCode: e.target.value.toUpperCase() })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        placeholder="e.g., MUM, DEL, BLR"
                        required
                      />
                    </div>
                    <div className="flex items-center">
                      <input
                        type="checkbox"
                        id="cityActive"
                        checked={formData.isActive !== false}
                        onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
                        className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                      />
                      <label htmlFor="cityActive" className="ml-2 text-sm text-gray-700">
                        Active
                      </label>
                    </div>
                  </>
                )}

                {/* Warehouse Form */}
                {activeTab === 'warehouses' && (
                  <>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Warehouse Name *
                      </label>
                      <input
                        type="text"
                        value={formData.name || ''}
                        onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Warehouse ID *
                      </label>
                      <input
                        type="text"
                        value={formData.warehouseId || ''}
                        onChange={(e) => setFormData({ ...formData, warehouseId: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        placeholder="e.g., MUM-WH02"
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        City *
                      </label>
                      <select
                        value={formData.cityCode || ''}
                        onChange={(e) => setFormData({ ...formData, cityCode: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        required
                      >
                        <option value="">Select City</option>
                        {cities.filter(c => c.isActive).map(city => (
                          <option key={city.id} value={city.cityCode}>
                            {city.name} ({city.cityCode})
                          </option>
                        ))}
                      </select>
                    </div>
                    <div className="flex items-center">
                      <input
                        type="checkbox"
                        id="warehouseActive"
                        checked={formData.isActive !== false}
                        onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
                        className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                      />
                      <label htmlFor="warehouseActive" className="ml-2 text-sm text-gray-700">
                        Active
                      </label>
                    </div>
                  </>
                )}

                {/* Area Form */}
                {activeTab === 'areas' && (
                  <>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Area Name *
                      </label>
                      <input
                        type="text"
                        value={formData.name || ''}
                        onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Warehouse *
                      </label>
                      <select
                        value={formData.warehouseId || ''}
                        onChange={(e) => setFormData({ ...formData, warehouseId: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        required
                      >
                        <option value="">Select Warehouse</option>
                        {warehouses.filter(w => w.isActive).map(warehouse => {
                            const city = cities.find(c => c.cityCode === warehouse.cityCode);
                          return (
                            <option key={warehouse.id} value={warehouse.id}>
                              {warehouse.name} - {city?.name || warehouse.cityCode}
                            </option>
                          );
                        })}
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Description
                      </label>
                      <textarea
                        value={formData.description || ''}
                        onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        rows={3}
                      />
                    </div>
                    <div className="flex items-center">
                      <input
                        type="checkbox"
                        id="areaActive"
                        checked={formData.isActive !== false}
                        onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
                        className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                      />
                      <label htmlFor="areaActive" className="ml-2 text-sm text-gray-700">
                        Active
                      </label>
                    </div>
                  </>
                )}

                {/* Route Form */}
                {activeTab === 'routes' && (
                  <>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Route Name *
                      </label>
                      <input
                        type="text"
                        value={formData.name || ''}
                        onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Route ID
                      </label>
                      <input
                        type="text"
                        value={formData.routeId || ''}
                        readOnly
                        placeholder={editingItem ? '' : 'Auto-generated (e.g., RT007)'}
                        className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 bg-gray-100 text-gray-600 border-gray-200 cursor-not-allowed`}
                      />
                      <p className="mt-1 text-xs text-gray-500">Route ID is system-generated and cannot be edited.</p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Warehouse *
                      </label>
                      <select
                        value={formData.warehouseId || ''}
                        onChange={(e) => {
                          const selectedWarehouse = warehouses.find(w => w.id === e.target.value);
                          setFormData({ 
                            ...formData, 
                            warehouseId: e.target.value,
                            warehouseName: selectedWarehouse?.name || ''
                          });
                        }}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        required
                      >
                        <option value="">Select Warehouse</option>
                        {editingItem 
                          ? // Edit mode: show warehouses from same city only
                            warehouses.filter(warehouse => {
                              const currentWarehouse = warehouses.find(w => w.warehouseId === editingItem.warehouseId);
                              return currentWarehouse ? warehouse.cityCode === currentWarehouse.cityCode : true;
                            }).filter(w => w.isActive).map(warehouse => {
                              const city = cities.find(c => c.cityCode === warehouse.cityCode);
                              return (
                                <option key={warehouse.id} value={warehouse.id}>
                                  {warehouse.name} - {city?.name || warehouse.cityCode}
                                </option>
                              );
                            })
                          : // Add mode: show all active warehouses
                            warehouses.filter(w => w.isActive).map(warehouse => {
                              const city = cities.find(c => c.cityCode === warehouse.cityCode);
                              return (
                                <option key={warehouse.id} value={warehouse.id}>
                                  {warehouse.name} - {city?.name || warehouse.cityCode}
                                </option>
                              );
                            })
                        }
                      </select>
                    </div>
                    {/* City Display (Read-only) */}
                    {formData.warehouseId && (() => {
                      const selectedWarehouse = warehouses.find(w => w.id === formData.warehouseId);
                      const selectedCity = selectedWarehouse ? cities.find(c => c.cityCode === selectedWarehouse.cityCode) : null;
                      return selectedCity ? (
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            City (Auto-selected)
                          </label>
                          <div className="w-full px-3 py-2 border border-gray-200 rounded-md bg-gray-50 text-gray-700">
                            {selectedCity.name} ({selectedCity.cityCode})
                          </div>
                        </div>
                      ) : null;
                    })()}
                    <div className="flex items-center">
                      <input
                        type="checkbox"
                        id="routeActive"
                        checked={formData.isActive !== false}
                        onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
                        className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                      />
                      <label htmlFor="routeActive" className="ml-2 text-sm text-gray-700">
                        Active
                      </label>
                    </div>
                  </>
                )}

                {/* Locality Form */}
                {activeTab === 'localities' && (
                  <>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Locality Name *
                      </label>
                      <input
                        type="text"
                        value={formData.name || ''}
                        onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Area *
                      </label>
                      <select
                        value={formData.areaId || ''}
                        onChange={(e) => {
                          const selectedArea = areas.find(a => a.id === e.target.value);
                          setFormData({ 
                            ...formData, 
                            areaId: e.target.value,
                            areaName: selectedArea?.name || ''
                          });
                        }}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        required
                      >
                        <option value="">Select Area</option>
                          {areas
                            .filter(a => a.isActive)
                            .slice()
                            .sort((a, b) => (a.name || '').localeCompare(b.name || '', undefined, { sensitivity: 'base' }))
                            .map(area => {
                          const warehouse = warehouses.find(w => w.id === area.warehouseId || w.warehouseId === area.warehouseId);
                          const city = warehouse ? cities.find(c => c.cityCode === warehouse.cityCode) : null;
                          return (
                            <option key={area.id} value={area.id}>
                              {area.name} - {warehouse?.name || 'Unknown Warehouse'} ({city?.name || 'Unknown City'})
                            </option>
                          );
                          })}
                      </select>
                    </div>
                    {/* Area Display (Read-only) */}
                    {formData.areaId && (() => {
                      const selectedArea = areas.find(a => a.id === formData.areaId);
                      const warehouse = selectedArea ? warehouses.find(w => w.id === selectedArea.warehouseId || w.warehouseId === selectedArea.warehouseId) : null;
                      const city = warehouse ? cities.find(c => c.cityCode === warehouse.cityCode) : null;
                      return selectedArea ? (
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Area Details (Auto-selected)
                          </label>
                          <div className="w-full px-3 py-2 border border-gray-200 rounded-md bg-gray-50 text-gray-700">
                            <div className="text-sm">{selectedArea.name}</div>
                            <div className="text-xs text-gray-500">
                              {warehouse?.name || 'Unknown Warehouse'} - {city?.name || 'Unknown City'}
                            </div>
                          </div>
                        </div>
                      ) : null;
                    })()}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Pincode
                      </label>
                      <input
                        type="text"
                        value={formData.pincode || ''}
                        onChange={(e) => setFormData({ ...formData, pincode: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        placeholder="e.g., 400001"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Description
                      </label>
                      <textarea
                        value={formData.description || ''}
                        onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        rows={3}
                        placeholder="Optional description or notes about this locality"
                      />
                    </div>
                    <div className="flex items-center">
                      <input
                        type="checkbox"
                        id="localityActive"
                        checked={formData.isActive !== false}
                        onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
                        className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                      />
                      <label htmlFor="localityActive" className="ml-2 text-sm text-gray-700">
                        Active
                      </label>
                    </div>
                  </>
                )}

                {/* Classification Form */}
                {activeTab === 'classifications' && (
                  <>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Classification Name *
                      </label>
                      <input
                        type="text"
                        value={formData.name || ''}
                        onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Description
                      </label>
                      <textarea
                        value={formData.description || ''}
                        onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        rows={3}
                      />
                    </div>
                    <div className="flex items-center">
                      <input
                        type="checkbox"
                        id="classificationActive"
                        checked={formData.isActive !== false}
                        onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
                        className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                      />
                      <label htmlFor="classificationActive" className="ml-2 text-sm text-gray-700">
                        Active
                      </label>
                    </div>
                  </>
                )}

                {/* Outlet Form - Complete AddOutlet Components */}
                {activeTab === 'outlets' && (
                  <div className="space-y-6 max-h-[80vh] overflow-y-auto">
                    {/* Name Section */}
                    <div className="bg-gray-50 p-4 rounded-lg">
                      <h4 className="text-sm font-semibold text-gray-800 mb-3 flex items-center space-x-2">
                        <Building2 className="w-4 h-4 text-blue-600" />
                        <span>Name</span>
                      </h4>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Outlet Name</label>
                        <input
                          type="text"
                          value={formData.name || ''}
                          onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                          placeholder="Enter outlet name"
                        />
                      </div>
                    </div>

                    {/* Address Section */}
                    <div className="bg-gray-50 p-4 rounded-lg">
                      <h4 className="text-sm font-semibold text-gray-800 mb-3 flex items-center space-x-2">
                        <MapPin className="w-4 h-4 text-green-600" />
                        <span>Address</span>
                      </h4>
                      <div className="space-y-3">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Building/Shop No.</label>
                          <input
                            type="text"
                            value={formData.building || ''}
                            onChange={(e) => setFormData({ ...formData, building: e.target.value })}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                            placeholder="Building or shop number"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Street Name</label>
                          <input
                            type="text"
                            value={formData.streetName || ''}
                            onChange={(e) => setFormData({ ...formData, streetName: e.target.value })}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                            placeholder="Street name"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Landmark</label>
                          <input
                            type="text"
                            value={formData.landmark || ''}
                            onChange={(e) => setFormData({ ...formData, landmark: e.target.value })}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                            placeholder="Nearby landmark"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Sub-Locality</label>
                          <input
                            type="text"
                            value={formData.subLocality || ''}
                            onChange={(e) => setFormData({ ...formData, subLocality: e.target.value })}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                            placeholder="Sub-locality"
                          />
                        </div>
                        <div className="grid grid-cols-2 gap-3">
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Area</label>
                            <select
                              value={formData.areaId || ''}
                              onChange={(e) => {
                                const selectedArea = areas.find(area => area.id === e.target.value);
                                setFormData({ 
                                  ...formData, 
                                  areaId: e.target.value,
                                  areaName: selectedArea?.name || ''
                                });
                              }}
                              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                            >
                              <option value="">Select Area</option>
                              {areas.map(area => (
                                <option key={area.id} value={area.id}>{area.name}</option>
                              ))}
                            </select>
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Locality</label>
                            <select
                              value={formData.localityId || ''}
                              onChange={(e) => {
                                const selectedLocality = localities.find(locality => locality.id === e.target.value);
                                setFormData({ 
                                  ...formData, 
                                  localityId: e.target.value,
                                  localityName: selectedLocality?.name || ''
                                });
                              }}
                              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                            >
                              <option value="">Select Locality</option>
                              {localities.map(locality => (
                                <option key={locality.id} value={locality.id}>{locality.name}</option>
                              ))}
                            </select>
                          </div>
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Pincode</label>
                          <input
                            type="text"
                            value={formData.pincode || ''}
                            onChange={(e) => setFormData({ ...formData, pincode: e.target.value })}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                            placeholder="Postal code"
                          />
                        </div>
                      </div>
                    </div>

                    {/* Mobile and QR Section */}
                    <div className="bg-gray-50 p-4 rounded-lg">
                      <h4 className="text-sm font-semibold text-gray-800 mb-3 flex items-center space-x-2">
                        <Phone className="w-4 h-4 text-purple-600" />
                        <span>Mobile and QR</span>
                      </h4>
                      <div className="space-y-3">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Mobile Number</label>
                          <input
                            type="tel"
                            value={formData.mobile || ''}
                            onChange={(e) => setFormData({ ...formData, mobile: e.target.value })}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                            placeholder="Enter mobile number"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Mobile Number 2</label>
                          <input
                            type="tel"
                            value={formData.mobile2 || ''}
                            onChange={(e) => setFormData({ ...formData, mobile2: e.target.value })}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                            placeholder="Alternate mobile (optional)"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Mobile Number 3</label>
                          <input
                            type="tel"
                            value={formData.mobile3 || ''}
                            onChange={(e) => setFormData({ ...formData, mobile3: e.target.value })}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                            placeholder="Another mobile (optional)"
                          />
                        </div>
                        {formData.qrCodeData && (
                          <div className="bg-green-50 border border-green-200 rounded-lg p-3">
                            <div className="flex items-center justify-between mb-2">
                              <h5 className="text-sm font-medium text-green-800 flex items-center space-x-2">
                                <QrCode className="w-4 h-4" />
                                <span>QR Code Data</span>
                              </h5>
                              <button
                                type="button"
                                onClick={() => setFormData({ ...formData, qrCodeData: '' })}
                                className="text-xs text-red-600 hover:text-red-700"
                              >
                                <X className="w-4 h-4" />
                              </button>
                            </div>
                            <div className="bg-white border border-green-300 rounded p-2 text-xs font-mono break-all">
                              {formData.qrCodeData}
                            </div>
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Rating & Details Section */}
                    <div className="bg-gray-50 p-4 rounded-lg">
                      <h4 className="text-sm font-semibold text-gray-800 mb-3 flex items-center space-x-2">
                        <Sparkles className="w-4 h-4 text-yellow-600" />
                        <span>Rating & Details</span>
                      </h4>
                      <div className="space-y-3">
                        {/* Star Rating */}
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">Outlet Rating</label>
                          <div className="flex items-center space-x-1">
                            {[1, 2, 3, 4, 5].map((star) => (
                              <button
                                key={star}
                                type="button"
                                onClick={() => setFormData({ ...formData, rating: star })}
                                className={`w-6 h-6 transition-colors ${
                                  star <= (formData.rating || 0) 
                                    ? 'text-yellow-400 hover:text-yellow-500' 
                                    : 'text-gray-300 hover:text-yellow-400'
                                }`}
                              >
                                <svg className="w-full h-full fill-current" viewBox="0 0 24 24">
                                  <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/>
                                </svg>
                              </button>
                            ))}
                            <span className="ml-3 text-sm text-gray-600">
                              {(formData.rating || 0) > 0 ? `${formData.rating} star${formData.rating > 1 ? 's' : ''}` : 'No rating'}
                            </span>
                          </div>
                        </div>

                        {/* Conditional Questions for Rated Outlets */}
                        {(formData.rating || 0) > 0 && (
                          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 space-y-3">
                            <h5 className="text-sm font-medium text-yellow-800">Additional Questions</h5>
                            
                            {/* Exotic Fruits */}
                            <div className="flex items-center justify-between p-2 bg-white rounded border">
                              <div className="flex items-center space-x-2">
                                <span>🥝</span>
                                <div>
                                  <p className="text-sm font-medium">Exotic Fruits Available?</p>
                                  <p className="text-xs text-gray-500">Avocado, Dragon Fruit, Kiwi, etc.</p>
                                </div>
                              </div>
                              <div className="flex space-x-1">
                                <button
                                  type="button"
                                  onClick={() => setFormData({ ...formData, hasExoticFruits: false })}
                                  className={`px-2 py-1 text-xs rounded ${
                                    !formData.hasExoticFruits
                                      ? 'bg-red-100 text-red-800 border border-red-300'
                                      : 'bg-gray-100 text-gray-600'
                                  }`}
                                >
                                  No
                                </button>
                                <button
                                  type="button"
                                  onClick={() => setFormData({ ...formData, hasExoticFruits: true })}
                                  className={`px-2 py-1 text-xs rounded ${
                                    formData.hasExoticFruits
                                      ? 'bg-green-100 text-green-800 border border-green-300'
                                      : 'bg-gray-100 text-gray-600'
                                  }`}
                                >
                                  Yes
                                </button>
                              </div>
                            </div>

                            {/* Home Delivery */}
                            <div className="flex items-center justify-between p-2 bg-white rounded border">
                              <div className="flex items-center space-x-2">
                                <span>🚚</span>
                                <div>
                                  <p className="text-sm font-medium">Home Delivery Available?</p>
                                  <p className="text-xs text-gray-500">Delivery service provided?</p>
                                </div>
                              </div>
                              <div className="flex space-x-1">
                                <button
                                  type="button"
                                  onClick={() => setFormData({ ...formData, hasHomeDelivery: false })}
                                  className={`px-2 py-1 text-xs rounded ${
                                    !formData.hasHomeDelivery
                                      ? 'bg-red-100 text-red-800 border border-red-300'
                                      : 'bg-gray-100 text-gray-600'
                                  }`}
                                >
                                  No
                                </button>
                                <button
                                  type="button"
                                  onClick={() => setFormData({ ...formData, hasHomeDelivery: true })}
                                  className={`px-2 py-1 text-xs rounded ${
                                    formData.hasHomeDelivery
                                      ? 'bg-green-100 text-green-800 border border-green-300'
                                      : 'bg-gray-100 text-gray-600'
                                  }`}
                                >
                                  Yes
                                </button>
                              </div>
                            </div>
                          </div>
                        )}

                        <div className="grid grid-cols-2 gap-3">
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Classification</label>
                            <select
                              value={formData.classificationId || ''}
                              onChange={(e) => {
                                const selectedClassification = classifications.find(cls => cls.id === e.target.value);
                                setFormData({ 
                                  ...formData, 
                                  classificationId: e.target.value,
                                  classificationName: selectedClassification?.name || ''
                                });
                              }}
                              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                            >
                              <option value="">Select Classification</option>
                              {classifications.filter(c => c.isActive).map(classification => (
                                <option key={classification.id} value={classification.id}>
                                  {classification.name}
                                </option>
                              ))}
                            </select>
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Area (sq ft)</label>
                            <input
                              type="number"
                              value={formData.areaInSqft || ''}
                              onChange={(e) => setFormData({ ...formData, areaInSqft: e.target.value })}
                              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                              placeholder="Area in sq ft"
                            />
                          </div>
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Remarks / Observations</label>
                          <textarea
                            value={formData.remarks || ''}
                            onChange={(e) => setFormData({ ...formData, remarks: e.target.value })}
                            rows={3}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
                            placeholder="Add any remarks, observations, or notes about this outlet..."
                          />
                        </div>
                      </div>
                    </div>

                    {/* Location Section */}
                    <div className="bg-gray-50 p-4 rounded-lg">
                      <h4 className="text-sm font-semibold text-gray-800 mb-3 flex items-center space-x-2">
                        <Navigation className="w-4 h-4 text-blue-600" />
                        <span>Location</span>
                      </h4>
                      {(formData.latitude && formData.longitude) ? (
                        <div className="bg-green-50 border border-green-200 rounded p-3">
                          <div className="flex items-center space-x-2 mb-2">
                            <CheckCircle className="w-4 h-4 text-green-600" />
                            <span className="text-sm font-medium text-green-800">Location Confirmed</span>
                          </div>
                          <p className="text-xs text-green-600">
                            {formData.latitude?.toFixed(6)}, {formData.longitude?.toFixed(6)}
                          </p>
                        </div>
                      ) : (
                        <div className="bg-orange-50 border border-orange-200 rounded p-3">
                          <div className="flex items-center space-x-2 mb-2">
                            <AlertCircle className="w-4 h-4 text-orange-600" />
                            <span className="text-sm font-medium text-orange-800">No Location Data</span>
                          </div>
                          <p className="text-xs text-orange-600">
                            Location coordinates not available
                          </p>
                        </div>
                      )}
                    </div>

                    {/* Photos & Documents Section */}
                    <div className="bg-gray-50 p-4 rounded-lg">
                      <h4 className="text-sm font-semibold text-gray-800 mb-3 flex items-center space-x-2">
                        <Camera className="w-4 h-4 text-purple-600" />
                        <span>Photos & Documents</span>
                      </h4>
                      
                      {/* Regular Photos */}
                      {formData.outletInteriorExteriorPhotos && formData.outletInteriorExteriorPhotos.length > 0 && (
                        <div className="mb-3">
                          <p className="text-sm font-medium text-gray-700 mb-2">Outlet Photos ({formData.outletInteriorExteriorPhotos.length})</p>
                          <div className="grid grid-cols-3 gap-2">
                            {formData.outletInteriorExteriorPhotos.map((photo: string, index: number) => (
                              <div key={index} className="relative group">
                                <img
                                  src={photo}
                                  alt={`Photo ${index + 1}`}
                                  className="w-full h-16 object-cover rounded border cursor-pointer"
                                  onClick={() => {
                                    // Simple image viewer
                                    const modal = document.createElement('div');
                                    modal.className = 'fixed inset-0 bg-black bg-opacity-75 z-50 flex items-center justify-center p-4';
                                    modal.innerHTML = `
                                      <div class="relative max-w-4xl max-h-full">
                                        <img src="${photo}" alt="Full Size" class="max-w-full max-h-full object-contain" />
                                        <button class="absolute top-2 right-2 p-2 bg-white rounded-full hover:bg-gray-200">
                                          <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path>
                                          </svg>
                                        </button>
                                      </div>
                                    `;
                                    document.body.appendChild(modal);
                                    modal.querySelector('button')?.addEventListener('click', () => {
                                      document.body.removeChild(modal);
                                    });
                                    modal.addEventListener('click', (e) => {
                                      if (e.target === modal) {
                                        document.body.removeChild(modal);
                                      }
                                    });
                                  }}
                                />
                                <div className="absolute bottom-0 left-0 right-0 bg-black bg-opacity-70 text-white text-xs px-1 py-0.5 rounded-b">
                                  Photo {index + 1}
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Visiting Card */}
                      <div className="mb-3">
                        <p className="text-sm font-medium text-gray-700 mb-2">Visiting Card</p>
                        {formData.businessCardPhoto ? (
                          <div className="relative group w-32">
                            <img
                              src={formData.businessCardPhoto}
                              alt="Visiting Card"
                              className="w-full h-20 object-contain bg-white rounded border cursor-pointer"
                              onClick={() => {
                                // Simple image viewer
                                const modal = document.createElement('div');
                                modal.className = 'fixed inset-0 bg-black bg-opacity-75 z-50 flex items-center justify-center p-4';
                                modal.innerHTML = `
                                  <div class="relative max-w-4xl max-h-full">
                                    <img src="${formData.businessCardPhoto}" alt="Visiting Card Full Size" class="max-w-full max-h-full object-contain" />
                                    <button class="absolute top-2 right-2 p-2 bg-white rounded-full hover:bg-gray-200">
                                      <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path>
                                      </svg>
                                    </button>
                                  </div>
                                `;
                                document.body.appendChild(modal);
                                modal.querySelector('button')?.addEventListener('click', () => {
                                  document.body.removeChild(modal);
                                });
                                modal.addEventListener('click', (e) => {
                                  if (e.target === modal) {
                                    document.body.removeChild(modal);
                                  }
                                });
                              }}
                            />
                            <div className="absolute bottom-0 left-0 right-0 bg-black bg-opacity-70 text-white text-xs px-1 py-0.5 rounded-b">
                              Visiting Card
                            </div>
                          </div>
                        ) : (
                          <div className="w-32 h-20 bg-gray-100 rounded border border-dashed border-gray-300 flex items-center justify-center">
                            <div className="text-center">
                              <div className="text-gray-400 text-xs">No visiting card image</div>
                            </div>
                          </div>
                        )}
                      </div>

                      {/* WTF Contact Sticker */}
                      <div className="mb-3">
                        <p className="text-sm font-medium text-gray-700 mb-2">WTF Contact Sticker</p>
                        {formData.wtfBrandingStickerPhoto ? (
                          <div className="relative group w-32">
                            <img
                              src={formData.wtfBrandingStickerPhoto}
                              alt="WTF Contact Sticker"
                              className="w-full h-20 object-contain bg-white rounded border cursor-pointer"
                              onClick={() => {
                                // Simple image viewer
                                const modal = document.createElement('div');
                                modal.className = 'fixed inset-0 bg-black bg-opacity-75 z-50 flex items-center justify-center p-4';
                                modal.innerHTML = `
                                  <div class="relative max-w-4xl max-h-full">
                                    <img src="${formData.wtfBrandingStickerPhoto}" alt="WTF Contact Sticker Full Size" class="max-w-full max-h-full object-contain" />
                                    <button class="absolute top-2 right-2 p-2 bg-white rounded-full hover:bg-gray-200">
                                      <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path>
                                      </svg>
                                    </button>
                                  </div>
                                `;
                                document.body.appendChild(modal);
                                modal.querySelector('button')?.addEventListener('click', () => {
                                  document.body.removeChild(modal);
                                });
                                modal.addEventListener('click', (e) => {
                                  if (e.target === modal) {
                                    document.body.removeChild(modal);
                                  }
                                });
                              }}
                            />
                            <div className="absolute bottom-0 left-0 right-0 bg-black bg-opacity-70 text-white text-xs px-1 py-0.5 rounded-b">
                              WTF Contact Sticker
                            </div>
                          </div>
                        ) : (
                          <div className="w-32 h-20 bg-gray-100 rounded border border-dashed border-gray-300 flex items-center justify-center">
                            <div className="text-center">
                              <div className="text-gray-400 text-xs">No WTF sticker image</div>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Status Section */}
                    <div className="bg-gray-50 p-4 rounded-lg">
                      <h4 className="text-sm font-semibold text-gray-800 mb-3 flex items-center space-x-2">
                        <CheckCircle className="w-4 h-4 text-green-600" />
                        <span>Status</span>
                      </h4>
                      
                      <div className="space-y-3">
                        {/* Status Dropdown */}
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Current Status
                          </label>
                          <select
                            value={formData.status || 'pending_approval'}
                            onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                          >
                            <option value="pending_approval">Pending Approval</option>
                            <option value="active">Active</option>
                            <option value="inactive">Inactive</option>
                            <option value="draft">Draft</option>
                          </select>
                        </div>
                        
                        {/* Quick Approval Button */}
                        {formData.status === 'pending_approval' && (
                          <div className="pt-2 border-t border-gray-200">
                            <button
                              type="button"
                              onClick={handleApproveOutlet}
                              className="w-full flex items-center justify-center space-x-2 px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors"
                            >
                              <CheckCircle className="w-4 h-4" />
                              <span>Quick Approve</span>
                            </button>
                            <p className="text-xs text-gray-500 mt-1 text-center">
                              This will immediately set the outlet status to Active
                            </p>
                          </div>
                        )}
                        
                        {/* Status Badge */}
                        <div className="flex items-center space-x-2">
                          <span className="text-sm text-gray-600">Status:</span>
                          <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                            formData.status === 'active' 
                              ? 'bg-green-100 text-green-800' 
                              : formData.status === 'pending_approval'
                              ? 'bg-yellow-100 text-yellow-800'
                              : formData.status === 'inactive'
                              ? 'bg-red-100 text-red-800'
                              : 'bg-gray-100 text-gray-800'
                          }`}>
                            {formData.status === 'pending_approval' ? 'Pending Approval' :
                             formData.status === 'active' ? 'Active' :
                             formData.status === 'inactive' ? 'Inactive' : 'Draft'}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>

              <div className="flex justify-end space-x-3 mt-6">
                <button
                  type="button"
                  onClick={() => {
                    setShowAddModal(false);
                    setShowEditModal(false);
                    setEditingItem(null);
                    setFormData({});
                  }}
                  className="px-4 py-2 text-gray-600 border border-gray-300 rounded-md hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
                >
                  {editingItem ? 'Update' : 'Create'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Migration Modal */}
      {showMigrationModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-lg">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold">ID Migration System</h3>
              <button
                onClick={() => {
                  setShowMigrationModal(false);
                  setMigrationData(null);
                }}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            <div className="space-y-4">
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <h4 className="font-medium text-blue-900 mb-2">Serial Number Migration</h4>
                <p className="text-sm text-blue-700">
                  This will add sequential serial numbers to all existing documents, starting from 100001. 
                  This is safe and reversible - no documents will be deleted.
                </p>
              </div>

              {!migrationData ? (
                <div className="text-center py-4">
                  <button
                    onClick={prepareMigrationData}
                    disabled={migrationLoading}
                    className="flex items-center space-x-2 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors disabled:opacity-50 mx-auto"
                  >
                    {migrationLoading ? (
                      <>
                        <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                        <span>Analyzing Data...</span>
                      </>
                    ) : (
                      <>
                        <Database className="w-4 h-4" />
                        <span>Prepare Migration</span>
                      </>
                    )}
                  </button>
                </div>
              ) : (
                  <div className="space-y-4">
                    <div className="bg-gray-50 rounded-lg p-4">
                      <h4 className="font-medium text-gray-900 mb-3">Migration Summary</h4>
                      <div className="grid grid-cols-2 gap-4 text-sm">
                        <div>
                          <span className="text-gray-600">Total Items:</span>
                          <span className="font-medium ml-2">{migrationData.totalItems}</span>
                        </div>
                        <div>
                          <span className="text-gray-600">Serial Range:</span>
                          <span className="font-medium ml-2">{migrationData.startSerial} - {migrationData.endSerial}</span>
                        </div>
                        <div>
                          <span className="text-gray-600">Outlets:</span>
                          <span className="font-medium ml-2">{Object.keys(migrationData.outlets).length}</span>
                        </div>
                        <div>
                          <span className="text-gray-600">Migration Type:</span>
                          <span className="font-medium ml-2">Outlets Only</span>
                        </div>
                      </div>
                    </div>

                  <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                    <h4 className="font-medium text-green-900 mb-2">✅ Safe Migration Process</h4>
                    <ul className="text-sm text-green-700 space-y-1">
                      <li>• Adds serialNumber field to existing documents</li>
                      <li>• Preserves all original document IDs and relationships</li>
                      <li>• System remains fully functional during migration</li>
                      <li>• Can be reversed by removing serialNumber fields</li>
                    </ul>
                  </div>

                  <div className="flex justify-center">
                    <button
                      onClick={executeMigration}
                      disabled={migrationLoading}
                      className="flex items-center space-x-2 px-6 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50"
                    >
                      {migrationLoading ? (
                        <>
                          <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                          <span>Migrating...</span>
                        </>
                      ) : (
                        <>
                          <Database className="w-4 h-4" />
                          <span>Execute Migration</span>
                        </>
                      )}
                    </button>
                  </div>
                </div>
              )}
            </div>

            <div className="flex justify-end space-x-3 mt-6">
              <button
                onClick={() => {
                  setShowMigrationModal(false);
                  setMigrationData(null);
                }}
                className="px-4 py-2 text-gray-600 border border-gray-300 rounded-md hover:bg-gray-50"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Cleanup Modal */}
      {showCleanupModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-lg">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold">Outlet Fields Cleanup</h3>
              <button
                onClick={() => setShowCleanupModal(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            <div className="space-y-4">
              <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                <h4 className="font-medium text-red-900 mb-2">⚠️ Data Hierarchy Cleanup</h4>
                <p className="text-sm text-red-700">
                  This will clean up outlet data to enforce proper hierarchy: Outlet → Locality → Area → Warehouse → City.
                  Some fields will be permanently removed or migrated. This action cannot be undone.
                </p>
              </div>

              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                <h4 className="font-medium text-yellow-900 mb-2">Cleanup Tasks:</h4>
                <div className="text-sm text-yellow-700 space-y-1">
                  <div>• <strong>Remove warehouse name columns</strong> (enforce hierarchy)</div>
                  <div>• <strong>Migrate classification field</strong> to classificationId</div>
                  <div>• <strong>Remove documentId field</strong> (duplicate data)</div>
                  <div>• <strong>Enforce data hierarchy:</strong> Outlet → Locality only</div>
                </div>
              </div>

              <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                <h4 className="font-medium text-green-900 mb-2">🛡️ Safety Features:</h4>
                <ul className="text-sm text-green-700 space-y-1">
                  <li>• Creates complete backup collection before cleanup</li>
                  <li>• Backup includes timestamp and original document IDs</li>
                  <li>• Migrates classification data before removal</li>
                  <li>• Preserves core outlet and location data</li>
                </ul>
              </div>

              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <h4 className="font-medium text-blue-900 mb-2">Data Hierarchy Enforcement:</h4>
                <ul className="text-sm text-blue-700 space-y-1">
                  <li>• Outlets will only reference localityId</li>
                  <li>• Geographic data derived through: Locality → Area → Warehouse → City</li>
                  <li>• Cleaner, more maintainable data model</li>
                  <li>• Eliminates data redundancy and inconsistencies</li>
                </ul>
              </div>

              <div className="bg-gray-50 rounded-lg p-4">
                <p className="text-sm text-gray-700">
                  <strong>Note:</strong> Before cleanup, a complete backup will be created in a timestamped 
                  collection (outlets_backup_YYYY-MM-DD). This cleanup enforces proper data hierarchy by 
                  removing direct warehouse/area/city references from outlets and ensuring all geographic 
                  data is derived through the locality relationship.
                </p>
              </div>

              <div className="flex justify-center space-x-3">
                <button
                  onClick={() => setShowCleanupModal(false)}
                  className="px-4 py-2 text-gray-600 border border-gray-300 rounded-md hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  onClick={() => {
                    if (window.confirm('Are you absolutely sure you want to permanently remove these redundant fields from all outlets? This action cannot be undone.')) {
                      setShowCleanupModal(false);
                      executeOutletCleanup();
                    }
                  }}
                  disabled={cleanupLoading}
                  className="flex items-center space-x-2 px-6 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 transition-colors disabled:opacity-50"
                >
                  {cleanupLoading ? (
                    <>
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                      <span>Cleaning...</span>
                    </>
                  ) : (
                    <>
                      <Sparkles className="w-4 h-4" />
                      <span>Cleanup Now</span>
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Drop Redundant Fields Modal */}
      {showDropFieldsModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-lg">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold">Backup & Drop Redundant Outlet Fields</h3>
              <button onClick={() => setShowDropFieldsModal(false)} className="text-gray-400 hover:text-gray-600">
                <X className="w-6 h-6" />
              </button>
            </div>

            <div className="space-y-4">
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <h4 className="font-medium text-blue-900 mb-2">Backup will be created first</h4>
                <p className="text-sm text-blue-800">We'll copy all outlet documents to a timestamped collection. After a successful backup, the following fields will be deleted from each outlet if present:</p>
                <ul className="mt-2 text-sm text-blue-900 list-disc list-inside grid grid-cols-2 gap-1">
                  <li>addedByName</li>
                  <li>areaId</li>
                  <li>areaName</li>
                  <li>city</li>
                  <li>classificationId</li>
                  <li>classificationName</li>
                  <li>createdAt</li>
                  <li>createdBy</li>
                  <li>localityName</li>
                  <li>pincode</li>
                  <li>routeName</li>
                  <li>serialNumber</li>
                  <li>updatedByName</li>
                  <li>photos</li>
                  <li>photoURL</li>
                  <li>visitingCardImage</li>
                  <li>wtfStickerImage</li>
                </ul>
              </div>

              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                <h4 className="font-medium text-yellow-900 mb-2">Heads-up</h4>
                <p className="text-sm text-yellow-800">UI elements that rely on these fields (e.g., filters like Pincode or labels like Area/City names) may stop showing values. We can wire them to derive from Locality/Area data afterwards.</p>
              </div>

              <div className="flex justify-end space-x-3">
                <button onClick={() => setShowDropFieldsModal(false)} className="px-4 py-2 text-gray-700 border border-gray-300 rounded-md hover:bg-gray-50">Cancel</button>
                <button
                  onClick={() => {
                    if (window.confirm('Proceed to backup all outlets and drop the listed fields? This cannot be undone.')) {
                      executeOutletFieldDrop();
                    }
                  }}
                  disabled={dropLoading}
                  className="flex items-center space-x-2 px-6 py-2 bg-orange-600 text-white rounded-md hover:bg-orange-700 disabled:opacity-50"
                >
                  {dropLoading ? (
                    <>
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                      <span>Processing...</span>
                    </>
                  ) : (
                    <>
                      <Trash2 className="w-4 h-4" />
                      <span>Backup & Drop</span>
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Drop Locality Fields Modal */}
      {showDropLocalitiesModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-lg">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold">Backup & Drop Redundant Locality Fields</h3>
              <button onClick={() => setShowDropLocalitiesModal(false)} className="text-gray-400 hover:text-gray-600">
                <X className="w-6 h-6" />
              </button>
            </div>

            <div className="space-y-4">
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <h4 className="font-medium text-blue-900 mb-2">Backup will be created first</h4>
                <p className="text-sm text-blue-800">We'll copy all locality documents to a timestamped collection. After a successful backup, the following fields will be deleted from each locality if present:</p>
                <ul className="mt-2 text-sm text-blue-900 list-disc list-inside grid grid-cols-2 gap-1">
                  <li>areaName</li>
                  <li>cityId</li>
                  <li>cityName</li>
                  <li>geocoded</li>
                  <li>geocodedAt</li>
                  <li>geocodedBy</li>
                  <li>routeDocumentId</li>
                  <li>routeName</li>
                  <li>source</li>
                </ul>
              </div>

              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                <h4 className="font-medium text-yellow-900 mb-2">Heads-up</h4>
                <p className="text-sm text-yellow-800">If any UI elements rely on these fields, they may stop showing values. We can derive names/labels from related Area/City/Route data after cleanup.</p>
              </div>

              <div className="flex justify-end space-x-3">
                <button onClick={() => setShowDropLocalitiesModal(false)} className="px-4 py-2 text-gray-700 border border-gray-300 rounded-md hover:bg-gray-50">Cancel</button>
                <button
                  onClick={() => {
                    if (window.confirm('Proceed to backup all localities and drop the listed fields? This cannot be undone.')) {
                      executeLocalityFieldDrop();
                    }
                  }}
                  disabled={dropLocalitiesLoading}
                  className="flex items-center space-x-2 px-6 py-2 bg-orange-600 text-white rounded-md hover:bg-orange-700 disabled:opacity-50"
                >
                  {dropLocalitiesLoading ? (
                    <>
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                      <span>Processing...</span>
                    </>
                  ) : (
                    <>
                      <Trash2 className="w-4 h-4" />
                      <span>Backup & Drop</span>
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* CSV Upload Modal */}
      {showUploadModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold">Upload CSV</h3>
              <button
                onClick={() => setShowUploadModal(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Select CSV file for {activeTab}
                </label>
                <input
                  type="file"
                  accept=".csv"
                  onChange={(e) => {
                    const file = e.target.files?.[0];
                    if (file) {
                      let collectionName = '';
                      switch (activeTab) {
                        case 'cities': collectionName = 'cities'; break;
                        case 'warehouses': collectionName = 'warehouses'; break;
                        case 'areas': collectionName = 'areas'; break;
                        case 'routes': collectionName = 'routes'; break;
                        case 'localities': collectionName = 'localities'; break;
                        case 'classifications': collectionName = 'outlet_classifications'; break;
                        case 'outlets': collectionName = 'outlets'; break;
                      }
                      handleCSVUpload(file, collectionName);
                    }
                  }}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              
              <div className="text-sm text-gray-600">
                <p className="font-medium mb-2">Expected CSV format:</p>
                {activeTab === 'cities' && (
                  <code className="bg-gray-100 p-2 rounded block text-xs">
                    name,state,cityCode,isActive
                  </code>
                )}
                {activeTab === 'warehouses' && (
                  <code className="bg-gray-100 p-2 rounded block text-xs">
                    name,warehouseId,cityCode,isActive
                  </code>
                )}
                {activeTab === 'areas' && (
                  <code className="bg-gray-100 p-2 rounded block text-xs">
                    name,warehouseId,description,isActive
                  </code>
                )}
                {activeTab === 'routes' && (
                  <code className="bg-gray-100 p-2 rounded block text-xs">
                    name,description,isActive
                  </code>
                )}
                {activeTab === 'classifications' && (
                  <code className="bg-gray-100 p-2 rounded block text-xs">
                    name,description,isActive
                  </code>
                )}
              </div>
            </div>

            <div className="flex justify-end space-x-3 mt-6">
              <button
                onClick={() => setShowUploadModal(false)}
                className="px-4 py-2 text-gray-600 border border-gray-300 rounded-md hover:bg-gray-50"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* AddOutlet Modal Integration */}
      {showAddOutletModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-auto">
            <AddOutlet
              existingOutlet={editingOutlet}
              editMode={!!editingOutlet}
              userRole={currentUser?.role} // Pass the actual user role
              onBack={() => {
                setShowAddOutletModal(false);
                setEditingOutlet(null);
                loadNetworkData(); // Refresh all data after successful save
              }}
            />
          </div>
        </div>
      )}

      {/* Polygon Extractor Modal */}
      <PolygonExtractorModal
        isOpen={showPolygonExtractor}
        onClose={() => {
          setShowPolygonExtractor(false);
          setPolygonExtractionTarget(null);
        }}
        onPolygonExtracted={handlePolygonExtracted}
        title={polygonExtractionTarget ? `Extract Boundary for ${polygonExtractionTarget.name}` : 'Extract Boundary'}
        description={polygonExtractionTarget ? 
          `Upload a map image to automatically extract the boundary polygon for ${polygonExtractionTarget.type} "${polygonExtractionTarget.name}"` : 
          'Upload a map image to automatically extract the boundary polygon'
        }
      />
    </div>
  );
};

export default DistributionNetworkSection;
// ---------------------------

